import React, { useState, useEffect, useRef, useMemo } from "react";
import { supabase } from "./supabase";
import { useAuth } from "./AuthContext";
import UserMgmtNew from "./UserManagement";
import { ConsentSigningFlow, ConsentTemplatesManager, ClientHistory } from "./ConsentManager";
import { BusinessHoursEditor, ProviderAvailabilityEditor, HolidayHoursEditor, ProviderBlockHours } from "./BookingSettings";
import PageCustomization from "./PageCustomization";
import EmbedShare from "./EmbedShare";
import AddonsManager from "./AddonsManager";
import MembershipsManager from "./MembershipsManager";
import LeadEngine from "./LeadEngine";
import PortalShare from "./PortalShare";
import BotTraining from "./BotTraining";
import AppointmentAutomations from "./AppointmentAutomations";
import LeadPipelineBoard from "./LeadPipelineBoard";
import BotoxRecall from "./BotoxRecall";
import TodaysMission from "./TodaysMission";
import SophiaCalls from "./SophiaCalls";
import AIHandoffsBanner from "./AIHandoffsBanner";
import FormTemplatesManager from "./FormTemplatesManager";
import PatientPaperworkHub from "./PatientPaperworkHub";
import CheckInFlow from "./CheckInFlow";
import { SMSModal } from "./SMSComponent";
import MessagesInbox from "./MessagesInbox";
import ClientDocuments from "./ClientDocuments";
import { PackagesCatalog, ClientWallet, consumePackageSession, addCartToWallet } from "./PackagesManager";
import { ChargeModal, PaymentHistory } from "./StripePayment";
import CardFeeSettings from "./CardFeeSettings";
import { SalesReport, RecordPaymentModal, TakePaymentPage } from "./SalesReport";
import { ProvidersManager } from "./ProvidersManager";
import { ServicesManager } from "./ServicesManager";
import TreatmentMessagesManager from "./TreatmentMessagesManager";
import { ConsentsManager } from "./ConsentsManager";
import { ClientAnalytics } from "./ClientAnalytics";
import { WinBackEngine } from "./WinBackEngine";
import ColdOutreach from "./ColdOutreach";
import { TreatmentAnalytics } from "./TreatmentAnalytics";
import { MiniCalendar } from "./MiniCalendar";
import PayrollPage from "./PayrollPage";
import TreatmentRecord from "./TreatmentRecord";
import WaitlistDrawer from "./WaitlistDrawer";
import CardOnFileModal from "./CardOnFileModal";
import ManageCardsModal from "./ManageCardsModal";
import { ConfirmQueuePanel } from "./ConfirmQueue";
import BusinessReports from "./BusinessReports";
import GiftCardModal from "./GiftCardModal";
import { receiptRowsHtml, receiptEmailFields } from "./receipt";
import StaffHours, { useActivityHeartbeat } from "./StaffHours";

// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
// Searchable dropdown — type to filter, click to select
function SearchSelect({ value, onChange, options, placeholder = "Select...", getKey, getLabel, getGroup, style }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const wrapRef = React.useRef(null);
  // On phones, auto-focusing the search box pops the keyboard, which resizes
  // the viewport and can cancel the very next tap on an option (the option
  // moves out from under the finger). Skip autofocus on mobile.
  const isMobile = typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

  React.useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Find selected label
  const selected = options.find(o => String(getKey(o)) === String(value));
  const selectedLabel = selected ? getLabel(selected) : "";

  // Filter
  const filtered = options.filter(o => {
    if (!query) return true;
    const q = query.toLowerCase();
    return getLabel(o).toLowerCase().includes(q) || (getGroup ? getGroup(o)?.toLowerCase().includes(q) : false);
  });

  // Group if needed
  const grouped = {};
  filtered.forEach(o => {
    const g = getGroup ? (getGroup(o) || "Other") : "_";
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push(o);
  });

  return (
    <div ref={wrapRef} style={{position:"relative",...style}}>
      <div onClick={() => setOpen(!open)} style={{
        ...inp,
        cursor:"pointer",
        display:"flex",
        alignItems:"center",
        justifyContent:"space-between",
        background: open ? "#fff" : inp.background,
      }}>
        <span style={{color: selectedLabel ? C.text : "#9ca3af", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
          {selectedLabel || placeholder}
        </span>
        <span style={{color:C.muted,fontSize:11,marginLeft:8}}>{open ? "▲" : "🔍"}</span>
      </div>

      {open && (
        <div style={{
          position:"absolute",
          top:"100%",
          left:0,
          right:0,
          marginTop:4,
          background:"#fff",
          border:`1px solid ${C.border}`,
          borderRadius:8,
          boxShadow:"0 8px 24px rgba(0,0,0,0.12)",
          zIndex:1000,
          maxHeight:320,
          overflow:"hidden",
          display:"flex",
          flexDirection:"column",
        }}>
          <div style={{padding:8,borderBottom:`1px solid ${C.border}`,background:"#f8f5f0"}}>
            <input
              autoFocus={!isMobile}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Type to search..."
              style={{...inp,padding:"7px 10px",fontSize:12,background:"#fff"}}
              onKeyDown={e => { if (e.key === "Escape") setOpen(false); }}
            />
          </div>
          <div style={{overflow:"auto",flex:1}}>
            {filtered.length === 0 && (
              <div style={{padding:"14px 12px",fontSize:12,color:C.muted,textAlign:"center"}}>No matches for "{query}"</div>
            )}
            {Object.entries(grouped).map(([groupName, items]) => (
              <div key={groupName}>
                {getGroup && groupName !== "_" && (
                  <div style={{padding:"6px 12px",fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",background:"rgba(180,140,60,0.06)"}}>
                    {groupName}
                  </div>
                )}
                {items.map(o => {
                  const k = getKey(o);
                  const isSelected = String(k) === String(value);
                  return (
                    <div
                      key={k}
                      // ★ onMouseDown (not onClick): fires BEFORE the search input
                      //   blurs / the keyboard dismisses, so the tap always lands
                      //   on the option even on mobile. preventDefault keeps focus
                      //   from jumping. onTouchEnd is a belt-and-suspenders fallback
                      //   for browsers that delay the synthesized mouse events.
                      onMouseDown={(e) => { e.preventDefault(); onChange(k); setOpen(false); setQuery(""); }}
                      onTouchEnd={(e) => { e.preventDefault(); onChange(k); setOpen(false); setQuery(""); }}
                      style={{
                        padding:"8px 12px",
                        fontSize:13,
                        cursor:"pointer",
                        background: isSelected ? "rgba(180,140,60,0.12)" : "transparent",
                        color: isSelected ? C.gold : C.text,
                        fontWeight: isSelected ? 600 : 400,
                        borderBottom:`1px solid ${C.border}40`,
                      }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#f8f5f0"; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                    >
                      {getLabel(o)}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── REFINED LUXURY PALETTE ──
// Warmer neutrals, deeper text, softer status colors. Less "Bootstrap admin",
// more "Soho House meets Aesop". All status colors kept lower-saturation so
// the gold remains the only loud accent.
const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff",
  border:"#e8e2d8",                                   // warmer than #e8e4df
  borderSoft:"#f0ebe2",                                // brand-new soft separator
  borderGold:"rgba(180,140,60,0.28)",
  goldBg:"rgba(180,140,60,0.08)",
  cream:"#faf7f1",
  sidebar:"#0f0f1e", sidebarBorder:"#2a2a45",
  gold:"#b48c3c", goldL:"#d9b878", goldD:"#8a6a2a",   // deeper, more aged-gold
  green:"#2d8a55", greenL:"rgba(45,138,85,0.10)",     // softer green
  red:"#b54040",   redL:"rgba(181,64,64,0.10)",       // less alarm-red
  blue:"#2a5fb5",  blueL:"rgba(42,95,181,0.10)",      // less saturated
  purple:"#6e3fb0",purpleL:"rgba(110,63,176,0.10)",
  teal:"#0d8275",  tealL:"rgba(13,130,117,0.10)",
  orange:"#c66a1f",orangeL:"rgba(198,106,31,0.10)",
  text:"#1a1a2e",                                     // slightly warmer than pure navy
  textSoft:"#3c3d52",                                  // brand-new: for secondary body text
  muted:"#6b6356",                                    // warm-grey instead of #6b7280
  dim:"#a39a8a",                                      // warm dim instead of cold #9ca3af
  light:"#f4f0ea",                                    // warmer than #f3f4f6
};
const F = "'Montserrat','Plus Jakarta Sans',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

// ── MOBILE DETECTION ──────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    window.addEventListener("orientationchange", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("orientationchange", handler);
    };
  }, []);
  return isMobile;
};

// useIsCompact — true for phones AND iPad portrait (≤1024px). Use this for
// any "side-by-side" layout that becomes painful on an iPad.
const useIsCompact = () => {
  const [is, setIs] = useState(window.innerWidth <= 1024);
  useEffect(() => {
    const handler = () => setIs(window.innerWidth <= 1024);
    window.addEventListener("resize", handler);
    window.addEventListener("orientationchange", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("orientationchange", handler);
    };
  }, []);
  return is;
};

// ── DATA ──────────────────────────────────────────────────────────────────────
// Format a Date object as a LOCAL "YYYY-MM-DD" string. Using .toISOString()
// (which we did before) returns UTC — and after ~8 PM EDT the UTC date is
// already TOMORROW, which made the calendar open the wrong day when tapping
// on phone/iPad late in the evening. This helper always returns local date.
const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const TODAY = toLocalDateStr(new Date());

// ★★★ CRITICAL: format a "YYYY-MM-DD" date string for display without the
//   classic UTC shift. `new Date("2026-06-03")` parses as UTC midnight,
//   then toLocaleDateString in NY (UTC-4 EDT) becomes Tue Jun 2 — which is
//   exactly the bug that sent confirmation emails with the WRONG day.
//   Use this helper everywhere we render a date-only field for the client.
const fmtAppointmentDate = (dateStr, opts = { weekday: "long", month: "long", day: "numeric", year: "numeric" }) => {
  if (!dateStr) return "";
  // Force noon LOCAL — keeps the same date in any North-American zone.
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    // unknown format — fall back to the naive path (still better than nothing)
    return new Date(dateStr).toLocaleDateString("en-US", opts);
  }
  const [_, y, mm, dd] = m;
  const safe = new Date(Number(y), Number(mm) - 1, Number(dd), 12, 0, 0);
  return safe.toLocaleDateString("en-US", opts);
};

const format12Hour = (time24) => {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2,"0")} ${period}`;
};


let PROVIDERS = [];
let _providersListeners = new Set();

function useProviders() {
  const [providers, setProviders] = useState(PROVIDERS);
  
  useEffect(() => {
    _providersListeners.add(setProviders);
    
    const load = async () => {
      const { data } = await supabase.from("providers").select("*").eq("active", true).order("order_index").order("name");
      if (data) {
        PROVIDERS = data.map(p => ({
          id: p.id,
          name: p.name,
          role: p.title || p.role,
          color: p.color || C.gold,
          photo_url: p.photo_url || null,
          ini: (p.name||"").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()
        }));
        _providersListeners.forEach(fn => fn(PROVIDERS));
      }
    };
    load();
    
    const ch = supabase.channel("providers-sync-"+Math.random())
      .on("postgres_changes", { event: "*", schema: "public", table: "providers" }, () => load())
      .subscribe();
    
    return () => {
      _providersListeners.delete(setProviders);
      supabase.removeChannel(ch);
    };
  }, []);
  
  return providers;
}

const LOCS = ["Nova Med Spa"];
const TREATS = ["Botox","Lip Filler","Cheek Filler","Chemical Peel","Laser Hair Removal","RF Microneedling","HydraFacial","PRP Hair","Dermaplaning","Ultherapy","Kybella","IPL Photofacial","Sculptra","Juvederm","GLP-1 Program","IV Therapy","CO2 Laser"];
const TPRICE = {"Botox":800,"Lip Filler":900,"Cheek Filler":1200,"Chemical Peel":380,"Laser Hair Removal":500,"RF Microneedling":750,"HydraFacial":450,"PRP Hair":950,"Dermaplaning":280,"Ultherapy":1800,"Kybella":1100,"IPL Photofacial":550,"Sculptra":1400,"Juvederm":1000,"GLP-1 Program":600,"IV Therapy":350,"CO2 Laser":2200};
const TDUR = {"Botox":45,"Lip Filler":60,"Cheek Filler":60,"Chemical Peel":60,"Laser Hair Removal":90,"RF Microneedling":75,"HydraFacial":75,"PRP Hair":90,"Dermaplaning":45,"Ultherapy":90,"Kybella":60,"IPL Photofacial":60,"Sculptra":75,"Juvederm":60,"GLP-1 Program":30,"IV Therapy":60,"CO2 Laser":90};
const ROOMS = ["Suite A","Suite B","Treatment Room 1","Laser Room","Consultation Room"];

const INIT_CLIENTS = [];

const INIT_APPTS = [];

const INIT_MEMBERS = [];

const INVENTORY = [];

const GFE_FORMS = [];

const USERS = [];

// ── STATUS CONFIGS ────────────────────────────────────────────────────────────
const SC = {
  confirmed:{label:"Confirmed",bg:C.blueL,c:C.blue},
  completed:{label:"Completed",bg:C.greenL,c:C.green},
  cancelled:{label:"Cancelled",bg:C.redL,c:C.red},
  "no-show":{label:"No Show",bg:C.orangeL,c:C.orange},
  hold:{label:"On Hold",bg:C.orangeL,c:C.orange},
  active:{label:"Active",bg:C.greenL,c:C.green},
  inactive:{label:"Inactive",bg:C.redL,c:C.red},
  waiting:{label:"Waiting",bg:C.orangeL,c:C.orange},
  "in-treatment":{label:"In Treatment",bg:C.greenL,c:C.green},
  upcoming:{label:"Upcoming",bg:C.blueL,c:C.blue},
  "checked-out":{label:"Checked Out",bg:"#f3f4f6",c:C.muted},
  valid:{label:"Valid",bg:C.greenL,c:C.green},
  pending:{label:"Pending",bg:C.orangeL,c:C.orange},
  expired:{label:"Expired",bg:C.redL,c:C.red},
  admin:{label:"Admin",bg:C.goldBg,c:C.gold},
  provider:{label:"Provider",bg:C.blueL,c:C.blue},
  frontdesk:{label:"Front Desk",bg:C.greenL,c:C.green},
};

// ── ICONS ─────────────────────────────────────────────────────────────────────
const IP = {
  dash:"M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  users:"M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  cal:"M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  chart:"M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
  clip:"M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
  box:"M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4",
  mega:"M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z",
  bot:"M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-2",
  team:"M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  plus:"M12 4v16m8-8H4", x:"M6 18L18 6M6 6l12 12", chk:"M5 13l4 4L19 7",
  srch:"M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  edit:"M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  warn:"M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  send:"M12 19l9 2-9-18-9 18 9-2zm0 0v-8",
  credit:"M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  link:"M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1",
  cash:"M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z",
  star:"M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z",
  shield:"M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  photo:"M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z",
  pkg:"M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4",
  key:"M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z",
  arr:"M9 5l7 7-7 7",
  clock:"M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  payroll:"M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z",
  members:"M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  desk:"M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  gfe:"M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  menu:"M4 6h16M4 12h16M4 18h16",
  // Missing icons — added so NAV items always have a glyph
  trending:"M3 17l6-6 4 4 8-8M21 7h-6M21 7v6",
  sparkles:"M5 3v4M3 5h4M6 17v4M4 19h4M13 3l3 7 7 3-7 3-3 7-3-7-7-3 7-3z",
  package:"M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12",
  dollar:"M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6",
  user:"M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M16 7a4 4 0 11-8 0 4 4 0 018 0z",
};

const Ic = ({n,s=16,c}) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c||"currentColor"} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    {(IP[n]||"").split(" M").map((p,i) => <path key={i} d={i===0?p:"M"+p}/>)}
  </svg>
);

// ── ELYON LOGO ────────────────────────────────────────────────────────────────
const ElyonLogo = ({collapsed}) => (
  <div style={{display:"flex",alignItems:"center",gap:collapsed?0:10}}>
    <svg width={34} height={34} viewBox="0 0 40 40" fill="none">
      <defs>
        <linearGradient id="eg1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#d4a853"/>
          <stop offset="100%" stopColor="#8a6f2e"/>
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r="19" fill="none" stroke="#b48c3c" strokeWidth="1.5"/>
      <path d="M20 5L22.5 15L33 18L22.5 21L20 31L17.5 21L7 18L17.5 15Z" fill="url(#eg1)"/>
      <circle cx="20" cy="20" r="3.5" fill="#0f0f1e" stroke="#b48c3c" strokeWidth="1"/>
      <circle cx="20" cy="20" r="1.8" fill="url(#eg1)"/>
    </svg>
    {!collapsed && (
      <div>
        <div style={{fontSize:17,fontWeight:800,letterSpacing:"0.1em",color:"#b48c3c",fontFamily:S,lineHeight:1}}>ELYON</div>
        <div style={{fontSize:9,color:"rgba(255,255,255,0.6)",letterSpacing:"0.18em",textTransform:"uppercase",marginTop:1}}>Med Spa Platform</div>
      </div>
    )}
  </div>
);

// ── SHARED UI ─────────────────────────────────────────────────────────────────
const Bdg = ({status,label}) => {
  const cfg = SC[status]||{label:status||"",bg:C.light,c:C.muted};
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:20,background:cfg.bg,color:cfg.c,border:`1px solid ${cfg.c}25`,whiteSpace:"nowrap"}}>
      <span style={{width:5,height:5,borderRadius:"50%",background:cfg.c,flexShrink:0}}/>
      {label||cfg.label}
    </span>
  );
};

function Btn({children,v="primary",onClick,sz="md",icon,full,style:ex,disabled}){
  const sizes = {sm:{padding:"5px 12px",fontSize:11},md:{padding:"9px 18px",fontSize:13},lg:{padding:"12px 24px",fontSize:14}};
  const vars = {
    primary:{background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",boxShadow:`0 2px 8px rgba(180,140,60,0.3)`},
    ghost:{background:"transparent",color:C.muted,border:`1px solid ${C.border}`},
    danger:{background:C.redL,color:C.red,border:`1px solid rgba(192,57,43,0.25)`},
    success:{background:C.greenL,color:C.green,border:`1px solid rgba(46,139,87,0.25)`},
    sec:{background:C.goldBg,color:C.gold,border:`1px solid ${C.borderGold}`},
    dark:{background:C.light,color:C.text,border:`1px solid ${C.border}`},
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{display:"inline-flex",alignItems:"center",gap:6,borderRadius:8,fontWeight:600,cursor:disabled?"not-allowed":"pointer",fontFamily:F,border:"none",transition:"all 0.15s",whiteSpace:"nowrap",justifyContent:"center",opacity:disabled?0.5:1,...sizes[sz],...(vars[v]||vars.ghost),...(full?{width:"100%"}:{}),...ex}}>
      {icon && <Ic n={icon} s={sz==="sm"?12:14} c={v==="primary"?"#fff":undefined}/>}
      {children}
    </button>
  );
}

function Modal({title,onClose,children,w=580}){
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,width:"100%",maxWidth:w,maxHeight:"90vh",overflow:"auto",boxShadow:"0 20px 60px rgba(0,0,0,0.15)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 22px",borderBottom:`1px solid ${C.border}`}}>
          <h3 style={{margin:0,fontSize:16,fontWeight:700,color:C.gold,fontFamily:S}}>{title}</h3>
          <button onClick={onClose} style={{background:C.light,border:"none",borderRadius:8,padding:6,cursor:"pointer",color:C.muted,display:"flex"}}><Ic n="x" s={16}/></button>
        </div>
        <div style={{padding:22}}>{children}</div>
      </div>
    </div>
  );
}

// Appointment Detail Modal
function ApptModal({appt,onClose,onUpdate,onEdit,onCheckIn,onRecordTreatment,onOpenClient,isNewPatient}){
  const [custConfirmed, setCustConfirmed] = React.useState(!!appt?.customer_confirmed);
  const [custConfirmedAt, setCustConfirmedAt] = React.useState(appt?.customer_confirmed_at);
  // ★ In-CRM cancel modal — replaces window.confirm() which doesn't reliably
  //   surface "Send notification?" prompt on iPad / mobile Safari (modal can
  //   pop behind other windows). This one renders inside our own React tree.
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  // ★ Defaults to FALSE per owner directive 2026-06-10. Cancellation SMS
  //   should only fire when staff EXPLICITLY checks the box — opt-in,
  //   not opt-out. Previously the box was checked by default and the
  //   owner kept accidentally sending cancellation SMS while cleaning up
  //   duplicate appointments.
  const [cancelNotify, setCancelNotify] = React.useState(false);
  // Client contact (phone) — pulled on open. Lets staff see/tap-to-call the
  // patient without leaving the modal. Email too in case they want a quick
  // reach-out.
  const [clientContact, setClientContact] = React.useState({ phone: "", email: "" });
  // ★ Waitlist status — tag clients who are sitting on a waitlist somewhere.
  const [isOnWaitlist, setIsOnWaitlist] = React.useState(false);
  // ★ Google review request — manual one-tap send after a visit.
  const [reviewState, setReviewState] = React.useState("idle"); // idle|confirm|sending|sent|error
  const [reviewErr, setReviewErr] = React.useState("");
  const [reviewSentAt, setReviewSentAt] = React.useState(appt?.review_requested_at || null);
  React.useEffect(()=>{
    setCustConfirmed(!!appt?.customer_confirmed);
    setCustConfirmedAt(appt?.customer_confirmed_at);
    setReviewSentAt(appt?.review_requested_at || null);
    setReviewState("idle");
    setReviewErr("");
  },[appt?.id,appt?.customer_confirmed]);
  React.useEffect(() => {
    let cancelled = false;
    if (!appt?.cid) { setClientContact({ phone: "", email: "" }); setIsOnWaitlist(false); return; }
    (async () => {
      const [{ data: client }, { data: wait }] = await Promise.all([
        supabase.from("clients").select("phone,email").eq("id", appt.cid).maybeSingle(),
        supabase.from("waitlist_entries").select("id").eq("client_id", appt.cid).eq("status", "waiting").limit(1),
      ]);
      if (cancelled) return;
      if (client) setClientContact({ phone: client.phone || "", email: client.email || "" });
      setIsOnWaitlist((wait || []).length > 0);
    })();
    return () => { cancelled = true; };
  }, [appt?.cid]);
  if(!appt) return null;
  const p = PROVIDERS.find(x=>x.id===appt.pid);
  // Format US-style phone for display: (516) 244-0248. Keeps tel: link
  // tappable with the raw digits.
  const formatPhone = (raw) => {
    const d = (raw || "").replace(/\D/g, "");
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    if (d.length === 11 && d.startsWith("1")) return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
    return raw;
  };
  const toggleConfirmed = async () => {
    const newVal = !custConfirmed;
    const newAt = newVal ? new Date().toISOString() : null;
    setCustConfirmed(newVal);
    setCustConfirmedAt(newAt);
    try {
      const { error } = await supabase.from("appointments").update({
        customer_confirmed: newVal,
        customer_confirmed_at: newAt,
      }).eq("id",appt.id);
      if(error) throw error;
      if(onUpdate) onUpdate(appt.id, appt.status, { customer_confirmed: newVal, customer_confirmed_at: newAt });
    } catch(err) {
      setCustConfirmed(!newVal);
      setCustConfirmedAt(appt.customer_confirmed_at);
      alert("Failed: " + (err.message||err));
    }
  };
  // ★ Google review request — the message is written to nudge a 5-star review,
  //   and the receptionist sends it manually (so it only goes to happy clients).
  const GOOGLE_REVIEW_URL = "https://g.page/r/CbrHBnaZGJSFEBM/review";
  const reviewFirstName = (appt.cn || "").trim().split(/\s+/)[0] || "there";
  const reviewMessage = `Hi ${reviewFirstName}! 💛 It was so lovely having you at Nova Med Spa. If you enjoyed your visit, would you mind leaving us a quick ⭐⭐⭐⭐⭐ Google review? It truly makes our day: ${GOOGLE_REVIEW_URL} 🌸`;
  const sendReviewRequest = async () => {
    if (reviewState === "sending") return;
    const phone = (clientContact.phone || "").trim();
    if (!phone) { setReviewState("error"); setReviewErr("No phone number on file for this client."); return; }
    setReviewState("sending");
    try {
      const { data, error } = await supabase.functions.invoke("send-lead-sms", {
        body: { phone, client_id: appt.cid, message: reviewMessage }
      });
      let detail = "";
      if (error) {
        detail = error.message || "send failed";
        try { if (error.context?.json) { const j = await error.context.json(); if (j?.error) detail = j.error; } } catch {}
      } else if (data?.error) { detail = data.error; }
      if (detail) { setReviewState("error"); setReviewErr(detail); return; }
      const now = new Date().toISOString();
      setReviewSentAt(now);
      setReviewState("sent");
      try { await supabase.from("appointments").update({ review_requested_at: now }).eq("id", appt.id); } catch {}
    } catch (e) {
      setReviewState("error"); setReviewErr(e?.message || "exception");
    }
  };
  return (
    <Modal title="Appointment Details" onClose={onClose} w={560}>
      <div style={{display:"flex",gap:16,alignItems:"center",padding:"18px",background:C.goldBg,borderRadius:12,border:`1px solid ${C.borderGold}`,marginBottom:22}}>
        <div onClick={()=>onOpenClient&&onOpenClient(appt.cid)} title="Open client EMR" style={{width:58,height:58,borderRadius:13,background:(p?.color||C.gold)+"20",border:`2px solid ${(p?.color||C.gold)}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:700,color:p?.color||C.gold,flexShrink:0,cursor:onOpenClient?"pointer":"default"}}>{appt.cn?.split(" ").map(n=>n[0]).join("")}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
            <button
              onClick={()=>onOpenClient&&onOpenClient(appt.cid)}
              disabled={!onOpenClient}
              title="Open client EMR & full history"
              style={{background:"none",border:"none",padding:0,cursor:onOpenClient?"pointer":"default",fontSize:22,fontWeight:800,color:C.text,fontFamily:S,textAlign:"left",textDecoration:onOpenClient?"underline":"none",textDecorationColor:C.gold+"60",textUnderlineOffset:4,letterSpacing:"-0.01em"}}
              onMouseEnter={e=>{if(onOpenClient)e.currentTarget.style.color=C.gold;}}
              onMouseLeave={e=>{e.currentTarget.style.color=C.text;}}
            >
              {appt.cn}
            </button>
            {onOpenClient && (
              <button onClick={()=>onOpenClient(appt.cid)} style={{fontSize:10.5,padding:"4px 10px",background:C.gold,color:"#fff",border:"none",borderRadius:99,cursor:"pointer",fontWeight:700,fontFamily:F,letterSpacing:"0.05em"}}>📂 OPEN EMR →</button>
            )}
            {/* New vs Existing patient pill */}
            {isNewPatient !== undefined && (
              <span style={{
                fontSize:10,padding:"3px 10px",borderRadius:99,fontWeight:800,letterSpacing:"0.05em",
                background: isNewPatient ? "linear-gradient(135deg,#10b981,#059669)" : "rgba(37,99,235,0.12)",
                color: isNewPatient ? "#fff" : "#2563eb",
                border: isNewPatient ? "none" : "1px solid rgba(37,99,235,0.3)",
                textTransform:"uppercase",
              }}>
                {isNewPatient ? "🆕 New Patient" : "★ Existing Patient"}
              </span>
            )}
            {/* Waitlist pill — they're sitting on a waitlist somewhere */}
            {isOnWaitlist && (
              <span style={{
                fontSize:10,padding:"3px 10px",borderRadius:99,fontWeight:800,letterSpacing:"0.05em",
                background:"linear-gradient(135deg,#b48c3c,#8a6f2e)",
                color:"#fff",
                textTransform:"uppercase",
              }}>
                ⏳ On Waitlist
              </span>
            )}
          </div>
          <div style={{fontSize:14,color:C.muted,marginBottom:6,fontWeight:500}}>{appt.treat} · {appt.date} at {format12Hour(appt.time)}</div>
          {(clientContact.phone || clientContact.email) && (
            <div style={{fontSize:12,color:C.muted,marginBottom:8,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
              {clientContact.phone && (
                <a href={`tel:${clientContact.phone.replace(/\D/g,"")}`} style={{display:"inline-flex",alignItems:"center",gap:5,color:C.gold,fontWeight:600,textDecoration:"none",fontFamily:F}} onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"} onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>
                  📞 {formatPhone(clientContact.phone)}
                </a>
              )}
              {clientContact.email && (
                <a href={`mailto:${clientContact.email}`} style={{display:"inline-flex",alignItems:"center",gap:5,color:C.blue,fontWeight:500,textDecoration:"none",fontFamily:F,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:220}} onMouseEnter={e=>e.currentTarget.style.textDecoration="underline"} onMouseLeave={e=>e.currentTarget.style.textDecoration="none"}>
                  ✉️ {clientContact.email}
                </a>
              )}
            </div>
          )}
          <Bdg status={appt.status}/>
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{fontSize:30,fontWeight:800,color:C.gold,fontFamily:S,letterSpacing:"-0.02em"}}>${appt.price}</div>
          {appt.paid&&<div style={{fontSize:12,color:C.green,fontWeight:700,marginTop:2}}>✓ Paid</div>}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:18}}>
        {[["👨‍⚕️ Provider",appt.prov||"—",p?.color||C.gold],["⏰ Time & Duration",`${format12Hour(appt.time)} · ${appt.dur} min`,C.blue],["🏠 Room",appt.room||"—",C.purple],["💳 Deposit",(+appt.dep)>0?`$${appt.dep} paid`:"No deposit",(+appt.dep)>0?C.green:C.muted],["💰 Quote",`$${appt.price}`,C.gold]].map(([label,value,color])=>(
          <div key={label} style={{padding:"14px 16px",background:C.light,borderRadius:10,border:`1px solid ${C.border}`}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:5,fontWeight:600,letterSpacing:"0.04em"}}>{label}</div>
            <div style={{fontSize:15,fontWeight:700,color:color||C.text,lineHeight:1.3}}>{value}</div>
          </div>
        ))}
      </div>
      {appt.notes&&<div style={{padding:"14px 16px",background:C.light,borderRadius:10,border:`1px solid ${C.border}`,marginBottom:18}}><div style={{fontSize:11,color:C.muted,marginBottom:5,fontWeight:600,letterSpacing:"0.04em"}}>📝 NOTES</div><div style={{fontSize:14,color:C.text,lineHeight:1.55}}>{appt.notes}</div></div>}
      {/* Booked-by audit line */}
      {(appt.booked_by_user_name || appt.booked_by_source) && (
        <div style={{padding:"10px 14px",background:C.cream||C.light,borderRadius:10,border:`1px solid ${C.borderSoft||C.border}`,marginBottom:18,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <div style={{fontSize:11,color:C.muted,fontWeight:600,letterSpacing:"0.04em"}}>📅 BOOKED BY</div>
          <div style={{fontSize:13,color:C.text,fontWeight:700}}>
            {appt.booked_by_source === "ai_bot"
              ? "🤖 AI Bot"
              : appt.booked_by_source === "booking_link"
                ? "🌐 Online (booking link)"
                : appt.booked_by_user_name || "Staff"}
          </div>
          {appt.booked_at && (
            <div style={{fontSize:11,color:C.muted,marginLeft:"auto"}}>
              {new Date(appt.booked_at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
            </div>
          )}
        </div>
      )}
      {/* ★ Cancelled-by audit line — owner reported appts "cancelling
          themselves". This makes it visible exactly which staff member
          (or external trigger) cancelled the slot + when. Falls back
          gracefully if the audit columns are blank (legacy data). */}
      {appt.status === "cancelled" && (appt.cancelled_by_name || appt.cancelled_at) && (
        <div style={{padding:"10px 14px",background:"rgba(192,57,43,0.06)",borderRadius:10,border:`1px solid ${C.red}40`,marginBottom:18,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <div style={{fontSize:11,color:C.red,fontWeight:700,letterSpacing:"0.04em"}}>🚫 CANCELLED BY</div>
          <div style={{fontSize:13,color:C.text,fontWeight:700}}>{appt.cancelled_by_name || "Unknown"}</div>
          {appt.cancelled_at && (
            <div style={{fontSize:11,color:C.muted,marginLeft:"auto"}}>
              {new Date(appt.cancelled_at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
            </div>
          )}
        </div>
      )}
      {/* Customer Confirmation Toggle */}
      <div onClick={toggleConfirmed} style={{padding:"14px 16px",background:custConfirmed?"#e6f9ec":"#f7f7f7",borderRadius:10,border:`1px solid ${custConfirmed?"#22c55e":C.border}`,marginBottom:18,display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",userSelect:"none"}}>
        <div>
          <div style={{fontSize:11,color:C.muted,marginBottom:4,fontWeight:600,letterSpacing:"0.04em"}}>📱 CUSTOMER CONFIRMATION (via SMS)</div>
          <div style={{fontSize:14,fontWeight:700,color:custConfirmed?"#15803d":C.muted}}>
            {custConfirmed?"✓ Confirmed by customer":"Not confirmed yet"}
          </div>
          {custConfirmedAt&&<div style={{fontSize:12,color:C.muted,marginTop:3}}>at {new Date(custConfirmedAt).toLocaleString()}</div>}
        </div>
        <div style={{width:50,height:26,borderRadius:13,background:custConfirmed?"#22c55e":"#ccc",position:"relative",transition:"all 0.2s",flexShrink:0}}>
          <div style={{width:22,height:22,borderRadius:11,background:"#fff",position:"absolute",top:2,left:custConfirmed?26:2,transition:"all 0.2s"}}/>
        </div>
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {appt.status==="confirmed"&&<Btn v="sec" icon="chk" onClick={()=>{if(onCheckIn)onCheckIn(appt);onClose();}}>✓ Check In & Sign Consents</Btn>}
        {/* Mark Complete removed per workflow change — appointments now
            transition through check-in → record treatment → completed,
            so the one-click "Mark Complete" was bypassing the treatment
            record step. */}
        {appt.status==="confirmed"&&<Btn v="danger" onClick={()=>{onUpdate(appt.id,"no-show");onClose();}}>No Show</Btn>}
        {(appt.status==="completed"||appt.treatment_recorded) && onRecordTreatment && <Btn v={appt.treatment_recorded?"sec":"primary"} onClick={()=>{onRecordTreatment(appt);onClose();}}>{appt.treatment_recorded?"📋 View/Edit Record":"✍️ Record Treatment"}</Btn>}
        <Btn v="sec" icon="edit" onClick={()=>{onEdit&&onEdit(appt);onClose();}}>Edit Appointment</Btn>
        {appt.status==="confirmed"&&<Btn v="danger" onClick={()=>setCancelConfirmOpen(true)}>🚫 Cancel Appointment</Btn>}
        {appt.status!=="cancelled" && (
          <Btn v="sec" onClick={()=>setReviewState(["confirm","sending","error"].includes(reviewState)?"idle":"confirm")}>
            ⭐ {reviewSentAt ? "Review Requested ✓" : "Request Google Review"}
          </Btn>
        )}
        <Btn v="ghost" onClick={onClose}>Close</Btn>
      </div>

      {/* ★ Google review request — preview the exact text before it goes out */}
      {["confirm","sending","sent","error"].includes(reviewState) && (
        <div style={{marginTop:14,padding:"14px 16px",background:C.goldBg,borderRadius:10,border:`1px solid ${C.borderGold}`}}>
          <div style={{fontSize:11,color:C.goldD||C.gold,fontWeight:800,letterSpacing:"0.04em",marginBottom:8}}>⭐ GOOGLE REVIEW REQUEST</div>
          {!clientContact.phone ? (
            <div style={{fontSize:13,color:C.red,fontWeight:600}}>No phone number on file for this client — can't send a text.</div>
          ) : (
            <>
              <div style={{fontSize:13,color:C.text,lineHeight:1.55,background:"#fff",padding:"11px 13px",borderRadius:8,border:`1px solid ${C.border}`,marginBottom:8,whiteSpace:"pre-wrap"}}>{reviewMessage}</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:10}}>To: <b style={{color:C.text}}>{appt.cn}</b> · {formatPhone(clientContact.phone)}{reviewSentAt&&reviewState!=="sent"?<span style={{color:C.gold}}> · already requested {new Date(reviewSentAt).toLocaleDateString()}</span>:null}</div>
              {reviewState==="sent" ? (
                <div style={{fontSize:14,color:C.green,fontWeight:800}}>✓ Review request sent!</div>
              ) : reviewState==="error" ? (
                <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  <div style={{fontSize:12,color:C.red,fontWeight:700}}>Failed: {reviewErr}</div>
                  <Btn sz="sm" v="primary" onClick={sendReviewRequest}>Retry</Btn>
                  <Btn sz="sm" v="ghost" onClick={()=>setReviewState("idle")}>Cancel</Btn>
                </div>
              ) : (
                <div style={{display:"flex",gap:8}}>
                  <Btn sz="sm" v="primary" onClick={sendReviewRequest}>{reviewState==="sending"?"Sending…":"📤 Send Review Request"}</Btn>
                  <Btn sz="sm" v="ghost" onClick={()=>setReviewState("idle")}>Cancel</Btn>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ★ Custom in-CRM cancel confirmation. Replaces window.confirm() which
          is unreliable on iPad Safari (modal can land behind the window or
          get blocked). Always asks the operator whether to notify the client
          and lets them pick before committing. */}
      {cancelConfirmOpen && (
        <div onClick={()=>setCancelConfirmOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(3px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:440,width:"100%",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}}>
            <div style={{padding:"18px 22px",borderBottom:`1px solid ${C.border}`,background:C.redL}}>
              <div style={{fontSize:17,fontWeight:800,color:C.red,fontFamily:S}}>🚫 Cancel Appointment?</div>
              <div style={{fontSize:12,color:C.muted,marginTop:4}}>{appt.treat || "Appointment"} · {appt.cn || ""} · {appt.date} at {format12Hour(appt.time)}</div>
            </div>
            <div style={{padding:"18px 22px"}}>
              {/* ★ Opt-in (off by default). Owner directive 2026-06-10:
                  cancellation SMS only fires when staff EXPLICITLY ticks
                  this — never as a default. The earlier opt-out UX kept
                  surprising the owner with SMS he didn't intend. */}
              <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",padding:"12px 14px",background:cancelNotify?"rgba(192,57,43,0.08)":C.light,border:`1.5px solid ${cancelNotify?C.red+"60":C.border}`,borderRadius:10,marginBottom:10,transition:"all 0.15s"}}>
                <input type="checkbox" checked={cancelNotify} onChange={e=>setCancelNotify(e.target.checked)} style={{marginTop:2,accentColor:C.red,width:18,height:18,flexShrink:0}}/>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3}}>📧 Also send a cancellation SMS + Email</div>
                  <div style={{fontSize:11,color:C.muted}}>Leave UNCHECKED by default. Only check this if you specifically want the client to be notified — most cancellations (duplicates, mistakes) should be silent.</div>
                </div>
              </label>
              <div style={{fontSize:11,color:C.muted,marginTop:6,fontStyle:"italic"}}>This action cannot be undone.</div>
            </div>
            <div style={{padding:"12px 22px",borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"flex-end",gap:8,background:C.light}}>
              <button onClick={()=>setCancelConfirmOpen(false)} style={{padding:"9px 18px",background:"#fff",color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>Keep Appointment</button>
              <button onClick={()=>{
                setCancelConfirmOpen(false);
                onUpdate(appt.id, "cancelled", { notify: cancelNotify });
                onClose();
              }} style={{padding:"9px 18px",background:C.red,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>
                {cancelNotify ? "🚫 Cancel + Notify Client" : "🤫 Silent Cancel (no SMS)"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

const Card = ({children,style,onClick}) => (
  <div onClick={onClick} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",...style}}>{children}</div>
);

const inp = {width:"100%",padding:"10px 13px",background:C.light,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};
const Fld = ({label,children,col,required}) => (
  <div style={{gridColumn:col}}>
    <label style={{display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
      {label}{required&&<span style={{color:C.red,marginLeft:2}}>*</span>}
    </label>
    {children}
  </div>
);

function StatCard({label,value,sub,color,icon,trend}){
  return (
    <Card style={{padding:"18px 20px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
        <div style={{width:36,height:36,borderRadius:9,background:color+"15",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <Ic n={icon} s={17} c={color}/>
        </div>
        {trend!==undefined&&<span style={{fontSize:11,fontWeight:700,color:trend>0?C.green:C.red}}>{trend>0?"+":""}{trend}%</span>}
      </div>
      <div style={{fontSize:24,fontWeight:800,color:C.text,fontFamily:S,marginBottom:3}}>{value}</div>
      <div style={{fontSize:12,color:C.muted}}>{label}</div>
      {sub&&<div style={{fontSize:11,color:C.dim,marginTop:2}}>{sub}</div>}
    </Card>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function Dashboard({clients,appointments}){
  const isMobile = useIsMobile();
  const { canViewSales } = useAuth();   // gate revenue cards to admin/director/Sarah
  const todayAppts = appointments.filter(a=>a.date===TODAY&&a.status==="confirmed");
  const ns = appointments.filter(a=>a.status==="no-show").length;
  const gfeAlerts = []; // Disabled - too many imported clients with pending status
  const lowStock = INVENTORY.filter(i=>i.stock<=i.min);

  // ★ Revenue numbers come from the `payments` table — the same source the
  //   POS / Checkout panel uses — so the dashboard and POS always agree.
  //   Previously the dashboard summed `appointments.price` which double-
  //   counted imported AR appointments and ignored refunds / voids /
  //   custom-amount charges that don't have an appointment_id. Owner
  //   noticed the mismatch on 2026-06-10.
  const [revenue, setRevenue] = useState({ today: 0, allTimeNet: 0, allTimeGross: 0, todayCount: 0 });
  useEffect(() => {
    let active = true;
    const loadRev = async () => {
      // Today's window in local time (matches the POS "Today" range).
      const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
      const endOfToday = new Date(startOfToday); endOfToday.setDate(endOfToday.getDate() + 1);

      // Paginate over the payments table so we don't silently lose rows
      // past Supabase's 1000-row default.
      const all = [];
      let from = 0;
      const PAGE = 1000;
      while (true) {
        const { data } = await supabase
          .from("payments")
          .select("amount,status,created_at")
          .order("created_at", { ascending: false })
          .range(from, from + PAGE - 1);
        if (!data || data.length === 0) break;
        all.push(...data);
        if (data.length < PAGE) break;
        from += PAGE;
      }
      if (!active) return;

      const succeeded = all.filter(p => p.status === "succeeded" || p.status === "completed");
      const refunds = all.filter(p => p.status === "refunded" || Number(p.amount || 0) < 0);
      const gross = succeeded.reduce((s, p) => s + Math.max(0, Number(p.amount || 0)), 0);
      const refundedAmt = Math.abs(refunds.reduce((s, p) => s + Number(p.amount || 0), 0));

      const todaySucceeded = succeeded.filter(p => {
        const t = p.created_at ? new Date(p.created_at) : null;
        return t && t >= startOfToday && t < endOfToday;
      });
      const todayTotal = todaySucceeded.reduce((s, p) => s + Math.max(0, Number(p.amount || 0)), 0);

      setRevenue({
        today: todayTotal,
        allTimeGross: gross,
        allTimeNet: gross - refundedAmt,
        todayCount: todaySucceeded.length,
      });
    };
    loadRev();
    // Live sync — any new payment anywhere in the CRM refreshes the cards.
    const ch = supabase.channel("dashboard-rev-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "payments" }, loadRev)
      .subscribe();
    return () => { active = false; supabase.removeChannel(ch); };
  }, []);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      {/* AI handoff alerts — persistent banner; stays here until staff resolves each one */}
      <AIHandoffsBanner />
      {(gfeAlerts.length>0||lowStock.length>0)&&(
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10}}>
          {gfeAlerts.length>0&&(
            <div style={{padding:"12px 16px",background:C.redL,border:"1px solid rgba(192,57,43,0.2)",borderRadius:10,display:"flex",gap:10,alignItems:"center"}}>
              <Ic n="shield" s={16} c={C.red}/>
              <div><div style={{fontSize:12,fontWeight:700,color:C.red}}>GFE Alert — {gfeAlerts.length} clients</div><div style={{fontSize:11,color:C.muted}}>{gfeAlerts.map(c=>(c.name||"").split(" ")[0]).join(", ")}</div></div>
            </div>
          )}
          {lowStock.length>0&&(
            <div style={{padding:"12px 16px",background:C.orangeL,border:"1px solid rgba(217,119,6,0.2)",borderRadius:10,display:"flex",gap:10,alignItems:"center"}}>
              <Ic n="box" s={16} c={C.orange}/>
              <div><div style={{fontSize:12,fontWeight:700,color:C.orange}}>Low Stock — {lowStock.length} items</div><div style={{fontSize:11,color:C.muted}}>{lowStock.map(i=>i.name.split(" ")[0]).join(", ")}</div></div>
            </div>
          )}
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:12}}>
        {canViewSales && <StatCard label="Revenue Today" value={`$${revenue.today.toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:2})}`} color={C.gold} icon="chart"/>}
        {canViewSales && <StatCard label="Total Revenue (Net)" value={`$${revenue.allTimeNet.toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:2})}`} color={C.green} icon="cash"/>}
        <StatCard label="Today's Appointments" value={todayAppts.length} color={C.blue} icon="cal"/>
        <StatCard label="No-Show Rate" value={`${appointments.length?Math.round((ns/appointments.length)*100):0}%`} color={C.red} icon="warn"/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"2fr 1fr",gap:16}}>
        <Card>
          <div style={{padding:"14px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Today — {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</div>
            <Bdg status="confirmed" label={`${todayAppts.length} confirmed`}/>
          </div>
          {todayAppts.length===0&&<div style={{padding:"30px",textAlign:"center",color:C.dim,fontSize:13}}>No appointments today</div>}
          {todayAppts.map(a=>{const p=PROVIDERS.find(x=>x.id===a.pid);return(
            <div key={a.id} style={{display:"flex",gap:12,alignItems:"center",padding:"12px 18px",borderBottom:`1px solid ${C.border}`}}>
              <div style={{width:50,textAlign:"center",flexShrink:0}}>
                <div style={{fontSize:14,fontWeight:800,color:C.gold,fontFamily:S}}>{format12Hour(a.time)}</div>
                <div style={{fontSize:10,color:C.dim}}>{a.dur}min</div>
              </div>
              <div style={{width:3,height:36,borderRadius:2,background:p?.color||C.gold,flexShrink:0}}/>
              {p?.photo_url ? (
                <img src={p.photo_url} alt={p.name} title={p.name} style={{width:32,height:32,borderRadius:"50%",objectFit:"cover",border:"2px solid "+(p.color||C.gold),flexShrink:0}}/>
              ) : (
                <div title={p?.name||a.prov} style={{width:32,height:32,borderRadius:"50%",background:(p?.color||C.gold)+"22",border:"2px solid "+(p?.color||C.gold),display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:p?.color||C.gold,flexShrink:0}}>{p?.ini || (a.prov||"").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}</div>
              )}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.cn}</div>
                <div style={{fontSize:11,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.treat} · {(a.prov||"").split(" ").slice(0,2).join(" ")}</div>
              </div>
              <div style={{fontSize:14,fontWeight:700,color:C.text,flexShrink:0}}>${a.price}</div>
              <Bdg status={a.checkin||"upcoming"}/>
            </div>
          );})}
        </Card>

        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Card style={{padding:16}}>
            <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>⚕️ Medical Alerts</div>
            {clients.filter(c=>(c.med || "").includes("⚠️")).map(c=>(
              <div key={c.id} style={{padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                <div style={{fontSize:12,fontWeight:600,color:C.text}}>{c.name}</div>
                <div style={{fontSize:11,color:C.red}}>{(c.med||"").replace("⚠️ ","")}</div>
              </div>
            ))}
          </Card>
          <Card style={{padding:16}}>
            <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>📊 Quick Stats</div>
            {[["Active Clients",clients.filter(c=>c.status==="active").length,C.green],["VIP Clients",clients.filter(c=>c.tags?.includes("VIP")).length,C.gold],["Members",clients.filter(c=>c.memberPlan).length,C.purple],["Cards on File",clients.filter(c=>c.cardOnFile).length,C.blue]].map(([l,v,c])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                <span style={{color:C.muted}}>{l}</span><span style={{fontWeight:700,color:c}}>{v}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── FRONT DESK ────────────────────────────────────────────────────────────────
function FrontDesk({appointments,setAppointments,clients}){
  const isMobile = useIsMobile();
  // Fee prompt shown after marking a no-show here (charge $50 or waive).
  const [feePrompt, setFeePrompt] = useState(null);
  // Compute today's date FRESH on every render. Don't rely on the module-
  // level TODAY constant — if the tab stays open past midnight it keeps
  // showing yesterday's appointments. Local-date computed below.
  const todayLocalStr = toLocalDateStr(new Date());
  // Filter out cancelled + no-show so the Front Desk panels always reflect
  // the LIVE state. Previously a cancelled appointment with checkin still
  // set to "upcoming" stayed visible until staff manually re-checked-in.
  const todayAppts = appointments
    .filter(a => a.date === todayLocalStr)
    .filter(a => a.status !== "cancelled" && a.status !== "no-show")
    .sort((a,b) => (a.time || "").localeCompare(b.time || ""));
  // Re-render every 60 seconds so the day rolls over automatically at
  // midnight without requiring a manual refresh.
  const [, _setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => _setTick(x => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const cols = [["upcoming","Upcoming",C.blue],["waiting","⏳ Waiting",C.orange],["in-treatment","💆 In Treatment",C.green],["checked-out","✅ Done",C.muted]];
  const setCheckin = async (id,val) => {
    const updates = {checkin:val, status: val==="checked-out"?"completed":val==="no-show"?"no-show":undefined};
    const clean = Object.fromEntries(Object.entries(updates).filter(([_,v])=>v!==undefined));
    await supabase.from("appointments").update(clean).eq("id",id);

    // ★ When the appointment completes (checkout), deduct a session from any
    // package the client has for this treatment.
    if (val === "checked-out") {
      const appt = appointments.find(a => a.id === id);
      if (appt) await deductPackageIfAvailable(appt);
    }

    // ★ Policy: marking a no-show opens the $50 fee decision (charge or waive).
    if (val === "no-show") {
      const appt = appointments.find(a => a.id === id);
      if (appt) setFeePrompt({ appt, feeType: "no_show" });
    }

    // ★ REMOVED (2026-06-10): this block used to auto-email a cancellation
    //   notice with NO staff opt-in — the only completely ungated client-
    //   facing cancellation path in the app, and the prime suspect for the
    //   Diana Grant incident. Cancellation comms now flow exclusively
    //   through the cancel dialog's opt-in checkbox (updateApptStatus),
    //   and the Edge Functions enforce staff_confirmed:true server-side.
    setAppointments(as=>as.map(a=>a.id===id?{...a,...clean,status:clean.status||a.status}:a));
  };

  // Task checklist — persisted per user in the `frontdesk_tasks` table
  const [tasks, setTasks] = useState([]);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [newTaskText, setNewTaskText] = useState("");

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setTasksLoaded(true); return; }
      const { data } = await supabase.from("frontdesk_tasks")
        .select("*").eq("user_id", user.id).order("sort_order", { ascending: true });
      if (data && data.length > 0) {
        setTasks(data.map(t => ({ id: t.id, text: t.text, done: !!t.done })));
      } else {
        // First time — seed default checklist items, save to DB
        const seeds = [
          "Confirm tomorrow's appointments",
          "Check inventory levels",
          "Review GFE expirations",
          "Process pending payments",
          "Send post-treatment follow-ups",
          "Update client notes from today",
        ];
        const rows = seeds.map((text, i) => ({ user_id: user.id, text, done: false, sort_order: i }));
        const { data: created } = await supabase.from("frontdesk_tasks").insert(rows).select();
        setTasks((created || []).map(t => ({ id: t.id, text: t.text, done: false })));
      }
      setTasksLoaded(true);
    })();
  }, []);

  const toggleTask = async (id) => {
    const next = tasks.map(t => t.id === id ? {...t, done: !t.done} : t);
    setTasks(next);
    const done = next.find(t => t.id === id).done;
    await supabase.from("frontdesk_tasks").update({ done }).eq("id", id);
  };

  const addTask = async () => {
    const text = newTaskText.trim();
    if (!text) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const sort_order = (tasks.length || 0);
    const { data: created } = await supabase.from("frontdesk_tasks")
      .insert([{ user_id: user.id, text, done: false, sort_order }]).select().single();
    if (created) setTasks([...tasks, { id: created.id, text: created.text, done: false }]);
    setNewTaskText("");
  };

  const removeTask = async (id) => {
    setTasks(tasks.filter(t => t.id !== id));
    await supabase.from("frontdesk_tasks").delete().eq("id", id);
  };

  const resetDay = async () => {
    if (!window.confirm("Uncheck all tasks for a new day?")) return;
    const next = tasks.map(t => ({...t, done: false}));
    setTasks(next);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) await supabase.from("frontdesk_tasks").update({ done: false }).eq("user_id", user.id);
  };

  // Appointments the AI booked that still need a verbal confirmation call from front desk
  const needVerbal = appointments.filter(a =>
    a.needs_verbal_confirmation === true &&
    !a.verbal_confirmed_at &&
    a.status !== "cancelled"
  ).sort((a,b) => (a.date + a.time).localeCompare(b.date + b.time));

  const markVerbalConfirmed = async (id) => {
    const now = new Date().toISOString();
    await supabase.from("appointments").update({
      verbal_confirmed_at: now,
      verbal_confirmed_by: "front_desk",
    }).eq("id", id);
    setAppointments(as => as.map(a => a.id === id ? {...a, verbal_confirmed_at: now, verbal_confirmed_by: "front_desk"} : a));
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {/* ★ Confirmation calls sit at the top of the Front Desk screen too,
          not only Today's Mission. This is where the front desk actually
          works all day, and these are already-booked appointments that die
          without a call: 46 of 46 unconfirmed were cancelled, 0 of 16
          confirmed. A row clears only when someone confirms or releases it. */}
      <ConfirmQueuePanel userName={undefined}/>

      {/* AI handoff alerts — front desk + nurses see these here too */}
      <AIHandoffsBanner />
      {needVerbal.length > 0 && (
        <div style={{background:"linear-gradient(135deg, #fff7ed, #ffedd5)",border:"1.5px solid #f97316",borderRadius:12,padding:"14px 18px",boxShadow:"0 4px 12px rgba(249,115,22,0.15)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:8}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{fontSize:22}}>📞</div>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:"#9a3412",fontFamily:S}}>Verbal Confirmation Queue</div>
                <div style={{fontSize:11,color:"#9a3412",opacity:0.85}}>AI booked these — call the client to confirm and lock it in</div>
              </div>
            </div>
            <div style={{padding:"4px 12px",background:"#f97316",color:"#fff",borderRadius:99,fontSize:13,fontWeight:800}}>{needVerbal.length} call{needVerbal.length>1?"s":""} pending</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:280,overflowY:"auto"}}>
            {needVerbal.map(a => {
              const c = clients.find(x => x.id === a.cid);
              const phone = c?.phone || "";
              const callHref = phone ? `tel:${phone.replace(/[^\d+]/g,"")}` : null;
              return (
                <div key={a.id} style={{padding:"10px 12px",background:"#fff",border:"1px solid #fed7aa",borderRadius:9,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:160}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{a.cn}</div>
                    <div style={{fontSize:11,color:C.muted}}>{a.treat} · {fmtAppointmentDate(a.date,{weekday:"short",month:"short",day:"numeric"})} at {format12Hour(a.time)} · {a.prov?.split(" ").slice(0,2).join(" ")}</div>
                    {phone && <div style={{fontSize:11,color:"#9a3412",fontWeight:600,marginTop:2}}>📱 {phone}</div>}
                  </div>
                  {callHref && (
                    <a href={callHref} style={{padding:"7px 14px",background:"linear-gradient(135deg, #f97316, #ea580c)",color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,textDecoration:"none",fontFamily:F}}>📞 Call</a>
                  )}
                  <button onClick={()=>markVerbalConfirmed(a.id)} style={{padding:"7px 14px",background:"linear-gradient(135deg, #2e8b57, #1e6b3f)",color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>✓ Confirmed</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:10}}>
        {cols.map(([status,label,color])=>(
          <div key={status} style={{padding:"14px 16px",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,borderTop:`3px solid ${color}`,boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
            <div style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4}}>{label}</div>
            <div style={{fontSize:26,fontWeight:800,color,fontFamily:S}}>{todayAppts.filter(a=>a.checkin===status).length}</div>
          </div>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(4,1fr)",gap:12}}>
        {cols.map(([status,label,color])=>(
          <div key={status}>
            <div style={{fontSize:12,fontWeight:700,color,marginBottom:8,display:"flex",alignItems:"center",gap:5}}>
              <div style={{width:7,height:7,borderRadius:"50%",background:color}}/>{label.replace("⏳ ","").replace("💆 ","").replace("✅ ","")}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:7}}>
              {todayAppts.filter(a=>a.checkin===status).map(a=>{

const c = clients.find(x=>x.id===a.cid);
                const p = PROVIDERS.find(x=>x.id===a.pid);
                return (
                  <div key={a.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:9,padding:"11px 13px",borderLeft:`3px solid ${color}`,boxShadow:"0 1px 3px rgba(0,0,0,0.05)"}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:2}}>{a.cn}</div>
                    <div style={{fontSize:11,color:C.muted,marginBottom:2}}>{a.time} · {a.treat}</div>
                    <div style={{fontSize:11,color:C.dim,marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
                      {p?.photo_url ? (
                        <img src={p.photo_url} alt={p.name} style={{width:20,height:20,borderRadius:"50%",objectFit:"cover",border:"1.5px solid "+(p.color||C.gold),flexShrink:0}}/>
                      ) : (
                        <div style={{width:20,height:20,borderRadius:"50%",background:(p?.color||C.gold)+"22",border:"1.5px solid "+(p?.color||C.gold),display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:p?.color||C.gold,flexShrink:0}}>{p?.ini || (a.prov||"").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}</div>
                      )}
                      <span>{(a.prov||"").split(" ").slice(0,2).join(" ")} · {a.room}</span>
                    </div>
                    {/* ★ Phone + email — Frances at the front desk needs these visible to make confirmation calls without opening the modal */}
                    {(c?.phone || c?.email) && (
                      <div style={{fontSize:10.5,color:C.muted,marginBottom:6,display:"flex",flexDirection:"column",gap:1}}>
                        {c.phone && <a href={`tel:${c.phone.replace(/[^\d+]/g,"")}`} style={{color:C.gold,fontWeight:600,textDecoration:"none"}}>📞 {c.phone}</a>}
                        {c.email && <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📧 {c.email}</span>}
                      </div>
                    )}
                    {c?.cardOnFile&&<span style={{fontSize:10,color:C.green,background:C.greenL,padding:"1px 6px",borderRadius:6,fontWeight:600,marginBottom:6,display:"inline-block"}}>💳 Card on File</span>}
                    <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:4}}>
                      {status==="upcoming"&&<Btn sz="sm" v="sec" onClick={()=>setCheckin(a.id,"waiting")}>Check In</Btn>}
                      {status==="waiting"&&<><Btn sz="sm" v="primary" onClick={()=>setCheckin(a.id,"in-treatment")}>Start</Btn><Btn sz="sm" v="danger" onClick={()=>setCheckin(a.id,"no-show")}>No Show</Btn></>}
                      {status==="in-treatment"&&<Btn sz="sm" v="success" onClick={()=>setCheckin(a.id,"checked-out")}>Checkout ✓</Btn>}
                    </div>
                  </div>
                );
              })}
              {todayAppts.filter(a=>a.checkin===status).length===0&&(
                <div style={{padding:"16px",textAlign:"center",color:C.dim,fontSize:12,background:C.light,borderRadius:9,border:`1px dashed ${C.border}`}}>Empty</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Task Checklist */}
      <Card style={{padding:0}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${C.border}`,background:C.goldBg,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>📋 Daily Task Checklist</div>
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>{tasks.filter(t=>t.done).length}/{tasks.length} completed{tasks.length>0?` · ${Math.round((tasks.filter(t=>t.done).length/tasks.length)*100)}%`:""}</div>
          </div>
          {tasks.length > 0 && (
            <button onClick={resetDay} style={{padding:"6px 12px",background:"#fff",color:C.gold,border:"1px solid "+C.borderGold,borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>↻ Reset for new day</button>
          )}
        </div>
        <div style={{padding:"8px 18px"}}>
          {!tasksLoaded && <div style={{padding:20,textAlign:"center",color:C.muted,fontSize:12}}>Loading…</div>}
          {tasksLoaded && tasks.length === 0 && (
            <div style={{padding:20,textAlign:"center",color:C.muted,fontSize:12,background:C.light,borderRadius:8,border:"1px dashed "+C.border,marginBottom:10}}>No tasks yet — add your first one below.</div>
          )}
          {tasks.map(task=>(
            <div key={task.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
              <div onClick={()=>toggleTask(task.id)}
                style={{width:20,height:20,borderRadius:5,border:`2px solid ${task.done?C.green:C.border}`,background:task.done?C.green:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
                {task.done&&<Ic n="chk" s={12} c="#fff"/>}
              </div>
              <span style={{fontSize:13,color:task.done?C.dim:C.text,textDecoration:task.done?"line-through":"none",flex:1}}>{task.text}</span>
              <Bdg status={task.done?"completed":"upcoming"} label={task.done?"Done":"Pending"}/>
              <button onClick={()=>removeTask(task.id)} title="Remove this task" style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:14,padding:"2px 6px",borderRadius:5,opacity:0.6}} onMouseEnter={e=>{e.currentTarget.style.opacity="1";e.currentTarget.style.color=C.red;}} onMouseLeave={e=>{e.currentTarget.style.opacity="0.6";e.currentTarget.style.color=C.muted;}}>🗑</button>
            </div>
          ))}
          <div style={{display:"flex",gap:8,marginTop:14,paddingTop:10,borderTop:"1px solid "+C.border}}>
            <input
              value={newTaskText}
              onChange={e=>setNewTaskText(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();addTask();}}}
              placeholder="+ Add a task to your list…"
              style={{...inp,flex:1}}
            />
            <button onClick={addTask} disabled={!newTaskText.trim()} style={{padding:"9px 16px",background:newTaskText.trim()?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":C.light,color:newTaskText.trim()?"#fff":C.muted,border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:newTaskText.trim()?"pointer":"not-allowed",fontFamily:F}}>Add</button>
          </div>
        </div>
      </Card>
      {feePrompt && (
        <FeeDecisionModal appt={feePrompt.appt} feeType={feePrompt.feeType} onDone={()=>setFeePrompt(null)} />
      )}
    </div>
  );
}

// ── SHARED HELPERS ──────────────────────────────────────────
// Stamp a freshly-created appointment row with who-booked-it metadata.
// Pass the existing row object — returns a copy with booked_by_* and booked_at added.
async function stampBookedBy(apptRow, sourceLabel = "crm") {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { ...apptRow, booked_by_source: sourceLabel };
    const { data: prof } = await supabase.from("profiles").select("full_name,email").eq("id", user.id).single();
    return {
      ...apptRow,
      booked_by_user_id: user.id,
      booked_by_user_name: prof?.full_name || prof?.email || user.email || "Staff",
      booked_by_source: sourceLabel,
      booked_at: new Date().toISOString(),
    };
  } catch (e) {
    console.warn("stampBookedBy failed:", e);
    return { ...apptRow, booked_by_source: sourceLabel };
  }
}

// ★ Cancellation policy (owner directive 2026-09-01) — ALL appointments.
//   A card on file is required to book. $50 charged automatically on no-show;
//   $25 on a cancel or reschedule inside 24 hours (that $25 becomes a 14-day
//   wallet credit, so it comes off their treatment when they do come in).
//   Staff can waive either fee with one toggle.
//
//   The charge itself happens ONLY in the `appointment-fee` edge function,
//   which holds a UNIQUE(appointment_id, fee_type) claim — so no appointment
//   can ever be charged twice, no matter how many times a status is toggled
//   or how many tabs are open.
const FEE_AMOUNTS = { no_show: 50, late_change: 25 };
const LATE_WINDOW_HOURS = 24;

// Hours between now and the appointment's start (negative = already passed).
function hoursUntilAppt(appt) {
  try {
    const start = new Date(`${appt.date}T${String(appt.time || "00:00").slice(0, 5)}:00`);
    return (start.getTime() - Date.now()) / 3600000;
  } catch { return null; }
}

// A cancel/reschedule counts as "late" when the ORIGINAL slot was inside the
// 24h window. Slots already long past (>12h ago) are staff cleanup, not a
// client late-change, so they never trigger a fee.
function isLateChange(appt) {
  const h = hoursUntilAppt(appt);
  return h !== null && h <= LATE_WINDOW_HOURS && h > -12;
}

// Decide + apply a fee. `waive: true` records a waiver instead of charging.
// Returns the server's result so the caller can surface it to staff.
async function applyAppointmentFee(appointmentId, feeType, { waive = false, reason = null } = {}) {
  const { data, error } = await supabase.functions.invoke("appointment-fee", {
    body: { appointment_id: appointmentId, fee_type: feeType, waive, reason },
  });
  if (error) return { ok: false, error: error.message || "Fee request failed" };
  return data || { ok: false, error: "No response" };
}

// ★ The one dialog staff sees before any fee. Defaults to CHARGING (that's the
//   policy) with a single toggle to waive — so the normal path is one click
//   and the exception is deliberate and recorded with who decided it.
function FeeDecisionModal({ appt, feeType, onDone }) {
  const [waive, setWaive] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const amount = FEE_AMOUNTS[feeType];
  const isNoShow = feeType === "no_show";

  const submit = async () => {
    setBusy(true);
    const res = await applyAppointmentFee(appt.id, feeType, { waive, reason: reason || null });
    setBusy(false);
    setResult(res);
    if (res?.ok && !res.error) setTimeout(() => onDone(res), waive ? 600 : 1600);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:4000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(3px)"}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:460,padding:24,boxShadow:"0 20px 60px rgba(0,0,0,0.35)"}}>
        <div style={{fontSize:18,fontWeight:800,color:isNoShow?C.red:C.gold,fontFamily:S,marginBottom:4}}>
          {isNoShow ? "👻 No-Show Fee" : "🕐 Late Change Fee"}
        </div>
        <div style={{fontSize:12.5,color:C.muted,marginBottom:16}}>
          {appt.cn || "Client"} · {appt.treat || "Appointment"} · {appt.date} {String(appt.time||"").slice(0,5)}
        </div>

        {!result && (
          <>
            <div style={{padding:"14px 16px",background:waive?C.light:(isNoShow?"rgba(192,57,43,0.08)":C.goldBg),border:`1px solid ${waive?C.border:(isNoShow?C.red+"40":C.borderGold)}`,borderRadius:10,marginBottom:14}}>
              <div style={{fontSize:26,fontWeight:800,color:waive?C.muted:(isNoShow?C.red:C.gold),textDecoration:waive?"line-through":"none"}}>${amount}</div>
              <div style={{fontSize:12,color:C.muted,marginTop:2}}>
                {waive ? "Will NOT be charged — waived" : `Charged to the card on file${feeType==="late_change" ? " · becomes a 14-day wallet credit" : ""}`}
              </div>
            </div>

            <label style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:waive?"rgba(46,139,87,0.10)":C.light,border:`1px solid ${waive?C.green+"50":C.border}`,borderRadius:10,cursor:"pointer",marginBottom:12}}>
              <div onClick={()=>setWaive(v=>!v)} style={{width:44,height:26,borderRadius:14,background:waive?C.green:C.dim,position:"relative",flexShrink:0,transition:"all .18s"}}>
                <div style={{position:"absolute",top:2,left:waive?20:2,width:22,height:22,borderRadius:"50%",background:"#fff",transition:"all .18s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/>
              </div>
              <input type="checkbox" checked={waive} onChange={e=>setWaive(e.target.checked)} style={{display:"none"}}/>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:C.text}}>Waive this fee</div>
                <div style={{fontSize:11,color:C.muted}}>For exceptions — first time, our mistake, emergency</div>
              </div>
            </label>

            {waive && (
              <input value={reason} onChange={e=>setReason(e.target.value)} placeholder="Reason (optional) — e.g. first time, sick"
                style={{width:"100%",padding:"10px 12px",background:C.light,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box",marginBottom:12}}/>
            )}

            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>onDone(null)} disabled={busy} style={{padding:"10px 16px",fontSize:13,fontWeight:600,background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,cursor:"pointer",fontFamily:F}}>Decide later</button>
              <button onClick={submit} disabled={busy} style={{padding:"10px 22px",fontSize:13,fontWeight:700,background:busy?C.light:(waive?`linear-gradient(135deg,${C.green},#246b45)`:`linear-gradient(135deg,${C.gold},${C.goldD})`),color:busy?C.muted:"#fff",border:"none",borderRadius:8,cursor:busy?"not-allowed":"pointer",fontFamily:F}}>
                {busy ? "Working…" : (waive ? "Waive — don't charge" : `Charge $${amount}`)}
              </button>
            </div>
          </>
        )}

        {result && (
          <>
            <div style={{padding:"14px 16px",borderRadius:10,marginBottom:14,
              background: result.ok ? (result.status==="charged"?C.greenL:C.light) : "rgba(192,57,43,0.10)",
              border:`1px solid ${result.ok ? (result.status==="charged"?C.green+"50":C.border) : C.red+"50"}`}}>
              <div style={{fontSize:14,fontWeight:700,color: result.ok ? (result.status==="charged"?C.green:C.text) : C.red}}>
                {result.ok
                  ? (result.status==="charged" ? `✅ $${result.amount} charged${result.card_last4?` (•••• ${result.card_last4})`:""}`
                    : result.status==="waived" ? "✅ Fee waived"
                    : result.status==="no_card" ? "⚠️ No card on file — nothing charged"
                    : result.status==="already" ? `ℹ️ Already handled (${result.existing_status})`
                    : "Done")
                  : `❌ ${result.error || "Charge failed"}`}
              </div>
              {result.message && <div style={{fontSize:12,color:C.muted,marginTop:4}}>{result.message}</div>}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end"}}>
              <button onClick={()=>onDone(result)} style={{padding:"10px 20px",fontSize:13,fontWeight:700,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontFamily:F}}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Deduct one session from any active package the client has for this treatment.
// Safe to call multiple times — consumePackageSession is a no-op when there's
// no matching package or zero sessions left.
async function deductPackageIfAvailable(appt) {
  try {
    if (!appt?.cid || !appt?.treat) return;
    const { data: { user } } = await supabase.auth.getUser();
    const { data: prof } = user ? await supabase.from("profiles").select("full_name,email").eq("id", user.id).single() : { data: null };
    await consumePackageSession(
      appt.cid,
      appt.treat,
      appt.id,
      user?.id || null,
      prof?.full_name || prof?.email || "Front Desk"
    );
  } catch (e) { console.warn("Package deduction failed:", e); }
}

// Compact widget shown on the client Overview tab — lists active packages with
// "X of Y sessions used" progress bars. Updates live via realtime subscription.
function ClientPackagesCompact({ clientId }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.from("patient_packages")
        .select("*").eq("client_id", clientId).order("expires_at", { ascending: true });
      if (active) { setPacks(data || []); setLoading(false); }
    })();
    const ch = supabase.channel("client-packs-"+clientId)
      .on("postgres_changes", { event: "*", schema: "public", table: "patient_packages", filter: "client_id=eq."+clientId }, async () => {
        const { data } = await supabase.from("patient_packages").select("*").eq("client_id", clientId).order("expires_at", { ascending: true });
        if (active) setPacks(data || []);
      }).subscribe();
    return () => { active = false; supabase.removeChannel(ch); };
  }, [clientId]);

  const activePacks = packs.filter(p => p.status === "active" && (p.sessions_remaining||0) > 0);
  if (loading) return null;
  if (activePacks.length === 0) return null;

  return (
    <div style={{marginTop:10,padding:12,background:"linear-gradient(135deg,"+C.goldBg+",#fff)",borderRadius:9,border:`1px solid ${C.borderGold}`}}>
      <div style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>📦 Active Packages ({activePacks.length})</div>
      {activePacks.map(p => {
        const used = p.sessions_used || 0;
        const remain = p.sessions_remaining || 0;
        const total = p.sessions_total || (used + remain);
        const pct = total > 0 ? Math.round((used / total) * 100) : 0;
        return (
          <div key={p.id} style={{marginBottom:8,paddingBottom:8,borderBottom:"1px solid "+C.borderSoft}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
              <div style={{fontSize:12,fontWeight:700,color:C.text}}>{p.treatment || p.package_name || "Package"}</div>
              <div style={{fontSize:12,color:C.gold,fontWeight:800}}>{remain} <span style={{fontSize:10,color:C.muted,fontWeight:600}}>of {total} left</span></div>
            </div>
            <div style={{height:6,background:C.light,borderRadius:6,overflow:"hidden"}}>
              <div style={{height:"100%",width:pct+"%",background:"linear-gradient(90deg,"+C.gold+","+C.goldD+")",transition:"width 0.4s"}}/>
            </div>
            <div style={{fontSize:10,color:C.muted,marginTop:3}}>{used} used · {pct}% complete{p.expires_at?" · expires "+new Date(p.expires_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):""}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── APPOINTMENTS ──────────────────────────────────────────────────────────────
function Appointments({appointments,setAppointments,clients,setClients,openClientProfile, jumpToDate, clearJumpDate}){
  useProviders();
  // Fee decision prompt (no-show $50 / late change $25) — see FeeDecisionModal.
  const [feePrompt, setFeePrompt] = useState(null);
  // Card-on-file prompt shown right after booking when the client has no card.
  const [cardPrompt, setCardPrompt] = useState(null);
  const [services, setServices] = useState([]);
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from("services").select("*").eq("active", true).order("category").order("name");
      if (data) setServices(data);
    };
    load();
    const ch = supabase.channel("services-appts-sync").on("postgres_changes", {event:"*", schema:"public", table:"services"}, load).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);
  // ★ Retail/products (skincare lines like Hydrinity, IS Clinical, Avinichi)
  //   shouldn't appear in the appointment-booking dropdown — owner explicitly
  //   asked: "we don't book appointments with products, only with services."
  //   Detection: any category that starts with "Retail" is a product line.
  //   The full `services` list is still used for price/duration LOOKUPS by
  //   name (in case a legacy appointment references an old retail item) —
  //   only the picker dropdown gets the filtered subset.
  const bookableServices = services.filter(s => !/^retail/i.test(String(s.category || "").trim()));
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({name:"",phone:"",email:"",dob:"",loc:"Nova Med Spa"});
  const [depositCharge, setDepositCharge] = useState(null);
  const isMobile = useIsMobile();
  const isCompact = useIsCompact();
  // ★ Default to "By Provider" — staff want to see the day grouped by who's
  //   booked with whom, not a flat chronological list.
  const [view,setView] = useState("provider");
  const [filter,setFilter] = useState("all");
  // Provider focus filter — on iPad/phone the day-view's multi-provider grid
  // is too cramped. Default to ALL on desktop, first provider on compact.
  const [providerFocusId, setProviderFocusId] = useState(null); // null = all
  const [showF,setShowF] = useState(false);
  const [selAppt,setSelAppt] = useState(null);
  // ★ Visible SMS/email status toast — shows right after booking / cancel /
  //   reschedule so staff can SEE if comms went out instead of digging in
  //   the console. Shape: { ok: bool, kind: "booking"|"cancel"|"resched",
  //   sms: "ok"|err string, email: "ok"|err string } | null
  const [commToast, setCommToast] = useState(null);
  // Hover preview tooltip — shows on Day view when hovering an appointment.
  // We pass {appt, x, y} so we can absolutely-position the tooltip near the cursor.
  const [hoverPreview, setHoverPreview] = useState(null);
  const [recordingAppt, setRecordingAppt] = useState(null);
  const [weekOffset,setWeekOffset] = useState(0);
  const [monthOffset,setMonthOffset] = useState(0);
  const [form,setForm] = useState({});

  // Waitlist drawer
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);

  // ★ Client photo map — client_id → URL of best face photo (front angle
  // preferred). Used to show the CLIENT'S face on the Day view appointment
  // rows instead of the provider avatar.
  const [clientPhotoMap, setClientPhotoMap] = useState(new Map());
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("checkins")
        .select("client_id, face_photos, checked_in_at")
        .not("client_id", "is", null)
        .order("checked_in_at", { ascending: false });
      const map = new Map();
      for (const ck of data || []) {
        if (!ck.client_id || map.has(ck.client_id)) continue;
        const photos = Array.isArray(ck.face_photos) ? ck.face_photos : [];
        if (photos.length === 0) continue;
        const front = photos.find(p => p?.angle === "front") || photos[0];
        if (front?.url) map.set(ck.client_id, front.url);
      }
      setClientPhotoMap(map);
    };
    load();
    const ch = supabase.channel("appts-client-photos-sync")
      .on("postgres_changes", { event:"*", schema:"public", table:"checkins" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  // First-last initials from "Sarah King" → "SK"
  const clientInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // ★ New vs Existing patient detection
  // Build a set of appointment IDs that represent a client's FIRST-EVER non-
  // cancelled appointment. Used to flag "🆕 NEW PATIENT" badges in the
  // calendar + appointment modal. Existing logic: per client, find earliest
  // (date, time) appointment that isn't cancelled — that one is "new", every
  // later appt for the same client is "existing".
  const newPatientApptIds = useMemo(() => {
    const firstByClient = new Map(); // cid → {id, date, time}
    for (const a of appointments) {
      if (!a.cid || a.status === "cancelled") continue;
      const cur = firstByClient.get(a.cid);
      const key = `${a.date}T${a.time || "00:00"}`;
      if (!cur || key < cur.key) {
        firstByClient.set(a.cid, { id: a.id, key });
      }
    }
    return new Set([...firstByClient.values()].map(v => v.id));
  }, [appointments]);

  // Small inline badge factory — keeps the call sites tidy.
  const newBadge = (apptId, compact = false) =>
    newPatientApptIds.has(apptId)
      ? (
        <span style={{
          fontSize: compact ? 8 : 9,
          padding: compact ? "1px 5px" : "2px 7px",
          background: "linear-gradient(135deg,#10b981,#059669)",
          color: "#fff",
          borderRadius: 50,
          fontWeight: 800,
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
          textTransform: "uppercase",
          flexShrink: 0,
        }}>🆕 New</span>
      )
      : (
        <span style={{
          fontSize: compact ? 8 : 9,
          padding: compact ? "1px 5px" : "2px 7px",
          background: "rgba(37,99,235,0.10)",
          color: "#2563eb",
          borderRadius: 50,
          fontWeight: 800,
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
          textTransform: "uppercase",
          flexShrink: 0,
        }}>★ Existing</span>
      );

  // Live waitlist count for the badge on the calendar header button
  useEffect(() => {
    const load = async () => {
      const { count } = await supabase.from("waitlist_entries")
        .select("id", { count: "exact", head: true })
        .eq("status", "waiting");
      setWaitlistCount(count || 0);
    };
    load();
    const ch = supabase.channel("waitlist-count")
      .on("postgres_changes", { event:"*", schema:"public", table:"waitlist_entries" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  // Day view date — uses LOCAL date so tapping a day on phone/iPad after 8PM
  // doesn't roll over to "tomorrow" (was a bug because toISOString returns UTC).
  const [dayOffset,setDayOffset] = useState(0);

  // ★ Honour the parent's jumpToDate (set when user clicks "Back to
  //   Appointments" from a client profile). Convert the date string into
  //   an offset from today so the existing day-nav code keeps working.
  useEffect(() => {
    if (!jumpToDate) return;
    const target = new Date(jumpToDate + "T12:00:00");
    const now = new Date();
    now.setHours(12, 0, 0, 0);
    const diffDays = Math.round((target - now) / 86400000);
    setDayOffset(diffDays);
    clearJumpDate && clearJumpDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToDate]);
  const currentDay = new Date();
  currentDay.setDate(currentDay.getDate()+dayOffset);
  const currentDayStr = toLocalDateStr(currentDay);

  // Provider weekly hours — load once, cache, and use to filter the day-view
  // columns to ONLY providers who actually work that day of week. Keeps the
  // grid clean and prevents empty-Sunday-column noise.
  const [providerWeeklyHours, setProviderWeeklyHours] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase.from("provider_weekly_hours").select("provider_id,day_of_week,available,start_time,end_time");
      if (!cancelled) setProviderWeeklyHours(data || []);
    };
    load();
    const ch = supabase.channel("prov-weekly-hours-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "provider_weekly_hours" }, load)
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, []);

  // ★ Date-specific extra availability (guest providers, e.g. Lisbell) — a
  //   provider with an extra-day row counts as ON DUTY that date, so their
  //   column shows in the calendar even with no weekly schedule.
  const [providerExtraDays, setProviderExtraDays] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase.from("provider_extra_availability").select("provider_id,date,start_time,end_time");
      if (!cancelled) setProviderExtraDays(data || []);
    };
    load();
    const ch = supabase.channel("prov-extra-days-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "provider_extra_availability" }, load)
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, []);

  // ★ Holidays / special-hours overrides — show a marker on those days.
  const [blockedDates, setBlockedDates] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase.from("booking_blocked_dates").select("*");
      if (!cancelled) setBlockedDates(data || []);
    };
    load();
    const ch = supabase.channel("blocked-dates-cal-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "booking_blocked_dates" }, load)
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, []);
  const blockedFor = (dateStr) => (blockedDates || []).find(b => b.date === dateStr) || null;

  // Week view
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1 + (weekOffset*7));
  const days = Array.from({length:5},(_,i)=>{
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate()+i);
    return d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"2-digit"});
  });
  const dmap = Object.fromEntries(days.map((d,i)=>{
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate()+i);
    return [d,date.toISOString().split("T")[0]];
  }));
  const hours = ["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00"];
  // ★ Skip cancelled + no-show so the week grid doesn't show "ghost" squares
  // for appointments the front desk already invalidated.
  const getSlot = (day,hr) => {const date=dmap[day];const h=hr.split(":")[0].padStart(2,"0");return appointments.find(a=>a.date===date&&a.time.startsWith(h)&&a.status!=="cancelled"&&a.status!=="no-show");};

  // Month view
  const monthDate = new Date();
  monthDate.setMonth(monthDate.getMonth()+monthOffset);
  const monthYear = monthDate.getFullYear();
  const monthNum = monthDate.getMonth();
  const firstDay = new Date(monthYear,monthNum,1).getDay();
  const daysInMonth = new Date(monthYear,monthNum+1,0).getDate();
  const monthName = monthDate.toLocaleDateString("en-US",{month:"long",year:"numeric"});

  const filtered = (filter==="all"?appointments:appointments.filter(a=>a.status===filter)).sort((a,b)=>b.date.localeCompare(a.date)||b.time.localeCompare(a.time));

  const [consentModal, setConsentModal] = useState(null);

  const openCheckIn = (appt) => {
    // ★ Match tolerant of bigint-vs-string id types (a recurring mismatch here),
    //   and fall back to the appointment's OWN client id + name when the client
    //   isn't in the loaded list (pagination/type) — otherwise check-in dies on
    //   "Cannot save — missing client" even though the appt has a client. Mirrors
    //   the same fallback used by the treatment-record flow.
    const client = clients.find(c => String(c.id) === String(appt.cid)) || { id: appt.cid, name: appt.cn };
    setConsentModal({appt, client});
  };

  const completeCheckIn = async (payload) => {
    // payload can be either { signedDocs, visit_type, face_photos } (new CheckInFlow)
    // or the legacy array of signed docs.
    const signedDocs = Array.isArray(payload) ? payload : payload.signedDocs;
    const visitType = Array.isArray(payload) ? null : payload.visit_type;
    const facePhotos = Array.isArray(payload) ? [] : (payload.face_photos || []);
    const questionnaireAnswers = Array.isArray(payload) ? [] : (payload.questionnaire_answers || []);
    const { appt } = consentModal;
    const { data: { user } } = await supabase.auth.getUser();
    const { data: prof } = await supabase.from("profiles").select("full_name").eq("id", user.id).single();
    // Upsert: CheckInFlow now saves progressively after the photos and
    // questionnaire steps so a row may already exist for this appointment.
    // We want this final write to overwrite/extend that row, not collide.
    const checkinRow = {
      appointment_id: appt.id,
      client_id: appt.cid,
      client_name: appt.cn,
      checked_in_by: user.id,
      checked_in_by_name: prof?.full_name || user.email,
      consents_signed: signedDocs.map(d => ({id:d.id, name:d.template_name})),
      visit_type: visitType,
      questionnaire_answers: questionnaireAnswers,
    };
    // ★ PHOTO PRESERVATION (Frances bug):
    //   When staff re-opens an already-checked-in appointment to sign a
    //   newly-assigned consent, CheckInFlow may finish with face_photos
    //   = [] (e.g. if a load race left state empty). Writing that empty
    //   array on UPDATE would nuke photos taken in the first pass.
    //   So: only write face_photos on UPDATE if we actually have some.
    //   On INSERT we always write (column may be NOT NULL).
    const { data: existing } = await supabase
      .from("checkins").select("id, face_photos").eq("appointment_id", appt.id).limit(1);
    if (existing?.length) {
      if (Array.isArray(facePhotos) && facePhotos.length > 0) {
        checkinRow.face_photos = facePhotos;
      }
      // else: omit face_photos from the patch → existing row keeps its photos
      await supabase.from("checkins").update(checkinRow).eq("id", existing[0].id);
    } else {
      checkinRow.face_photos = facePhotos;
      await supabase.from("checkins").insert([checkinRow]);
    }
    await supabase.from("appointments").update({checkin:"waiting"}).eq("id", appt.id);
    setAppointments(as => as.map(a => a.id === appt.id ? {...a, checkin:"waiting"} : a));
    setConsentModal(null);
  };

  const updateApptStatus = async (id,status,opts={}) => {
    // opts.notify: when an appointment is cancelled MANUALLY through the
    // calendar, the modal asks the user whether to text/email the client.
    // ★ DEFAULT FALSE (2026-06-10) — owner directive: never send a cancel
    //   SMS unless staff explicitly checked the "Notify the client" box.
    //   The opt-in UX is the only safe one; opt-out led to repeated
    //   accidental SMS while cleaning up duplicate appointments.
    const notify = opts.notify === true;
    const updated = {status, checkin: status==="completed"?"checked-out":status==="no-show"?"no-show":undefined};
    // ★ AUDIT — owner reported appointments "cancelling themselves". The code
    //   never auto-cancels, but to PROVE that next time and to know which
    //   staff member cancelled, we stamp cancelled_at + cancelled_by_name on
    //   every cancel write. Shown on the appointment modal under "Cancelled by".
    if (status === "cancelled") {
      try {
        const { data: { user: _u } } = await supabase.auth.getUser();
        if (_u?.id) {
          const { data: _p } = await supabase.from("profiles").select("full_name,email").eq("id", _u.id).maybeSingle();
          updated.cancelled_by_name = _p?.full_name || _p?.email || _u.email || "Unknown";
        } else {
          updated.cancelled_by_name = "Unknown (not signed in)";
        }
        updated.cancelled_at = new Date().toISOString();
      } catch (e) {
        console.warn("cancel audit stamp failed:", e);
      }
    }
    const cleanUpdate = Object.fromEntries(Object.entries(updated).filter(([_,v])=>v!==undefined));
    await supabase.from("appointments").update(cleanUpdate).eq("id",id);
    setAppointments(as=>as.map(a=>a.id===id?{...a,status:cleanUpdate.status||a.status,checkin:cleanUpdate.checkin||a.checkin,cancelled_at:cleanUpdate.cancelled_at||a.cancelled_at,cancelled_by_name:cleanUpdate.cancelled_by_name||a.cancelled_by_name}:a));

    // ★ Deduct package session when an appointment is completed
    if (status === "completed") {
      const appt = appointments.find(a => a.id === id);
      if (appt) await deductPackageIfAvailable(appt);
    }

    // ★ Auto-increment client.noshow when an appointment is marked no-show.
    // This is what triggers the auto-deposit requirement on future bookings.
    if (status === "no-show") {
      const appt = appointments.find(a => a.id === id);
      if (appt?.cid) {
        try {
          const { data: cur } = await supabase.from("clients").select("noshow").eq("id", appt.cid).single();
          const next = (Number(cur?.noshow) || 0) + 1;
          await supabase.from("clients").update({ noshow: next }).eq("id", appt.cid);
        } catch (e) { console.warn("Failed to bump client noshow:", e); }
      }
      // ★ Policy: no-show always opens the $50 fee decision (charge or waive).
      if (appt) setFeePrompt({ appt, feeType: "no_show" });
    }

    // ★ Policy: a cancel INSIDE the 24h window opens the $25 late-change fee.
    //   Cancels made in good time (or cleanup of long-past slots) charge nothing.
    if (status === "cancelled") {
      const _appt = appointments.find(a => a.id === id);
      if (_appt && isLateChange(_appt)) setFeePrompt({ appt: _appt, feeType: "late_change" });
    }

    // ★ Cancellation SMS + Email — sends only when `notify` is true.
    //   Each call is awaited + status surfaced to staff in commToast.
    if (status === "cancelled" && notify !== false) {
      const appt = appointments.find(a => a.id === id);
      const client = appt?.cid ? clients.find(c => c.id === appt.cid) : null;
      const phone = client?.phone || appt?.phone || "";
      const email = client?.email || appt?.email || "";
      const niceName = client?.name || appt?.cn || "Client";
      let _smsStatus = phone ? "pending" : "no_phone";
      let _emailStatus = email ? "pending" : "no_email";

      if (phone) {
        try {
          const { data, error } = await supabase.functions.invoke("notify-appointment-change", {
            body: {
              type: "cancellation",
              client_name: niceName,
              client_phone: phone,
              service: appt?.treat || "appointment",
              provider: appt?.provider_name || "",
              date: appt?.date,
              time: appt?.time,
              location: appt?.loc || "",
              force: true,  // bypass the AI-already-replied guard for staff-driven cancels
              // ★ Server-side gate token — cancellation messages are blocked
              //   at the Edge Function unless this is true. Only THIS code
              //   path (staff ticked the opt-in checkbox) sets it.
              staff_confirmed: true,
            }
          });
          if (error) {
            let detail = error.message || "unknown";
            try { if (error.context?.json) { const j = await error.context.json(); if (j?.error) detail = j.error; } } catch {}
            _smsStatus = "FAIL: " + detail;
            console.error("[cancel-sms]", error, data);
          } else if (data?.error) _smsStatus = "FAIL: " + data.error;
          else if (data?.skipped) _smsStatus = "SKIPPED: " + data.skipped;
          else _smsStatus = "ok";
        } catch (e) { _smsStatus = "FAIL: " + (e?.message || "exception"); }
      }
      if (email) {
        try {
          const { data, error } = await supabase.functions.invoke("send-appointment-email", {
            body: {
              type: "cancellation",
              to_email: email,
              to_name: niceName,
              service: appt?.treat || "appointment",
              provider: appt?.provider_name || "",
              date: appt?.date,
              time: appt?.time,
              location: appt?.loc || "",
              staff_confirmed: true,  // server-side gate token (checkbox path only)
            }
          });
          if (error) {
            let detail = error.message || "unknown";
            try { if (error.context?.json) { const j = await error.context.json(); if (j?.error) detail = j.error; } } catch {}
            _emailStatus = "FAIL: " + detail;
            console.error("[cancel-email]", error, data);
          } else if (data?.error) _emailStatus = "FAIL: " + data.error;
          else _emailStatus = "ok";
        } catch (e) { _emailStatus = "FAIL: " + (e?.message || "exception"); }
      }
      setCommToast({ kind: "cancel", client: niceName, phone, email, sms: _smsStatus, email_status: _emailStatus });
      setTimeout(() => setCommToast(null), 12000);
    }

    // ★ Auto-promote from waitlist when a slot frees up. Runs for any
    // cancellation (manual or otherwise). The function is idempotent — if
    // no matching waitlist entry exists it returns { skipped: "no_match" }.
    if (status === "cancelled") {
      try {
        const { data } = await supabase.functions.invoke("waitlist-promote", {
          body: { cancelled_appointment_id: id, source: "auto_cancel" }
        });
        if (data?.ok && data?.appointment) {
          // Show the new appointment immediately + tell staff
          setAppointments(as => [data.appointment, ...as.filter(a => a.id !== data.appointment.id)]);
          const _name = data.appointment.cn || "Waitlist client";
          alert(`✅ Auto-promoted ${_name} from the waitlist into this freed slot.\n\nConfirmation SMS sent.`);
        }
      } catch (e) { console.error("Waitlist auto-promote failed:", e); }
    }
    };

  const [editAppt,setEditAppt] = useState(null);
  const handleEdit = (appt) => {setEditAppt(appt);setForm({...appt});setShowF(true);};

  // ★ Live-call "Book appointment" (LiveCallPanel): open New Appointment with
  //   the lead already in it — existing client selected, or the new-client
  //   form pre-filled with her name / phone / email — so the desk books while
  //   still on the call instead of asking for everything again.
  useEffect(() => {
    const open = () => {
      const p = window.__elyonPendingLeadBooking;
      if (!p || Date.now() - p.at > 5 * 60000) return;
      window.__elyonPendingLeadBooking = null;
      const d10 = String(p.phone || "").replace(/\D/g, "").slice(-10);
      const email = String(p.email || "").trim().toLowerCase();
      const match = (p.clientId && clients.find(c => String(c.id) === String(p.clientId)))
        || (d10 && clients.find(c => String(c.phone || "").replace(/\D/g, "").slice(-10) === d10))
        || (email && clients.find(c => String(c.email || "").trim().toLowerCase() === email));
      setEditAppt(null);
      setForm({treat:"",pid:"",date:currentDayStr,time:"10:00",dur:60,price:"",status:"confirmed",room:"",loc:"Nova Med Spa",dep:0,notes:p.interest?`Interested in: ${p.interest} (auto-dial lead)`:"",checkin:"upcoming",tip:0,paid:false,...(match?{cid:match.id}:{})});
      if (match) {
        setShowNewClient(false);
      } else {
        setNewClient({name:p.name||"",phone:p.phone||"",email:p.email||"",dob:"",loc:"Nova Med Spa"});
        setShowNewClient(true);
      }
      setShowF(true);
    };
    open(); // panel may have switched tabs to get here — pick up the stashed lead
    window.addEventListener("elyon:book-for-lead", open);
    return () => window.removeEventListener("elyon:book-for-lead", open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, currentDayStr]);
  const saveEdit = async () => {
    const _oldAppt = editAppt ? {...editAppt} : null;
    const oldAppt = editAppt;
    if(!form.treat){alert("Please select a treatment");return;}

const c = clients.find(x=>x.id===form.cid);
    const updated = {...form,cn:c?.name||form.cn};

    // Strip any field starting with "_" — those are client-side computed
    // helpers (e.g. _start, _end added by the Provider view for layout) and
    // do NOT exist as DB columns. Postgres will reject the whole update with
    // "Could not find the '_end' column" if we leave them in. Also strip
    // `deposit_waived` which is a form-only flag (waive UX), not a DB column.
    delete updated.deposit_waived;
    for (const k of Object.keys(updated)) {
      if (k.startsWith("_")) delete updated[k];
    }

    // Numeric/bigint columns — Postgres rejects "", "undefined", NaN, "NaN".
    // Convert valid strings → number, drop the rest entirely so Postgres keeps
    // existing value or uses column default.
    const numericKeys = ["id", "cid", "pid", "dur", "price", "dep", "tip"];
    for (const k of numericKeys) {
      const v = updated[k];
      if (v === undefined || v === null || v === "" || v === "undefined" || v === "NaN") {
        delete updated[k];
        continue;
      }
      const n = typeof v === "number" ? v : Number(v);
      if (Number.isNaN(n)) {
        delete updated[k];
      } else {
        updated[k] = n;
      }
    }

    // Catch-all: strip any remaining literal "undefined" / undefined values
    // on non-numeric fields too — keeps Postgres happy on text/bool columns
    // that were left blank.
    for (const k of Object.keys(updated)) {
      if (updated[k] === "undefined" || updated[k] === undefined) delete updated[k];
    }

    // id is required for the .eq() filter but should not appear in the SET clause
    delete updated.id;

    // ★ WHITELIST — only send columns that actually exist on the
    //   appointments table. The form object is a spread of the appointment
    //   row + any locally-computed extras (calendar layout helpers,
    //   provider name lookups, etc.). Without this filter, an old or
    //   freshly-inserted appointment with a stray extra key (e.g.
    //   `_clientName`, `customer_confirmed_at` set to a non-ISO string,
    //   `addons` deserialized weirdly) makes Postgres reject the whole
    //   update with "Update failed: column does not exist" and the user
    //   loses their edit. Owner reported this specifically while editing
    //   notes — was failing under the surface for any save.
    const ALLOWED_APPT_COLUMNS = new Set([
      "cid","cn","treat","pid","prov","date","time","dur","price",
      "status","room","dep","loc","checkin","tip","paid","notes",
      "addons","chart_markers","clinical_notes","post_care_notes",
      "customer_confirmed","customer_confirmed_at",
      "needs_verbal_confirmation","verbal_confirmed_at","verbal_confirmed_by",
      "treatment_recorded","treatment_recorded_at","treatment_recorded_by_name","treatment_recorded_by_user_id",
      "booked_at","booked_by_ai","booked_by_source","booked_by_user_id","booked_by_user_name",
      "provider_name","provider_user_id",
      "cancelled_at","cancelled_by_name",
      "card_requirement_waived","card_waived_by_name",
      "updated_at",
    ]);
    for (const k of Object.keys(updated)) {
      if (!ALLOWED_APPT_COLUMNS.has(k)) delete updated[k];
    }

    // ★ RESCHEDULE HISTORY (audit fix 2026-06-10) — when date/time change,
    //   append the old slot to notes so the original is never lost. The
    //   cancel flow got this treatment June 8 (cancelled_at/by); edits had
    //   no trail at all — a mis-typed date silently destroyed the original.
    if (_oldAppt && (_oldAppt.date !== form.date || _oldAppt.time !== form.time)) {
      try {
        const { data: { user: _ru } } = await supabase.auth.getUser();
        const { data: _rp } = _ru ? await supabase.from("profiles").select("full_name,email").eq("id", _ru.id).maybeSingle() : { data: null };
        const _staff = _rp?.full_name || _rp?.email || "staff";
        const stamp = `[RESCHEDULED ${_oldAppt.date} ${_oldAppt.time} → ${form.date} ${form.time} by ${_staff} ${new Date().toISOString().slice(0,16)}]`;
        updated.notes = ((updated.notes || form.notes || "") + " " + stamp).trim();
      } catch (e) { console.warn("resched history stamp failed:", e); }
    }

    // ★ STALE-ID GUARD (2026-06-13) — if this appointment object lost its
    //   id (see the state-merge bug below), .eq("id", undefined) produces
    //   Postgres "invalid input syntax for type bigint: undefined" and the
    //   staff's edit is lost. Fail fast with a human explanation instead.
    if (form.id === undefined || form.id === null || form.id === "undefined") {
      alert("This appointment view is stale — please refresh the page (F5) and edit again. Your changes were NOT saved.");
      return;
    }

    const { error } = await supabase.from("appointments").update(updated).eq("id",form.id);
    if (error) { alert("Update failed: " + error.message); return; }
    // ★ MERGE over the existing row — `updated` deliberately has NO id
    //   (deleted above for the SET clause). Replacing the state object
    //   with it wholesale stripped the id, so the SECOND edit of the same
    //   appointment failed with bigint "undefined" (front-desk report
    //   2026-06-13, morning). Spread keeps id + any column we dropped.
    setAppointments(as=>as.map(a=>a.id===form.id?{...a, ...updated, id: form.id}:a));

    // ★ Policy (2026-09-01): a reschedule decided INSIDE 24h of the ORIGINAL
    //   slot triggers the $25 late-change fee. Measured against the old date/
    //   time — moving a Tuesday appointment on Monday morning is free; moving
    //   tonight's appointment an hour before it is not. Staff can waive.
    if (_oldAppt && (_oldAppt.date !== form.date || _oldAppt.time !== form.time) && isLateChange(_oldAppt)) {
      setFeePrompt({ appt: { ..._oldAppt, id: form.id }, feeType: "late_change" });
    }

    // ★ Reschedule SMS + Email — fire only when date or time changed.
    //   Awaited + reported to staff via commToast.
    if (_oldAppt && (_oldAppt.date !== form.date || _oldAppt.time !== form.time)) {
      const client = form.cid ? clients.find(c => c.id === form.cid) : null;
      const phone = client?.phone || form.phone || "";
      const email = client?.email || form.email || "";
      const niceName = client?.name || form.cn || "Client";
      let _smsStatus = phone ? "pending" : "no_phone";
      let _emailStatus = email ? "pending" : "no_email";

      if (phone) {
        try {
          const { data, error } = await supabase.functions.invoke("notify-appointment-change", {
            body: {
              type: "change",
              client_name: niceName,
              client_phone: phone,
              service: form.treat || "appointment",
              provider: form.provider_name || "",
              date: form.date,
              time: form.time,
              old_date: _oldAppt.date,
              old_time: _oldAppt.time,
              location: form.loc || "",
              force: true,
            }
          });
          if (error) {
            let detail = error.message || "unknown";
            try { if (error.context?.json) { const j = await error.context.json(); if (j?.error) detail = j.error; } } catch {}
            _smsStatus = "FAIL: " + detail;
            console.error("[resched-sms]", error, data);
          } else if (data?.error) _smsStatus = "FAIL: " + data.error;
          else if (data?.skipped) _smsStatus = "SKIPPED: " + data.skipped;
          else _smsStatus = "ok";
        } catch (e) { _smsStatus = "FAIL: " + (e?.message || "exception"); }
      }
      if (email) {
        try {
          const { data, error } = await supabase.functions.invoke("send-appointment-email", {
            body: {
              type: "reschedule",
              to_email: email,
              to_name: niceName,
              service: form.treat || "appointment",
              provider: form.provider_name || "",
              date: form.date,
              time: form.time,
              old_date: _oldAppt.date,
              old_time: _oldAppt.time,
              location: form.loc || "",
            }
          });
          if (error) {
            let detail = error.message || "unknown";
            try { if (error.context?.json) { const j = await error.context.json(); if (j?.error) detail = j.error; } } catch {}
            _emailStatus = "FAIL: " + detail;
            console.error("[resched-email]", error, data);
          } else if (data?.error) _emailStatus = "FAIL: " + data.error;
          else _emailStatus = "ok";
        } catch (e) { _emailStatus = "FAIL: " + (e?.message || "exception"); }
      }
      setCommToast({ kind: "resched", client: niceName, phone, email, sms: _smsStatus, email_status: _emailStatus });
      setTimeout(() => setCommToast(null), 12000);
    }

    // ★ Auto-promote from waitlist if a reschedule freed up a slot. We send
    // the ORIGINAL appointment id — the function will fabricate a new appt
    // at the OLD date/time using the cancelled appt's details. Slot only
    // considered "freed" if date or time actually changed.
    if (_oldAppt && !error && (_oldAppt.date !== form.date || _oldAppt.time !== form.time)) {
      try {
        // We can't pass the cancelled appt directly — instead we synthesize a
        // virtual lookup using a fresh entry in waitlist_entries criteria.
        // The function reads `appointments` by id, so we tell it the OLD
        // (now-empty) slot info by re-using the appointment record before
        // setting it to the new date. Easiest: write a temp record? No —
        // just pass the appointment_id and have the function detect that
        // its current date/time differs from waitlist target and treat the
        // pre-reschedule slot as freed. For simplicity here, we send the
        // OLD date/time so it can match waitlist targets directly.
        const oldDate = _oldAppt.date;
        const oldTime = _oldAppt.time;
        const oldTreat = _oldAppt.treat;
        // Use waitlist_entries lookup client-side: find matching waiter
        const { data: matches } = await supabase.from("waitlist_entries")
          .select("id")
          .eq("status", "waiting")
          .eq("auto_promote", true)
          .eq("target_date", oldDate)
          .eq("target_time", oldTime)
          .ilike("treatment_name", oldTreat || "%")
          .order("created_at", { ascending: true })
          .limit(1);
        if (matches?.length) {
          const { data: promoteRes } = await supabase.functions.invoke("waitlist-promote", {
            body: { waitlist_id: matches[0].id, source: "auto_reschedule" }
          });
          if (promoteRes?.ok && promoteRes?.appointment) {
            setAppointments(as => [promoteRes.appointment, ...as.filter(a => a.id !== promoteRes.appointment.id)]);
            const _name = promoteRes.appointment.cn || "Waitlist client";
            alert(`✅ Auto-promoted ${_name} into the slot you just freed up.\n\nConfirmation SMS sent.`);
          }
        }
      } catch (e) { console.error("Reschedule waitlist promote failed:", e); }
    }

    setShowF(false);
    setEditAppt(null);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {/* Fee decision after a no-show / late cancel / late reschedule. */}
      {feePrompt && (
        <FeeDecisionModal appt={feePrompt.appt} feeType={feePrompt.feeType} onDone={()=>setFeePrompt(null)} />
      )}
      {/* Card on file — asked after every booking that doesn't have one. */}
      {cardPrompt && (
        <CardOnFileModal
          client={cardPrompt.client}
          appointment={cardPrompt.appointment}
          onDone={(res)=>{
            setCardPrompt(null);
            if (res?.saved) setClients(cs=>cs.map(c=>c.id===cardPrompt.client.id?{...c,card_on_file:true}:c));
          }}
        />
      )}
      {/* ★ Comm toast — visible feedback after booking / cancel / reschedule
         so staff sees if SMS + email actually went out. Auto-dismisses after
         12s, or click to close. */}
      {commToast && (
        <div onClick={()=>setCommToast(null)} style={{position:"fixed",top:16,right:16,zIndex:9999,background:"#fff",border:"2px solid "+((commToast.sms==="ok"||commToast.sms==="no_phone")&&(commToast.email_status==="ok"||commToast.email_status==="no_email")?C.green:C.red),borderRadius:12,padding:"14px 18px",boxShadow:"0 8px 24px rgba(0,0,0,0.18)",maxWidth:380,cursor:"pointer",fontFamily:F}}>
          <div style={{fontSize:13,fontWeight:800,color:C.text,marginBottom:6}}>
            {commToast.kind === "booking" ? "📅 Booking" : commToast.kind === "cancel" ? "🚫 Cancellation" : commToast.kind === "resched" ? "🔁 Reschedule" : "📨 Comm"} — {commToast.client || ""}
          </div>
          <div style={{fontSize:11,color:C.muted,marginBottom:4}}>
            <strong style={{color:commToast.sms==="ok"?C.green:commToast.sms==="no_phone"?C.muted:C.red}}>SMS:</strong>{" "}
            {commToast.sms === "ok" ? `✓ sent to ${commToast.phone}` : commToast.sms === "no_phone" ? "skipped (no phone)" : commToast.sms}
          </div>
          <div style={{fontSize:11,color:C.muted}}>
            <strong style={{color:commToast.email_status==="ok"?C.green:commToast.email_status==="no_email"?C.muted:C.red}}>Email:</strong>{" "}
            {commToast.email_status === "ok" ? `✓ sent to ${commToast.email}` : commToast.email_status === "no_email" ? "skipped (no email)" : commToast.email_status}
          </div>
          <div style={{fontSize:9,color:C.dim,marginTop:6,fontStyle:"italic"}}>Click to dismiss</div>
        </div>
      )}
      {/* Toolbar */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
        <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:9,border:`1px solid ${C.border}`,overflowX:"auto",maxWidth:"100%"}}>
          {/* Week is desktop-only — the dense 7-col grid doesn't fit on phones. */}
          {[["day","Day"],["week","Week"],["month","Month"],["list","List"],["provider","By Provider"]]
            .filter(([v])=>!(isMobile && v==="week"))
            .map(([v,l])=>(
            <button key={v} className="el-tap-target" onClick={()=>setView(v)} style={{padding:isMobile?"10px 14px":"6px 12px",fontSize:12,fontWeight:view===v?700:500,color:view===v?"#fff":C.muted,background:view===v?C.gold:"none",borderRadius:7,border:"none",cursor:"pointer",fontFamily:F,transition:"all 0.15s",whiteSpace:"nowrap"}}>{l}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
          {!isMobile&&["all","confirmed","completed","no-show"].map(f=>(
            <button key={f} onClick={()=>setFilter(f)} style={{padding:"5px 11px",fontSize:11,fontWeight:filter===f?700:500,color:filter===f?"#fff":C.muted,background:filter===f?C.gold:C.surface,border:`1px solid ${filter===f?C.gold:C.border}`,borderRadius:20,cursor:"pointer",fontFamily:F,textTransform:"capitalize"}}>{f}</button>
          ))}
          <button
            onClick={()=>setShowWaitlist(true)}
            style={{padding:"7px 13px",background:waitlistCount>0?`linear-gradient(135deg,${C.gold}22,${C.gold}11)`:C.surface,color:waitlistCount>0?C.gold:C.muted,border:`1px solid ${waitlistCount>0?C.borderGold:C.border}`,borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:5}}
            title="Open waitlist"
          >
            ⏳ Waitlist
            {waitlistCount > 0 && <span style={{background:C.gold,color:"#fff",fontSize:10,padding:"1px 7px",borderRadius:50,fontWeight:800,minWidth:18,textAlign:"center"}}>{waitlistCount}</span>}
          </button>
          <Btn icon="plus" onClick={()=>{setForm({treat:"",pid:"",date:currentDayStr,time:"10:00",dur:60,price:"",status:"confirmed",room:"",loc:"Nova Med Spa",dep:0,notes:"",checkin:"upcoming",tip:0,paid:false});setShowF(true);}}>New Appointment</Btn>
        </div>
      </div>

      {/* Day View */}
      {view==="day"&&(
        <Card>
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:C.goldBg,gap:8,flexWrap:"wrap"}}>
            <button className="el-tap-target" onClick={()=>setDayOffset(d=>d-1)} style={{padding:"10px 16px",background:C.gold,border:"none",borderRadius:8,cursor:"pointer",fontSize:16,color:"#fff",fontWeight:700,minWidth:44,minHeight:44}}>←</button>
            <div style={{fontSize:isMobile?13:15,fontWeight:700,color:C.gold,fontFamily:S,textAlign:"center",flex:1,minWidth:0}}>
              {currentDay.toLocaleDateString("en-US",{weekday:isMobile?"short":"long",month:"short",day:"numeric",year:isMobile?undefined:"numeric"})}
              {currentDayStr===TODAY&&<span style={{fontSize:11,marginLeft:8,background:C.gold,color:"#fff",padding:"2px 8px",borderRadius:10,fontWeight:600}}>Today</span>}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="el-tap-target" onClick={()=>setDayOffset(0)} style={{padding:"10px 14px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,cursor:"pointer",fontSize:11,color:C.muted,fontFamily:F,minHeight:44}}>Today</button>
              <button className="el-tap-target" onClick={()=>setDayOffset(d=>d+1)} style={{padding:"10px 16px",background:C.gold,border:"none",borderRadius:8,cursor:"pointer",fontSize:16,color:"#fff",fontWeight:700,minWidth:44,minHeight:44}}>→</button>
            </div>
          </div>
          {(() => {
            // Hide cancelled + no-show from the Day list — the user wants this
            // view to mirror the live calendar, not show "stale" rows.
            const dayActive = appointments.filter(a =>
              a.date === currentDayStr &&
              a.status !== "cancelled" &&
              a.status !== "no-show"
            );
            if (dayActive.length === 0) {
              return <div style={{padding:"40px",textAlign:"center",color:C.dim,fontSize:13}}>No appointments on this day</div>;
            }
            return dayActive.sort((a,b)=>(a.time||"").localeCompare(b.time||"")).map(a=>{
              const p = PROVIDERS.find(x=>x.id===a.pid);
              const isCheckedIn = a.checkin==="waiting"||a.checkin==="in-treatment"||a.checkin==="checked-out";
              return (
                <div key={a.id} onClick={()=>setSelAppt(a)} style={{display:"flex",gap:12,alignItems:"center",padding:"13px 16px",borderBottom:`1px solid ${C.border}`,cursor:"pointer",transition:"background 0.15s",background:isCheckedIn?"rgba(46,139,87,0.08)":"transparent",borderLeft:isCheckedIn?`4px solid ${C.green}`:"none"}} onMouseEnter={e=>e.currentTarget.style.background=isCheckedIn?"rgba(46,139,87,0.15)":C.light} onMouseLeave={e=>e.currentTarget.style.background=isCheckedIn?"rgba(46,139,87,0.08)":"transparent"}>
                  <div style={{width:72,textAlign:"center",flexShrink:0}}>
                    {(() => {
                      // ★ Show start → end so staff sees the actual window the
                      // room is blocked. "10:00 – 10:45" beats "10:00 · 45min".
                      const [h, m] = (a.time || "00:00").split(":").map(Number);
                      const endMin = h * 60 + m + (a.dur || 60);
                      const eh = Math.floor(endMin / 60), em = endMin % 60;
                      const endStr = format12Hour(`${String(eh).padStart(2,"0")}:${String(em).padStart(2,"0")}`);
                      return (
                        <>
                          <div style={{fontSize:14,fontWeight:800,color:C.gold,fontFamily:S,lineHeight:1.1}}>{format12Hour(a.time)}</div>
                          <div style={{fontSize:10,color:C.muted,fontWeight:600,marginTop:1}}>↓ {endStr}</div>
                          <div style={{fontSize:9,color:C.dim,marginTop:1}}>{a.dur}min</div>
                        </>
                      );
                    })()}
                  </div>
                  <div style={{width:3,height:40,borderRadius:2,background:p?.color||C.gold,flexShrink:0}}/>
                  {/* CLIENT avatar (not provider). Prefers the "front" face
                      photo from any past check-in. Falls back to first-last
                      initials (e.g. "SK") so the row never looks empty. */}
                  {(() => {
                    const photoUrl = clientPhotoMap.get(a.cid);
                    const accent = p?.color || C.gold;
                    if (photoUrl) {
                      return <img src={photoUrl} alt={a.cn} title={a.cn} style={{width:36,height:36,borderRadius:"50%",objectFit:"cover",border:"2px solid "+accent,flexShrink:0}}/>;
                    }
                    return (
                      <div title={a.cn} style={{width:36,height:36,borderRadius:"50%",background:accent+"22",border:"2px solid "+accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:accent,flexShrink:0}}>
                        {clientInitials(a.cn)}
                      </div>
                    );
                  })()}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:600,color:C.text,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>{a.cn}{newBadge(a.id)}{isCheckedIn&&<span style={{fontSize:11,color:C.green,background:C.greenL,padding:"2px 8px",borderRadius:10,fontWeight:700,border:`1px solid ${C.green}40`}}>✓ Checked In</span>}{a.customer_confirmed&&!isCheckedIn&&<span style={{fontSize:11,color:"#15803d",background:"#e6f9ec",padding:"2px 8px",borderRadius:10,fontWeight:700,border:"1px solid #22c55e"}}>✓ Confirmed</span>}</div>
                    <div style={{fontSize:12,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.treat} · {(a.prov||"").split(" ").slice(0,2).join(" ")} · {a.room}</div>
                    {/* ★ Phone + email — front desk needs this visible at a glance to call/text the client without opening the modal */}
                    {(() => {
                      const cc = clients.find(x => x.id === a.cid);
                      const phone = cc?.phone;
                      const email = cc?.email;
                      if (!phone && !email) return null;
                      return (
                        <div style={{fontSize:11,color:C.dim,marginTop:3,display:"flex",gap:10,flexWrap:"wrap"}}>
                          {phone && <a href={`tel:${phone.replace(/[^\d+]/g,"")}`} onClick={e=>e.stopPropagation()} style={{color:C.gold,fontWeight:600,textDecoration:"none"}}>📞 {phone}</a>}
                          {email && <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:240}}>📧 {email}</span>}
                        </div>
                      );
                    })()}
                  </div>
                  <div style={{fontSize:15,fontWeight:700,color:C.text,flexShrink:0}}>${a.price}</div>
                  <Bdg status={a.status}/>
                </div>
              );
            });
          })()}
        </Card>
      )}

      {/* Week View */}
      {view==="week"&&!isMobile&&(
        <Card style={{overflow:"auto"}}>
          <div style={{padding:"10px 14px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:C.goldBg}}>
            <button onClick={()=>setWeekOffset(w=>w-1)} style={{padding:"5px 12px",background:C.gold,border:"none",borderRadius:7,cursor:"pointer",fontSize:14,color:"#fff",fontWeight:700}}>←</button>
            <div style={{fontSize:13,fontWeight:700,color:C.gold}}>{days[0]} — {days[4]}</div>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>setWeekOffset(0)} style={{padding:"4px 10px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,cursor:"pointer",fontSize:11,color:C.muted,fontFamily:F}}>Today</button>
              <button onClick={()=>setWeekOffset(w=>w+1)} style={{padding:"5px 12px",background:C.gold,border:"none",borderRadius:7,cursor:"pointer",fontSize:14,color:"#fff",fontWeight:700}}>→</button>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:`70px repeat(${days.length},1fr)`,minWidth:650}}>
            <div style={{padding:"10px 6px",background:C.light,borderBottom:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`}}/>
            {days.map(d=>{
              const _blk = blockedFor(dmap[d]);
              return (
              <div key={d} title={_blk ? (_blk.closed ? `Closed${_blk.note?" — "+_blk.note:""}` : `Special hours ${(_blk.start_time||"").slice(0,5)}–${(_blk.end_time||"").slice(0,5)}${_blk.note?" — "+_blk.note:""}`) : ""}
                style={{padding:"10px 10px",background: _blk ? (_blk.closed?C.redL:C.blueL) : C.light,borderBottom:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,textAlign:"center"}}>
                <div style={{fontSize:10,fontWeight:700,color:dmap[d]===TODAY?C.gold:C.muted,letterSpacing:"0.08em"}}>{d.slice(0,3).toUpperCase()}</div>
                <div style={{fontSize:19,fontWeight:800,color:_blk?(_blk.closed?C.red:C.blue):(dmap[d]===TODAY?C.gold:C.text),fontFamily:S}}>{d.split(" ")[2]}</div>
                {_blk && <div style={{marginTop:2,fontSize:8,fontWeight:800,letterSpacing:"0.04em",color:_blk.closed?C.red:C.blue}}>{_blk.closed?"🚫 CLOSED":"🕑 SPECIAL"}</div>}
              </div>
            );})}
            {hours.map(hour=>(
              <>
                <div key={"h"+hour} style={{padding:"8px 6px",borderBottom:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,fontSize:10,color:C.dim,textAlign:"right",background:"#fafaf8"}}>{format12Hour(hour)}</div>
                {days.map(day=>{
                  const a = getSlot(day,hour);
                  const p = a?PROVIDERS.find(x=>x.id===a.pid):null;
                                    const cellDate = dmap[day];
                  const cellTime = hour.replace(/\s/g,"").replace("AM","").replace("PM","");
                  let hh = parseInt(cellTime);
                  if (hour.includes("PM") && hh !== 12) hh += 12;
                  if (hour.includes("AM") && hh === 12) hh = 0;
                  const time24 = String(hh).padStart(2,"0") + ":00";
                  return (
                    <div key={day+hour} onClick={!a ? ()=>{setForm({date:cellDate,time:time24,loc:"Nova Med Spa",dur:60});setEditAppt(null);setShowF(true);} : undefined} style={{minHeight:52,borderBottom:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,padding:3,background:dmap[day]===TODAY?"rgba(180,140,60,0.03)":"transparent",cursor:!a?"pointer":"default"}}>
                      {a&&(() => {
                        const isCheckedIn=a.checkin==="waiting"||a.checkin==="in-treatment"||a.checkin==="checked-out";
                        // ★ Show start → end time on every box. Staff needs to
                        //   see the actual window the room/provider is blocked.
                        const [h_, m_] = (a.time||"00:00").split(":").map(Number);
                        const endMin_ = h_*60+m_+(a.dur||60);
                        const endStr_ = format12Hour(`${String(Math.floor(endMin_/60)).padStart(2,"0")}:${String(endMin_%60).padStart(2,"0")}`);
                        return (
                        <div onClick={()=>setSelAppt(a)} style={{padding:"7px 9px",background:isCheckedIn?"rgba(46,139,87,0.15)":(p?.color||C.gold)+"18",border:`1px solid ${isCheckedIn?C.green+"60":(p?.color||C.gold)+"35"}`,borderRadius:7,borderLeft:`4px solid ${isCheckedIn?C.green:p?.color||C.gold}`,cursor:"pointer",lineHeight:1.35}}>
                          <div style={{fontSize:10,fontWeight:700,color:p?.color||C.gold,marginBottom:2,letterSpacing:"0.02em"}}>{format12Hour(a.time)} – {endStr_}</div>
                          <div style={{fontSize:13,fontWeight:700,color:C.text,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",display:"flex",alignItems:"center",gap:4,marginBottom:2}}>{(isCheckedIn||a.customer_confirmed)&&<span style={{color:C.green,fontSize:11}}>✓</span>}{a.cn}{newBadge(a.id, true)}</div>
                          <div style={{fontSize:11,color:C.muted,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",fontWeight:500}}>{a.treat}</div>
                        </div>
                      );})()}
                    </div>
                  );
                })}
              </>
            ))}
          </div>
        </Card>
      )}

      {/* Month View */}
      {view==="month"&&(
        <Card>
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:C.goldBg}}>
            <button onClick={()=>setMonthOffset(m=>m-1)} style={{padding:"5px 12px",background:C.gold,border:"none",borderRadius:7,cursor:"pointer",fontSize:14,color:"#fff",fontWeight:700}}>←</button>
            <div style={{fontSize:15,fontWeight:700,color:C.gold,fontFamily:S}}>{monthName}</div>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>setMonthOffset(0)} style={{padding:"4px 10px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,cursor:"pointer",fontSize:11,color:C.muted,fontFamily:F}}>Today</button>
              <button onClick={()=>setMonthOffset(m=>m+1)} style={{padding:"5px 12px",background:C.gold,border:"none",borderRadius:7,cursor:"pointer",fontSize:14,color:"#fff",fontWeight:700}}>→</button>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)"}}>
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d=>(
              <div key={d} style={{padding:"8px",textAlign:"center",fontSize:11,fontWeight:700,color:C.muted,borderBottom:`1px solid ${C.border}`,background:C.light}}>{d}</div>
            ))}
            {Array.from({length:(firstDay===0?6:firstDay-1)}).map((_,i)=>(
              <div key={"empty"+i} style={{minHeight:80,borderBottom:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,background:C.light}}/>
            ))}
            {Array.from({length:daysInMonth}).map((_,i)=>{
              const dayNum = i+1;
              const dateStr = `${monthYear}-${String(monthNum+1).padStart(2,"0")}-${String(dayNum).padStart(2,"0")}`;
              // Month view: drop cancelled + no-show so the squares match
              // what the front desk actually expects to happen that day.
              const dayAppts = appointments.filter(a=>a.date===dateStr&&a.status!=="cancelled"&&a.status!=="no-show");
              const isToday = dateStr===TODAY;
              const _blk = blockedFor(dateStr);
              return (
                <div key={dayNum} title={_blk ? (_blk.closed ? `Closed${_blk.note?" — "+_blk.note:""}` : `Special hours ${(_blk.start_time||"").slice(0,5)}–${(_blk.end_time||"").slice(0,5)}${_blk.note?" — "+_blk.note:""}`) : ""}
                  style={{minHeight:80,borderBottom:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,padding:6,background:_blk?(_blk.closed?C.redL:C.blueL):(isToday?"rgba(180,140,60,0.05)":"transparent")}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                    <div style={{fontSize:13,fontWeight:isToday?800:500,color:isToday?C.gold:C.text,width:24,height:24,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",background:isToday?C.gold+"15":"transparent"}}>{dayNum}</div>
                    {_blk && <span style={{fontSize:9,fontWeight:800,color:_blk.closed?C.red:C.blue}}>{_blk.closed?"🚫":"🕑"}</span>}
                  </div>
                  {dayAppts.slice(0,3).map(a=>{const isCheckedIn=a.checkin==="waiting"||a.checkin==="in-treatment"||a.checkin==="checked-out"; return (
                    <div key={a.id} onClick={()=>setSelAppt(a)} style={{fontSize:12,padding:"4px 7px",background:isCheckedIn?"rgba(46,139,87,0.15)":C.gold+"15",border:`1px solid ${isCheckedIn?C.green+"60":C.gold+"30"}`,borderRadius:5,marginBottom:3,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",cursor:"pointer",color:C.text,fontWeight:600}}>{(isCheckedIn||a.customer_confirmed)&&"✓ "}{format12Hour(a.time)} {a.cn?.split(" ")[0]}</div>
                  );})}
                  {dayAppts.length>3&&<div style={{fontSize:10,color:C.muted}}>+{dayAppts.length-3} more</div>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {view==="provider" && (
        <Card>
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:C.goldBg,gap:8,flexWrap:"wrap"}}>
            <button className="el-tap-target" onClick={()=>setDayOffset(d=>d-1)} style={{padding:"10px 16px",background:C.gold,border:"none",borderRadius:8,cursor:"pointer",fontSize:16,color:"#fff",fontWeight:700,minWidth:44,minHeight:44}}>←</button>
            <div style={{fontSize:isMobile?13:15,fontWeight:700,color:C.gold,fontFamily:S,textAlign:"center",flex:1,minWidth:0}}>
              {currentDay.toLocaleDateString("en-US",{weekday:isMobile?"short":"long",month:"short",day:"numeric",year:isMobile?undefined:"numeric"})}
              {currentDayStr===TODAY&&<span style={{fontSize:11,marginLeft:8,background:C.gold,color:"#fff",padding:"2px 8px",borderRadius:10,fontWeight:600}}>Today</span>}
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="el-tap-target" onClick={()=>setDayOffset(0)} style={{padding:"10px 14px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,cursor:"pointer",fontSize:11,color:C.muted,fontFamily:F,minHeight:44}}>Today</button>
              <button className="el-tap-target" onClick={()=>setDayOffset(d=>d+1)} style={{padding:"10px 16px",background:C.gold,border:"none",borderRadius:8,cursor:"pointer",fontSize:16,color:"#fff",fontWeight:700,minWidth:44,minHeight:44}}>→</button>
            </div>
          </div>
          <div style={{display:isCompact?"block":"flex",gap:0,width:"100%"}}>
          {/* Mini month calendar shown EVERYWHERE — desktop, tablet, and phone.
              Earlier we hid it on iPad/phone to save space, but the owner
              prefers having the same square day-picker UX on every device. */}
          <MiniCalendar currentDayStr={currentDayStr} setDayOffset={setDayOffset} appointments={appointments} TODAY={TODAY} />
          {/* Provider focus pills — on iPad/phone the day-view grid is too narrow
              to comfortably show every provider as a column. Let the user pick
              one to focus on (or All). Hidden on desktop where the grid fits.
              The pill list itself follows the day-of-week / has-appts rule too,
              so Sunday-off providers don't appear here either. */}
          {(() => {
            if (!isCompact) return null;
            const dow = currentDay.getDay();
            const workingIds = new Set(
              providerWeeklyHours
                .filter(h => Number(h.day_of_week) === dow && h.available !== false)
                .map(h => h.provider_id)
            );
            // Extra-day providers (guest schedule, e.g. Lisbell) are on duty too.
            providerExtraDays.filter(x => x.date === currentDayStr).forEach(x => workingIds.add(x.provider_id));
            const appointedIds = new Set(
              appointments
                .filter(a => a.date === currentDayStr && a.status !== "cancelled" && a.status !== "no-show" && a.pid)
                .map(a => a.pid)
            );
            const onDuty = PROVIDERS.filter(p => workingIds.has(p.id) || appointedIds.has(p.id));
            // ★ Same ghost-column treatment as the grid below — include
            //   inactive/legacy providers that still have appointments on
            //   this day, so the pill row matches what the grid shows.
            const liveIds = new Set(PROVIDERS.map(p => p.id));
            const ghostPills = [];
            const palette = ["#7c3aed","#dc2626","#0891b2","#65a30d","#ea580c","#db2777"];
            let ghostIdx = 0;
            for (const pid of appointedIds) {
              if (liveIds.has(pid)) continue;
              const firstAppt = appointments.find(a => a.date === currentDayStr && a.pid === pid);
              const name = firstAppt?.prov || "Unknown";
              const color = palette[ghostIdx++ % palette.length];
              ghostPills.push({ id: pid, name, role: "Inactive", color, photo_url: null, ini: name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase(), _ghost: true });
            }
            const pillList = (onDuty.length + ghostPills.length) > 0 ? [...onDuty, ...ghostPills] : PROVIDERS;
            if (pillList.length <= 1) return null;
            return (
            <div style={{padding:"10px 12px",borderBottom:`1px solid ${C.border}`,background:"#fafaf8",overflowX:"auto",whiteSpace:"nowrap"}}>
              <div style={{display:"inline-flex",gap:6,alignItems:"center"}}>
                <button
                  onClick={()=>setProviderFocusId(null)}
                  className="el-tap-target"
                  style={{
                    padding:"8px 14px",
                    fontSize:12,fontWeight:providerFocusId===null?800:600,
                    color:providerFocusId===null?"#fff":C.muted,
                    background:providerFocusId===null?C.gold:"#fff",
                    border:`1px solid ${providerFocusId===null?C.gold:C.border}`,
                    borderRadius:99,cursor:"pointer",fontFamily:F,
                    whiteSpace:"nowrap",minHeight:40,
                  }}>All on duty</button>
                {pillList.map(p => {
                  const active = providerFocusId===p.id;
                  return (
                    <button key={p.id} onClick={()=>setProviderFocusId(p.id)} className="el-tap-target"
                      style={{
                        padding:"8px 12px 8px 8px",
                        fontSize:12,fontWeight:active?800:600,
                        color:active?"#fff":C.text,
                        background:active?p.color:"#fff",
                        border:`1px solid ${active?p.color:C.border}`,
                        borderRadius:99,cursor:"pointer",fontFamily:F,
                        display:"inline-flex",alignItems:"center",gap:8,whiteSpace:"nowrap",minHeight:40,
                      }}>
                      {p.photo_url ? (
                        <img src={p.photo_url} alt={p.name} style={{width:24,height:24,borderRadius:"50%",objectFit:"cover",border:`1.5px solid ${active?"#fff":p.color}`}}/>
                      ) : (
                        <span style={{width:24,height:24,borderRadius:"50%",background:active?"rgba(255,255,255,0.25)":p.color+"25",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:active?"#fff":p.color}}>{p.ini}</span>
                      )}
                      <span>{p.name.split(" ")[0]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            );
          })()}
          <div style={{overflow:"auto",flex:1,minWidth:0}}>
            {(() => {
              // Compute which providers should appear as columns TODAY.
              //
              // Rule (per owner): only show a provider's column if she's
              // actually on the schedule that day, OR she already has at
              // least one appointment booked that day. That collapses the
              // 5-column grid down to 1-3 columns on most days and removes
              // the empty-Sunday-style clutter completely.
              const dow = currentDay.getDay(); // 0=Sun..6=Sat
              const workingIds = new Set(
                providerWeeklyHours
                  .filter(h => Number(h.day_of_week) === dow && h.available !== false)
                  .map(h => h.provider_id)
              );
              // Extra-day providers (guest schedule, e.g. Lisbell) get a column
              // on the dates they were made available.
              providerExtraDays.filter(x => x.date === currentDayStr).forEach(x => workingIds.add(x.provider_id));
              const dayAppointments = appointments.filter(a =>
                a.date === currentDayStr && a.status !== "cancelled" && a.status !== "no-show" && a.pid
              );
              const appointedIds = new Set(dayAppointments.map(a => a.pid));
              let providersOnDuty = PROVIDERS.filter(p => workingIds.has(p.id) || appointedIds.has(p.id));

              // ★ GHOST PROVIDER COLUMNS — if an appointment is booked under a
              //   provider id that's NOT in our active PROVIDERS list (e.g. a
              //   legacy/inactive duplicate row from an old import), we still
              //   need to render a column for it so the appointment is visible.
              //   Build a synthetic provider from the appointment's `prov`
              //   name field so the grid never silently drops bookings.
              const liveIds = new Set(PROVIDERS.map(p => p.id));
              const orphanIds = [...appointedIds].filter(id => !liveIds.has(id));
              if (orphanIds.length) {
                const palette = ["#7c3aed","#dc2626","#0891b2","#65a30d","#ea580c","#db2777"];
                let idx = 0;
                for (const pid of orphanIds) {
                  const firstAppt = dayAppointments.find(a => a.pid === pid);
                  const name = firstAppt?.prov || "Unknown Provider";
                  const color = palette[idx++ % palette.length];
                  providersOnDuty.push({
                    id: pid,
                    name,
                    role: "Inactive",
                    color,
                    photo_url: null,
                    ini: name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(),
                    _ghost: true,
                  });
                }
              }

              // Safety net — if for some reason weekly_hours isn't loaded yet
              // and no appointments exist, fall back to all providers so the
              // grid isn't blank.
              if (providersOnDuty.length === 0) providersOnDuty = PROVIDERS;

              // If the user picked a specific provider on iPad, honour that —
              // but only if that provider is actually on duty (otherwise show
              // her column anyway since she's been explicitly selected).
              const visibleProviders = (isCompact && providerFocusId)
                ? PROVIDERS.filter(p => p.id === providerFocusId)
                : providersOnDuty;
              const colCount = visibleProviders.length;
              const compactColWidth = colCount === 1 ? "1fr" : "150px";
              return (
            <div style={{display:"grid",gridTemplateColumns:isCompact?`60px repeat(${colCount}, ${compactColWidth})`:`90px repeat(${colCount}, minmax(220px, 1fr))`,minWidth:isCompact?(colCount===1?"100%":60+colCount*150):90+colCount*220,width:"100%"}}>
              <div style={{padding:"12px 8px",background:C.light,borderBottom:`2px solid ${C.border}`,borderRight:`1px solid ${C.border}`,position:"sticky",left:0,zIndex:2}}></div>
              {visibleProviders.map(p => (
                <div key={p.id} style={{padding:"10px 8px",background:C.light,borderBottom:`2px solid ${C.border}`,borderRight:`1px solid ${C.border}`,textAlign:"center",borderTop:`3px solid ${p.color}`}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginBottom:4}}>
                    {p.photo_url ? (
                      <img src={p.photo_url} alt={p.name} style={{width:36,height:36,borderRadius:"50%",objectFit:"cover",border:`2px solid ${p.color}`,boxShadow:`0 2px 6px ${p.color}40`}}/>
                    ) : (
                      <div style={{width:36,height:36,borderRadius:"50%",background:p.color+"25",border:`2px solid ${p.color}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:p.color}}>{p.ini}</div>
                    )}
                  </div>
                  <div style={{fontSize:12,fontWeight:700,color:C.text,lineHeight:1.2}}>{p.name.split(" ").slice(0,2).join(" ")}</div>
                  <div style={{fontSize:10,color:C.muted,marginTop:2}}>{p.role}</div>
                </div>
              ))}
              {["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00"].map(hour => (
                <React.Fragment key={hour}>
                  <div style={{padding:"8px 6px",height:80,borderBottom:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,fontSize:11,color:C.muted,textAlign:"right",background:"#fafaf8",fontWeight:600,position:"sticky",left:0,zIndex:1,boxSizing:"border-box"}}>{format12Hour(hour)}</div>
                  {visibleProviders.map(p => {
                    const [h,m] = hour.split(":").map(Number);
                    const hourStart = h*60+m;
                    const hourEnd = hourStart+60;
                    // All appts for this provider on this day, with start/end minute marks.
                    const provAppts = appointments
                      .filter(a => a.date === currentDayStr && a.pid === p.id && a.status !== "cancelled" && a.status !== "no-show")
                      .map(a => {
                        const [aH,aM] = a.time.split(":").map(Number);
                        const aStart = aH*60+aM;
                        return { ...a, _start: aStart, _end: aStart + (a.dur || 60) };
                      })
                      .sort((x, y) => x._start - y._start || (x.id||0) - (y.id||0));
                    const slotAppts = provAppts.filter(a => a._start >= hourStart && a._start < hourEnd);
                    const quickBook = () => {
                      if(slotAppts.length>0) return;
                      setForm({treat:"",pid:p.id,prov:p.name,date:currentDayStr,time:hour,dur:60,price:"",status:"confirmed",room:"",loc:"Nova Med Spa",dep:0,notes:"",checkin:"upcoming",tip:0,paid:false});
                      setShowF(true);
                    };
                    // ★ Hourly row height — TALL on mobile so every box, even
                    //   a 10-min touch-up, shows time + name + treatment + dur.
                    //   Owner asked specifically that "the Botox at 1pm" still
                    //   reads as Botox — not just a sliver. 160px/hour is the
                    //   sweet spot: 10-min boxes get 27px (≈ 1 line, readable
                    //   with ellipsis), 30-min get 80px (room for 2 lines).
                    //   Desktop stays at 80px since wider columns afford it.
                    const ROW_HEIGHT = isCompact ? 160 : 80;
                    const PX_PER_MIN = ROW_HEIGHT / 60;
                    return (
                      <div key={hour+p.id} onClick={quickBook} style={{height:ROW_HEIGHT,position:"relative",borderBottom:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,background:currentDayStr===TODAY?"rgba(180,140,60,0.02)":"transparent",cursor:slotAppts.length===0?"pointer":"default",transition:"background 0.15s",overflow:"visible"}} onMouseEnter={e=>{if(slotAppts.length===0)e.currentTarget.style.background=p.color+"15";}} onMouseLeave={e=>{e.currentTarget.style.background=currentDayStr===TODAY?"rgba(180,140,60,0.02)":"transparent";}}>
                        {slotAppts.map(a => {
                          const isCheckedIn = a.checkin==="waiting"||a.checkin==="in-treatment"||a.checkin==="checked-out";
                          const offsetMin = a._start - hourStart;
                          const durMin = a.dur || 60;
                          const top = Math.round(offsetMin * PX_PER_MIN);
                          // ★ Height = natural (durMin * 1.33). For boxes < 25 min
                          //   we allow up to 12px of visual overflow into a
                          //   following GAP — but never into another appointment
                          //   (that's what caused the overlap-mess the owner
                          //   reported on 2026-06-10).
                          const naturalH = Math.round(durMin * PX_PER_MIN) - 2;
                          const nextAppt = provAppts.find(b => b._start >= a._end);
                          const gapToNextPx = nextAppt
                            ? Math.max(0, (nextAppt._start - a._end) * PX_PER_MIN - 2)
                            : Infinity;
                          const bonusH = Math.min(12, gapToNextPx); // overflow into free gap only
                          const height = Math.max(naturalH + bonusH, 22);
                          const cluster = provAppts.filter(b => b._start < a._end && b._end > a._start);
                          const cols = cluster.length;
                          const col = cluster.findIndex(o => o.id === a.id);
                          const widthPct = 100 / cols;
                          const leftPct = col * widthPct;
                          const isShared = cols > 1;
                          const isShort = durMin <= 20 || (isShared && durMin <= 30);
                          const isMedium = durMin <= 35;
                          return (
                            <div key={a.id}
                              onClick={(e)=>{e.stopPropagation();setSelAppt(a);}}
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setHoverPreview({ appt: a, x: rect.right + 8, y: rect.top });
                              }}
                              onMouseLeave={() => setHoverPreview(null)}
                              style={{
                              position:"absolute",
                              top: top+1,
                              left: `calc(${leftPct}% + 2px)`,
                              width: `calc(${widthPct}% - 4px)`,
                              height,
                              padding: isShort?"2px 6px":isMedium?"4px 7px":"6px 8px",
                              // ★ Box color rules (staff request — easier to spot at a glance than a tiny ✓):
                              //   • Checked-in client      → BLUE box (active visit)
                              //   • SMS-confirmed but not yet checked-in → GREEN box (will arrive)
                              //   • Default                → provider color
                              background: isCheckedIn ? "rgba(37,99,235,0.18)" : a.customer_confirmed ? "rgba(46,139,87,0.15)" : p.color+"20",
                              border: `1px solid ${(isCheckedIn ? C.blue : a.customer_confirmed ? C.green : p.color)}50`,
                              borderLeft: `4px solid ${isCheckedIn ? C.blue : a.customer_confirmed ? C.green : p.color}`,
                              borderRadius: 7,
                              cursor: "pointer",
                              lineHeight: 1.2,
                              overflow: "hidden",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                              boxSizing: "border-box",
                              zIndex: 1,
                            }}>
                              {(() => {
                                // ★ Compute end time for every box — owner wants this on EVERY size variant.
                                const endStr = format12Hour(`${String(Math.floor(a._end/60)).padStart(2,"0")}:${String(a._end%60).padStart(2,"0")}`);
                                if (isShort) {
                                  // ★ Compact 2-line layout — even a 10-min slot
                                  //   now has ≈27px which fits two tiny rows.
                                  //   Row 1: time + name + (new badge) + dur
                                  //   Row 2: treatment (truncated if needed)
                                  //   AM/PM stripped — column header conveys it.
                                  //   "EXISTING" chip suppressed (default state).
                                  const isNew = newPatientApptIds.has(a.id);
                                  const shortTime = format12Hour(a.time).replace(/\s?(AM|PM)$/i, "");
                                  return (
                                    <div style={{display:"flex",flexDirection:"column",gap:0,fontSize:11,overflow:"hidden",height:"100%",justifyContent:"center"}}>
                                      <div style={{display:"flex",alignItems:"center",gap:5,whiteSpace:"nowrap",overflow:"hidden",lineHeight:1.15}}>
                                        <span style={{color:isCheckedIn?C.blue:p.color,fontWeight:800,letterSpacing:"-0.01em",fontSize:11,flexShrink:0}}>{shortTime}</span>
                                        <span style={{fontSize:12,fontWeight:800,color:C.text,overflow:"hidden",textOverflow:"ellipsis",minWidth:0,flex:"1 1 auto"}}>{a.cn}</span>
                                        {isNew && newBadge(a.id, true)}
                                        <span style={{fontSize:9,color:C.muted,fontWeight:600,flexShrink:0}}>{durMin}m</span>
                                      </div>
                                      {a.treat && (
                                        <div style={{fontSize:10,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.15,marginTop:1,fontWeight:500}}>
                                          {a.treat}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                if (isMedium) {
                                  return (
                                    <>
                                      <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:1}}>
                                        <span style={{fontSize:11,color:isCheckedIn?C.blue:p.color,fontWeight:800,letterSpacing:"-0.01em"}}>{format12Hour(a.time)} – {endStr}</span>
                                        {/* SMS confirmation chip — only when NOT yet checked in. Once checked in, the whole box turns blue and the chip would be redundant. */}
                                        {a.customer_confirmed && !isCheckedIn && <span title="Client confirmed via SMS" style={{fontSize:9,color:C.green,fontWeight:800,padding:"1px 5px",background:"rgba(46,139,87,0.14)",borderRadius:5}}>✓ CONFIRMED</span>}
                                        {newBadge(a.id, true)}
                                        <span style={{fontSize:9,color:C.muted,fontWeight:600,marginLeft:"auto"}}>{durMin}m</span>
                                      </div>
                                      <div style={{fontSize:12,fontWeight:700,color:C.text,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>{a.cn} · {a.treat}</div>
                                    </>
                                  );
                                }
                                return (
                                  <>
                                    <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:2,flexWrap:"wrap"}}>
                                      <span style={{fontSize:12,color:isCheckedIn?C.blue:p.color,fontWeight:800,letterSpacing:"-0.01em"}}>{format12Hour(a.time)} – {endStr}</span>
                                      {/* SMS confirmation chip — only when NOT yet checked in (after check-in, blue box already signals it) */}
                                      {a.customer_confirmed && !isCheckedIn && <span title="Client confirmed via SMS" style={{fontSize:9,color:C.green,fontWeight:800,padding:"2px 6px",background:"rgba(46,139,87,0.14)",borderRadius:5,letterSpacing:"0.04em"}}>✓ CONFIRMED</span>}
                                      {newBadge(a.id, true)}
                                    </div>
                                    <div style={{fontSize:13,fontWeight:700,color:C.text,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",marginBottom:1}}>{a.cn}</div>
                                    <div style={{fontSize:11,color:C.muted,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",fontWeight:500}}>{a.treat}</div>
                                    {height >= 70 && <div style={{fontSize:10,color:C.muted,marginTop:1,fontWeight:500}}>{durMin}min · ${a.price}</div>}
                                  </>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
              );
            })()}
})()}
          </div>
                  </div>
</Card>
      )}

      {/* List View */}
      {view==="list"&&(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.map(a=>{const p=PROVIDERS.find(x=>x.id===a.pid);return(
            <Card key={a.id} style={{padding:"13px 16px",cursor:"pointer"}} onClick={()=>setSelAppt(a)}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:isMobile?"wrap":"nowrap",gap:10}}>
                <div style={{display:"flex",gap:12,alignItems:"center"}}>
                  <div style={{textAlign:"center",flexShrink:0}}>
                    <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S}}>{a.date.split("-")[2]}</div>
                    <div style={{fontSize:10,color:C.muted}}>{fmtAppointmentDate(a.date,{month:"short"})}</div>
                    <div style={{fontSize:11,fontWeight:700,color:C.text}}>{format12Hour(a.time)}</div>
                  </div>
                  <div style={{width:3,height:40,borderRadius:2,background:p?.color||C.gold,flexShrink:0}}/>
                  {p?.photo_url ? (
                    <img src={p.photo_url} alt={p.name} title={p.name} style={{width:36,height:36,borderRadius:"50%",objectFit:"cover",border:"2px solid "+(p.color||C.gold),flexShrink:0}}/>
                  ) : (
                    <div title={p?.name||a.prov} style={{width:36,height:36,borderRadius:"50%",background:(p?.color||C.gold)+"22",border:"2px solid "+(p?.color||C.gold),display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:p?.color||C.gold,flexShrink:0}}>{p?.ini || (a.prov||"").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}</div>
                  )}
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:2}}>{a.cn}</div>
                    <div style={{fontSize:12,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.treat} · {(a.prov||"").split(" ").slice(0,2).join(" ")} · {a.dur}min</div>
                  </div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                  <div style={{fontSize:15,fontWeight:700,color:C.text}}>${a.price}</div>
                  <Bdg status={a.status}/>
                </div>
              </div>
            </Card>
          );})}
          {filtered.length===0&&<div style={{textAlign:"center",padding:"40px",color:C.dim,fontSize:13,background:C.light,borderRadius:12}}>No appointments found</div>}
        </div>
      )}

      {/* Appointment Detail Modal */}
      {selAppt&&<ApptModal appt={selAppt} onClose={()=>setSelAppt(null)} onUpdate={updateApptStatus} onEdit={handleEdit} onCheckIn={openCheckIn} onRecordTreatment={(a)=>setRecordingAppt(a)} onOpenClient={(cid)=>{const apptDate=selAppt?.date;setSelAppt(null);openClientProfile&&openClientProfile(cid,{tab:"appointments",date:apptDate});}} isNewPatient={newPatientApptIds.has(selAppt.id)}/>}

      {/* Hover preview tooltip — desktop only (touch devices fire a sticky hover
          that would block the click). Shows the same essentials AR's calendar
          shows on hover: client, contact, time, service, status, prep flags. */}
      {hoverPreview && !isMobile && (() => {
        const a = hoverPreview.appt;
        const client = clients.find(c => c.id === a.cid);
        const isCheckedIn = a.checkin === "waiting" || a.checkin === "in-treatment" || a.checkin === "checked-out";
        const endMin = (() => {
          const [h, m] = (a.time || "00:00").split(":").map(Number);
          const total = h * 60 + m + (a.dur || 60);
          const eh = Math.floor(total / 60), em = total % 60;
          return format12Hour(`${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`);
        })();
        const statusLabel = a.status === "completed" ? "✅ Completed"
          : a.status === "cancelled" ? "🚫 Cancelled"
          : a.status === "no-show" ? "👻 No-Show"
          : isCheckedIn ? "🟢 Checked In"
          : a.customer_confirmed ? "✅ Confirmed by Client"
          : "⏳ Confirmed";
        const left = Math.min(hoverPreview.x, window.innerWidth - 290);
        const top = Math.min(hoverPreview.y, window.innerHeight - 260);
        return (
          <div style={{
            position: "fixed",
            left, top,
            zIndex: 9999,
            background: "rgba(26,26,46,0.96)",
            color: "#fff",
            padding: "12px 14px",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
            fontSize: 12,
            fontFamily: F,
            maxWidth: 280,
            pointerEvents: "none",
            lineHeight: 1.55,
          }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.gold, marginBottom: 4 }}>{a.cn}</div>
            {client?.email && <div style={{ opacity: 0.85 }}>📧 {client.email}</div>}
            {client?.phone && <div style={{ opacity: 0.85 }}>📞 {client.phone}</div>}
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.15)" }}>
              <div style={{ fontWeight: 700, color: "#fff", marginBottom: 2 }}>{format12Hour(a.time)} – {endMin}</div>
              <div style={{ opacity: 0.9 }}>{a.treat}{a.price ? ` — $${a.price}` : ""}</div>
              {a.prov && <div style={{ opacity: 0.75, fontSize: 11 }}>with {a.prov}</div>}
            </div>
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.15)", display: "flex", flexDirection: "column", gap: 3 }}>
              <div>{statusLabel}</div>
              {a.dep > 0 && <div style={{ color: "#a3e8c4" }}>💰 Deposit: ${a.dep}</div>}
              {a.notes && <div style={{ opacity: 0.8, fontStyle: "italic", marginTop: 4 }}>📝 {a.notes.length > 80 ? a.notes.slice(0, 80) + "..." : a.notes}</div>}
            </div>
          </div>
        );
      })()}
      {recordingAppt && (() => {
        const c = clients.find(cl => cl.id === recordingAppt.cid) || {id: recordingAppt.cid, name: recordingAppt.cn};
        return <TreatmentRecord appointment={recordingAppt} client={c} onClose={()=>setRecordingAppt(null)} onSaved={async ()=>{
          const { data } = await supabase.from("appointments").select("*").eq("id", recordingAppt.id).single();
          if (data) {
            setRecordingAppt(data);
            setAppointments(as => as.map(a => a.id === data.id ? data : a));
          }
        }}/>;
      })()}

      {/* Waitlist Drawer */}
      <WaitlistDrawer
        open={showWaitlist}
        onClose={()=>setShowWaitlist(false)}
        services={services}
        providers={PROVIDERS}
        onPromoted={(appt) => {
          // Refresh appointments so the new promoted booking shows up on the calendar
          setAppointments(as => [appt, ...as.filter(a => a.id !== appt.id)]);
        }}
      />

      {/* Consent Signing Modal */}
      {consentModal && (
        <Modal title="Patient Check-In" onClose={()=>setConsentModal(null)} w={780}>
          <CheckInFlow client={consentModal.client} appointment={consentModal.appt} onComplete={completeCheckIn} onCancel={()=>setConsentModal(null)} />
        </Modal>
      )}

      {/* New Appointment Modal */}


      {showF&&(
        <Modal title={editAppt?"Edit Appointment":"New Appointment"} onClose={()=>{setShowF(false);}} w={640}>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
            {!showNewClient && (
              <Fld label="Client" required col="1/-1"><div style={{display:"flex",gap:8}}>
                <SearchSelect value={form.cid} onChange={(v) => {
                  const selected = clients.find(x => x.id === +v);
                  // Auto-enforce a $25 deposit when the chosen client has any
                  // prior no-shows. The form already has a manual Deposit
                  // field, so staff can raise the number but the minimum is
                  // pre-filled and the row above is highlighted.
                  const needsDeposit = (Number(selected?.noshow) || 0) > 0;
                  setForm({ ...form, cid: +v, dep: needsDeposit ? Math.max(25, +form.dep || 0) : (+form.dep || 0) });
                }} options={clients} placeholder="Search client by name..." getKey={(c) => c.id} getLabel={(c) => c.name + (c.phone ? " · " + c.phone : "") + (c.email ? " · " + c.email : "")} style={{flex:1}} />
                <button type="button" onClick={()=>setShowNewClient(true)} style={{padding:"10px 14px",background:"linear-gradient(135deg,#b48c3c,#8a6f2e)",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>+ New Client</button>
              </div>
              {/* No-show warning banner — shows when the selected client
                  has at least one no-show on record. Forces staff to take
                  the $25 deposit before this booking can be saved. */}
              {(() => {
                const sel = clients.find(x => x.id === form.cid);
                const ns = Number(sel?.noshow) || 0;
                if (ns === 0) return null;
                const waived = !!form.deposit_waived;
                return (
                  <div style={{marginTop:8,padding:"10px 14px",background:waived?"rgba(46,139,87,0.10)":"rgba(217,119,6,0.10)",border:"1px solid "+(waived?"rgba(46,139,87,0.35)":"rgba(217,119,6,0.35)"),borderRadius:8,fontSize:12,color:waived?"#1e6b3f":"#92400e"}}>
                    {waived ? (
                      <>✅ Deposit <strong>waived</strong> by staff for <strong>{sel.name}</strong> (has {ns} no-show{ns>1?"s":""} on record).</>
                    ) : (
                      <>⚠️ <strong>{sel.name}</strong> has {ns} no-show{ns>1?"s":""} on record. A minimum <strong>$25 deposit</strong> is required.</>
                    )}
                    <label style={{display:"flex",alignItems:"center",gap:8,marginTop:8,cursor:"pointer",userSelect:"none"}}>
                      <input
                        type="checkbox"
                        checked={waived}
                        onChange={(e) => {
                          const w = e.target.checked;
                          // When waived, drop deposit to 0; when un-waived, restore the $25 minimum.
                          setForm({...form, deposit_waived: w, dep: w ? 0 : Math.max(25, +form.dep || 0)});
                        }}
                        style={{width:16,height:16,cursor:"pointer"}}
                      />
                      <span style={{fontSize:11,fontWeight:600}}>Waive deposit for this booking (CRM only — public booking always charges)</span>
                    </label>
                  </div>
                );
              })()}
              {/* ★ CARD ON FILE — required to book (policy 2026-09-01).
                  Shows the client's card status, texts them the secure card
                  link in one click, and lets staff waive with an audit note. */}
              {form.cid ? (() => {
                const sel = clients.find(x => x.id === form.cid);
                if (!sel) return null;
                const hasCard = !!(sel.card_on_file || sel.cardOnFile);
                const waived = !!form.card_requirement_waived;
                const sendLink = async () => {
                  if (!sel.phone) { alert("This client has no phone on file — add one first."); return; }
                  const first = (sel.name || "there").trim().split(/\s+/)[0];
                  const link = `https://www.elyonhq.com/card?cid=${sel.id}&n=${encodeURIComponent(first)}`;
                  const msg = `Hi ${first}, it's Nova Med Spa 💛 To hold your appointment, please save a card on file — you won't be charged today. Free cancellation up to 24 hours before your visit; changes within 24h are $25 (credited to your treatment); no-shows $50. Secure link: ${link}`;
                  if (!window.confirm(`Text the card link to ${sel.name} (${sel.phone})?`)) return;
                  const { data, error } = await supabase.functions.invoke("send-sms", { body: { to: sel.phone, message: msg, sender: "system", source: "card_guarantee" } });
                  if (error || data?.ok === false) alert("SMS failed: " + (error?.message || data?.error || data?.skipped || "unknown"));
                  else alert("Card link sent ✓ — they can save it in under a minute.");
                };
                return (
                  <div style={{marginTop:8,padding:"10px 14px",borderRadius:8,fontSize:12,
                    background: hasCard ? "rgba(46,139,87,0.10)" : waived ? C.light : "rgba(192,57,43,0.08)",
                    border: "1px solid " + (hasCard ? "rgba(46,139,87,0.35)" : waived ? C.border : "rgba(192,57,43,0.30)"),
                    color: hasCard ? "#1e6b3f" : waived ? C.muted : "#8c2f22"}}>
                    {hasCard ? (
                      <>💳 <strong>Card on file ✓</strong> — appointment is covered by the cancellation policy.</>
                    ) : (
                      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                        <span>💳 <strong>No card on file.</strong> You'll be asked to take it right after saving.</span>
                        <button type="button" onClick={()=>setCardPrompt({ client: sel, appointment: { ...form, treat: form.treat, date: form.date, time: form.time } })} style={{padding:"5px 12px",background:"linear-gradient(135deg,#b48c3c,#8a6f2e)",color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>💳 Take card now</button>
                        <button type="button" onClick={sendLink} style={{padding:"5px 12px",background:"rgba(180,140,60,0.10)",color:"#8a6f2e",border:"1px solid rgba(180,140,60,0.35)",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>📲 Text link</button>
                      </div>
                    )}
                    {!hasCard && (
                      <label style={{display:"flex",alignItems:"center",gap:8,marginTop:8,cursor:"pointer",userSelect:"none"}}>
                        <input type="checkbox" checked={waived} onChange={e=>setForm({...form, card_requirement_waived: e.target.checked})} style={{width:16,height:16,cursor:"pointer"}}/>
                        <span style={{fontSize:11,fontWeight:600}}>Book without a card this time (waive — recorded on the appointment)</span>
                      </label>
                    )}
                  </div>
                );
              })() : null}
              </Fld>
            )}
      {showNewClient && (
              <div style={{gridColumn:"1/-1",padding:14,background:"rgba(180,140,60,0.06)",border:"1px solid rgba(180,140,60,0.25)",borderRadius:10,marginBottom:4}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{fontSize:13,fontWeight:800,color:"#b48c3c",fontFamily:S}}>👤 New Client Details</div>
                  <button type="button" onClick={()=>{setShowNewClient(false);setNewClient({name:"",phone:"",email:"",dob:"",loc:"Nova Med Spa"});}} style={{padding:"5px 10px",background:"#f3f4f6",color:"#6b7280",border:"none",borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:F}}>Cancel, use existing</button>
                </div>
                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10}}>
                  <Fld label="Full Name" required col="1/-1"><input value={newClient.name} onChange={e=>setNewClient({...newClient,name:e.target.value})} placeholder="Jane Smith" style={inp}/></Fld>
                  <Fld label="Phone" required><input type="tel" inputMode="tel" value={newClient.phone} onChange={e=>setNewClient({...newClient,phone:e.target.value})} placeholder="(555) 123-4567" style={inp}/></Fld>
                  <Fld label="Email" required><input type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" value={newClient.email} onChange={e=>setNewClient({...newClient,email:e.target.value})} placeholder="jane@email.com" style={inp}/></Fld>
                  <Fld label="DOB (optional)" col="1/-1"><input type="date" value={newClient.dob} onChange={e=>setNewClient({...newClient,dob:e.target.value})} style={inp}/></Fld>
                </div>
              </div>
            )}
            {/* Multi-treatment picker — clients often book 2-3 services on
                one visit (e.g. "Botox + Lip Filler + IV Drip"). Treatments
                are stored in form.treat joined by " + "; quote/duration sum
                across all selected services. Empty-string slot = picker for
                a not-yet-chosen service. */}
            <Fld label="Treatments" required col="1/-1">
              {(() => {
                // Keep empty slots in the list so "+ Add another" actually
                // renders a new row. We only collapse empties when saving
                // form.treat to the DB (via join after filtering).
                // ★ Some service NAMES contain " + " (e.g. "Lymphatic Massage +
                //   Wood Therapy", "Health + Wellness Membership"). A naive
                //   split(" + ") would shred those into pieces that match no
                //   service, so the pick vanished. Parse greedily instead:
                //   match the LONGEST run of tokens that is a real service name.
                const _svcNames = new Set((services || []).map(s => s.name));
                const _parseTreats = (str) => {
                  if (!str) return [""];
                  const toks = str.split(" + ");
                  const out = [];
                  let i = 0;
                  while (i < toks.length) {
                    let matched = null, span = 1, cand = toks[i];
                    for (let j = i; j < toks.length; j++) {
                      if (j > i) cand = cand + " + " + toks[j];
                      if (_svcNames.has(cand)) { matched = cand; span = j - i + 1; }
                    }
                    if (matched) { out.push(matched); i += span; }
                    else { out.push(toks[i]); i += 1; }  // unknown token / trailing "" kept as-is
                  }
                  return out.length ? out : [""];
                };
                let list = _parseTreats(form.treat || "");
                if (list.length === 0) list = [""];
                // Strip leading-only empties; allow trailing empty (= "row being added")
                while (list.length > 1 && list[0] === "") list.shift();

                const writeBack = (next) => {
                  // Persist trailing empty as " + " so the UI re-renders the
                  // empty picker row. Filter for price/duration calculation.
                  const cleaned = next.filter(Boolean);
                  const totalPrice = cleaned.reduce((s, n) => s + (services.find(x => x.name === n)?.price || 0), 0);
                  // Sum durations from services; default 60 only if nothing picked yet
                  const totalDur = cleaned.length === 0
                    ? 60
                    : cleaned.reduce((s, n) => s + (services.find(x => x.name === n)?.duration_minutes || 30), 0);
                  // For form.treat: keep trailing empty as " + " so a half-finished
                  // pick survives the next render. If no empty, just join the
                  // selected names.
                  const treatStr = next[next.length - 1] === "" && next.length > 1
                    ? cleaned.join(" + ") + " + "
                    : cleaned.join(" + ");
                  setForm({ ...form, treat: treatStr, price: totalPrice, dur: totalDur, dep: 0 });
                };

                const updateAt = (idx, newName) => {
                  const next = [...list];
                  next[idx] = newName;
                  writeBack(next);
                };
                const removeAt = (idx) => {
                  const next = list.filter((_, i) => i !== idx);
                  writeBack(next.length === 0 ? [""] : next);
                };
                const addAnother = () => {
                  // Only add new empty row if last row isn't already empty
                  if (list[list.length - 1] === "") return;
                  writeBack([...list, ""]);
                };
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {list.map((name, idx) => (
                      <div key={idx} style={{display:"flex",gap:8,alignItems:"center"}}>
                        <div style={{flex:1}}>
                          <SearchSelect
                            value={name}
                            onChange={(v) => updateAt(idx, v)}
                            options={bookableServices}
                            placeholder={idx === 0 ? "Search treatment by name..." : "Add another treatment..."}
                            getKey={(s) => s.name}
                            getLabel={(s) => s.name + (s.price ? " — $" + s.price : "")}
                            getGroup={(s) => s.category}
                          />
                        </div>
                        {list.length > 1 && (
                          <button type="button" onClick={() => removeAt(idx)} style={{padding:"8px 12px",background:"rgba(192,57,43,0.08)",color:"#c0392b",border:"1px solid rgba(192,57,43,0.3)",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>✕</button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={addAnother} style={{padding:"8px 14px",background:"rgba(180,140,60,0.08)",color:C.gold,border:"1.5px dashed "+C.borderGold,borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,alignSelf:"flex-start"}}>
                      + Add another service
                    </button>
                    {(() => {
                      const cleaned = list.filter(Boolean);
                      if (cleaned.length < 2) return null;
                      const total = cleaned.reduce((s, n) => s + (services.find(x => x.name === n)?.price || 0), 0);
                      const dur = cleaned.reduce((s, n) => s + (services.find(x => x.name === n)?.duration_minutes || 0), 0);
                      return (
                        <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                          💡 {cleaned.length} treatments combined · {dur} min total · auto-quoted at ${total.toFixed(2)} (edit Quote below to override)
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </Fld>
            <Fld label="Provider"><select value={form.pid||""} onChange={e=>setForm({...form,pid:+e.target.value,prov:PROVIDERS.find(p=>p.id===+e.target.value)?.name||""})} style={{...inp}}><option value="">Select...</option>{PROVIDERS.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></Fld>
            <Fld label="Date" required><input type="date" value={form.date||TODAY} onChange={e=>setForm({...form,date:e.target.value})} style={inp}/></Fld>
            <Fld label="Time"><select value={form.time||"10:00"} onChange={e=>setForm({...form,time:e.target.value})} style={{...inp}}>{(()=>{const times=[];for(let h=8;h<=20;h++){for(let m=0;m<60;m+=15){const hh=String(h).padStart(2,"0");const mm=String(m).padStart(2,"0");const val24=hh+":"+mm;const display=format12Hour(val24);times.push(<option key={val24} value={val24}>{display}</option>);}}return times;})()}</select></Fld>
            <Fld label="Quote ($)"><input type="number" inputMode="decimal" value={form.price||""} onChange={e=>setForm({...form,price:+e.target.value})} style={inp}/></Fld>
            <Fld label="Duration (min)">
              <input
                type="number"
                inputMode="numeric"
                min="5"
                step="5"
                value={form.dur||""}
                // ★ Clamp to the 5-minute minimum only on blur. Clamping on every
                //   keystroke turned the first "1" of "120" into 5, so typing
                //   120 produced 520 and long appointments couldn't be entered.
                onChange={e => {
                  const n = parseInt(e.target.value, 10);
                  setForm({...form, dur: Number.isNaN(n) ? "" : n});
                }}
                onBlur={() => {
                  const n = parseInt(form.dur, 10);
                  if (!Number.isNaN(n) && n < 5) setForm({...form, dur: 5});
                }}
                onFocus={e => e.target.select()}
                placeholder="Auto-fills from service (e.g. 70)"
                style={inp}
              />
              <div style={{fontSize:10,color:C.muted,marginTop:4}}>
                {form.dur ? `= ${Math.floor(form.dur/60)>0 ? Math.floor(form.dur/60)+"h " : ""}${form.dur%60>0 ? (form.dur%60)+"m" : ""}`.trim() : "Type any number of minutes"}
              </div>
            </Fld>
            <Fld label="Deposit collected ($)"><input type="number" inputMode="decimal" value={form.dep||0} onChange={e=>setForm({...form,dep:+e.target.value})} placeholder="0 if none taken" style={inp}/></Fld>
            <Fld label="Room">
              <input
                value={form.room||""}
                onChange={e=>setForm({...form,room:e.target.value})}
                list="appt-rooms-list"
                placeholder="e.g. Suite A · Laser Room · Room 1"
                style={inp}
              />
              <datalist id="appt-rooms-list">
                {/* Suggestions: built-in defaults + any rooms already used on other appointments */}
                {[...new Set([...ROOMS, ...appointments.map(a => a.room).filter(Boolean)])].map(r => (
                  <option key={r} value={r}/>
                ))}
              </datalist>
            </Fld>
            <Fld label="Notes (visible on appointment)" col="1/-1"><textarea value={form.notes||""} onChange={e=>setForm({...form,notes:e.target.value})} rows={3} placeholder="Any special requests, prep instructions, etc." style={{...inp,resize:"vertical",minHeight:60}}/></Fld>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:18}}>
            <Btn v="ghost" onClick={()=>{setShowF(false);}}>Cancel</Btn>
            <Btn onClick={async ()=>{
              if(editAppt){saveEdit();return;}
              if(!form.treat){alert("Please select a treatment");return;}

              // ★ NO-SHOW DEPOSIT GATE — block booking if the client has any
              // prior no-show and the deposit wasn't raised to at least $25,
              // UNLESS the staff explicitly waived it via the checkbox.
              {
                const sel = clients.find(x => x.id === form.cid);
                const ns = Number(sel?.noshow) || 0;
                if (ns > 0 && !form.deposit_waived && (+form.dep || 0) < 25) {
                  alert(`${sel?.name || "This client"} has ${ns} no-show${ns>1?"s":""}. A minimum $25 deposit is required to book.\n\nEither enter the deposit in the field below, or tick "Waive deposit" above (visible only because you're booking from inside the CRM).`);
                  return;
                }
              }

              // ★ DOUBLE-CLICK LOCK — the Samantha Hockfield incident: two
              //   clicks 2s apart created two identical clients + two
              //   identical appointments. 5s window lock kills the repeat.
              if (window.__elyonBookingLock) { console.warn("booking ignored — lock active"); return; }
              window.__elyonBookingLock = true;
              setTimeout(() => { window.__elyonBookingLock = false; }, 5000);

              let clientId = form.cid;
              let clientName = "";

              if(showNewClient){
                if(!newClient.name.trim() || !newClient.phone.trim() || !newClient.email.trim()){
                  alert("New client: Name, phone, and email are required");
                  return;
                }
                // ★ DUPLICATE-CLIENT GUARD — same phone or email already on
                //   file? Offer to book for the existing client instead of
                //   silently creating a twin record.
                const _digits = newClient.phone.replace(/\D/g, "").slice(-10);
                const _emailLc = newClient.email.trim().toLowerCase();
                const dup = clients.find(c => {
                  const cd = (c.phone || "").replace(/\D/g, "").slice(-10);
                  return (_digits && cd === _digits) || (_emailLc && (c.email || "").trim().toLowerCase() === _emailLc);
                });
                let _createNew = !dup;
                if (dup) {
                  const useExisting = window.confirm(`⚠️ A client with this phone/email already exists:\n\n${dup.name}\n${dup.phone || ""} · ${dup.email || ""}\n\nOK — book for the EXISTING client (recommended)\nCancel — create a SEPARATE new client (e.g. a family member sharing a phone)`);
                  if (useExisting) {
                    clientId = dup.id;
                    clientName = dup.name;
                    setShowNewClient(false);
                  } else {
                    if (!window.confirm(`Create a SEPARATE new client "${newClient.name}" that shares this phone/email?\n\nOK — yes, create the new client\nCancel — stop`)) return;
                    _createNew = true;
                  }
                }
                if (_createNew) {
                const created = {...newClient,dob:newClient.dob||null,id:Date.now(),allergies:"None",med:"No contraindications",spent:0,visits:0,noshow:0,tags:[],notes:"",status:"active",cardOnFile:false,gfeStatus:"none",loyaltyPts:0,memberPlan:null};
                const { error: ce } = await supabase.from("clients").insert([created]);
                if(ce){ alert("Failed to create client: "+ce.message); return; }
                setClients(cs=>[...cs,created]);
                clientId = created.id;
                clientName = created.name;
                }
              } else {
                if(!form.cid){alert("Please select a client or click + New Client");return;}

const c = clients.find(x=>x.id===form.cid);
                clientName = c?.name||"";
              }
              
              const newApptBase = {...form,cid:clientId,id:Date.now(),cn:clientName,checkin:"upcoming",tip:0,paid:false,price:+form.price||0,dep:+form.dep||0,dur:+form.dur||60,time:form.time||"10:00",date:form.date||TODAY,status:form.status||"confirmed",loc:form.loc||"Nova Med Spa",notes:form.notes||"",room:form.room||"",prov:form.prov||""};
              // ★ Strip form-only fields that don't exist as DB columns. Without
              //   this, Postgres rejects the whole insert with
              //   "Could not find the 'deposit_waived' column" and the
              //   booking silently fails for any no-show client that staff
              //   tries to waive the deposit on. Also strips any other
              //   underscore-prefixed transient state.
              delete newApptBase.deposit_waived;
              for (const k of Object.keys(newApptBase)) {
                if (k.startsWith("_")) delete newApptBase[k];
              }
              // ★ Card policy: record WHO chose to book without a card, so the
              //   exception is visible on the appointment instead of invisible.
              if (newApptBase.card_requirement_waived) {
                try {
                  const { data: { user: _cu } } = await supabase.auth.getUser();
                  const { data: _cp } = _cu ? await supabase.from("profiles").select("full_name,email").eq("id", _cu.id).maybeSingle() : { data: null };
                  newApptBase.card_waived_by_name = _cp?.full_name || _cp?.email || "staff";
                } catch (e) { console.warn("card waiver stamp failed:", e); }
              }
              const newAppt = await stampBookedBy(newApptBase, "crm");
              console.log("Creating appointment:", newAppt);
              const { error, data: insertedData } = await supabase.from("appointments").insert([newAppt]).select();
              console.log("Insert result - error:", error, "data:", insertedData);
              if (error) { alert("Save failed: " + error.message); return; }
              setAppointments(as=>[...as,newAppt]);

              // ★ CARD ON FILE (policy 2026-09-01): every appointment is held
              //   with a card. Ask right after saving — this runs for BOTH
              //   existing and brand-new clients (the "+ New Client" path used
              //   to skip the prompt entirely). Staff can take the card here,
              //   text the link, or waive; the booking itself already saved.
              if (!newApptBase.card_requirement_waived) {
                try {
                  const _cardClient = showNewClient
                    ? { id: clientId, name: clientName, phone: newClient?.phone, email: newClient?.email, card_on_file: false }
                    : clients.find(x => x.id === clientId);
                  const _hasCard = !!(_cardClient?.card_on_file || _cardClient?.cardOnFile);
                  if (_cardClient?.id && !_hasCard) {
                    setCardPrompt({ client: _cardClient, appointment: { ...newAppt, id: newAppt.id } });
                  }
                } catch (e) { console.warn("card prompt failed:", e); }
              }

              // ★ Booking confirmation SMS + Email — re-enabled 2026-06-10
              //   per owner. Fire-and-forget so a notify failure can't break
              //   the booking flow. Skips silently if phone/email missing.
              const _clientForConfirm = showNewClient ? newClient : clients.find(x=>x.id===clientId);
              const _emailForConfirm = _clientForConfirm?.email;
              const _phoneForConfirm = _clientForConfirm?.phone;
              const _nameForConfirm = clientName;

              // Build human-readable date/time for the SMS text
              const _firstName = (_nameForConfirm || "").split(" ")[0] || "there";
              const _niceDate = (() => {
                if (!form.date) return "";
                const [y, m, d] = String(form.date).split("-").map(Number);
                if (!y || !m || !d) return form.date;
                return new Date(y, m - 1, d, 12).toLocaleDateString("en-US", {weekday:"long", month:"long", day:"numeric"});
              })();
              const _niceTime = format12Hour(form.time);
              const _spa = "Nova Med Spa";
              const _msg = `Hi ${_firstName}! Your ${form.treat || "appointment"} with ${form.provider_name || "us"} is confirmed for ${_niceDate} at ${_niceTime}. Reply C to confirm, or call (516) 543-3940 to reschedule. — ${_spa}`;

              // ★ AWAIT both sends so we can show the staff exactly what
              //   happened in a toast. Each is independently try/wrapped so
              //   one failure doesn't stop the other.
              let _smsStatus = _phoneForConfirm ? "pending" : "no_phone";
              let _emailStatus = _emailForConfirm ? "pending" : "no_email";

              if (_phoneForConfirm) {
                try {
                  const { data: _sData, error: _sErr } = await supabase.functions.invoke("send-lead-sms", {
                    body: { phone: _phoneForConfirm, client_id: clientId, message: _msg }
                  });
                  if (_sErr) {
                    // Try to read the JSON error body for a real reason
                    let _detail = _sErr.message || "unknown";
                    try { if (_sErr.context?.json) { const j = await _sErr.context.json(); if (j?.error) _detail = j.error; } } catch {}
                    _smsStatus = "FAIL: " + _detail;
                    console.error("[booking-sms] error:", _sErr, _sData);
                  } else if (_sData?.error) {
                    _smsStatus = "FAIL: " + _sData.error;
                  } else {
                    _smsStatus = "ok";
                  }
                } catch (e) {
                  _smsStatus = "FAIL: " + (e?.message || "exception");
                  console.error("[booking-sms] exception:", e);
                }
              }
              if (_emailForConfirm) {
                try {
                  const { data: _eData, error: _eErr } = await supabase.functions.invoke("send-appointment-email", {
                    body: {
                      type: "confirmation",
                      to_email: _emailForConfirm,
                      to_name: _nameForConfirm,
                      service: form.treat || "appointment",
                      provider: form.provider_name || "",
                      date: form.date,
                      time: form.time,
                      location: form.loc || "",
                    }
                  });
                  if (_eErr) {
                    let _detail = _eErr.message || "unknown";
                    try { if (_eErr.context?.json) { const j = await _eErr.context.json(); if (j?.error) _detail = j.error; } } catch {}
                    _emailStatus = "FAIL: " + _detail;
                    console.error("[booking-email] error:", _eErr, _eData);
                  } else if (_eData?.error) {
                    _emailStatus = "FAIL: " + _eData.error;
                  } else {
                    _emailStatus = "ok";
                  }
                } catch (e) {
                  _emailStatus = "FAIL: " + (e?.message || "exception");
                  console.error("[booking-email] exception:", e);
                }
              }

              // Surface to the staff
              setCommToast({
                kind: "booking",
                client: _nameForConfirm,
                phone: _phoneForConfirm,
                email: _emailForConfirm,
                sms: _smsStatus,
                email_status: _emailStatus,
              });
              setTimeout(() => setCommToast(null), 12000);

              // ★ Trigger pre-care immediately for any rule with
              //   trigger_event="on_booking". Without this, on_booking rules
              //   only fire when the 15-min cron next wakes up, which feels
              //   broken to the client ("I just booked and got nothing").
              //   pre-post-cron is idempotent (UNIQUE rule_id+appointment_id
              //   +channel) so calling it here can't double-send. Fire-and-
              //   forget — never block the booking UI on this.
              try {
                supabase.functions.invoke("pre-post-cron", { body: {} })
                  .then(({ data, error }) => {
                    if (error) console.error("pre-post-cron fast-fire error:", error);
                    else console.log("pre-post-cron fast-fire:", data);
                  });
              } catch(e) { console.error("pre-post-cron exception:", e); }

              // ★ STAFF NOTIFICATION on every new CRM-booked appointment.
              //   Sends to every phone in booking_settings.notification_phones
              //   PLUS every active profile that opted into new_bookings.
              //   Includes WHO booked it (the front-desk user who clicked
              //   Book) so the owner sees both source + author at a glance.
              try {
                const { data: { user: _u } } = await supabase.auth.getUser();
                let _staffLabel = "Front desk";
                if (_u?.id) {
                  const { data: _p } = await supabase.from("profiles")
                    .select("full_name,email").eq("id", _u.id).maybeSingle();
                  _staffLabel = _p?.full_name || _p?.email || _u.email || "Front desk";
                }
                supabase.functions.invoke("notify-new-booking", {
                  body: {
                    client_name: _nameForConfirm,
                    client_phone: _phoneForConfirm,
                    client_email: _emailForConfirm,
                    client_notes: newAppt.notes || "",
                    service_name: newAppt.treat,
                    provider_name: newAppt.prov,
                    date: newAppt.date,
                    time: format12Hour(newAppt.time),
                    duration: newAppt.dur,
                    price: newAppt.price,
                    deposit_paid: newAppt.dep > 0 ? newAppt.dep : 0,
                    spa_name: newAppt.loc || "Nova Med Spa",
                    booking_source: "CRM Front Desk",
                    booked_by: _staffLabel,
                  },
                }).then(({ error: _nErr }) => {
                  if (_nErr) console.error("notify-new-booking error:", _nErr);
                });
              } catch (notifyErr) { console.error("notify-new-booking exception:", notifyErr); }
                            setShowF(false);
              setShowNewClient(false);
              setNewClient({name:"",phone:"",email:"",dob:"",loc:"Nova Med Spa"});
            }}>{editAppt?"Save Changes":(showNewClient?"Create Client & Book":"Book Appointment")}</Btn>
          </div>
          {showNewClient && !form.cid && (
            <div style={{padding:"10px 14px",marginTop:8,background:"rgba(37,99,235,0.08)",borderRadius:8,fontSize:11,color:"#2563eb",fontWeight:600,textAlign:"center"}}>
              Tip: Click Save Client below to save them first, then charge a deposit before booking.
            </div>
          )}
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",flexWrap:"wrap",marginTop:10,paddingTop:10,borderTop:"1px solid "+C.border}}>
            {showNewClient && !form.cid && (
              <button type="button" onClick={async()=>{
                if(!newClient.name.trim() || !newClient.phone.trim() || !newClient.email.trim()){alert("Name, phone, and email are required");return;}
                // ★ Same duplicate guard as the main Book path.
                const _digits = newClient.phone.replace(/\D/g, "").slice(-10);
                const _emailLc = newClient.email.trim().toLowerCase();
                const dup = clients.find(c => {
                  const cd = (c.phone || "").replace(/\D/g, "").slice(-10);
                  return (_digits && cd === _digits) || (_emailLc && (c.email || "").trim().toLowerCase() === _emailLc);
                });
                if (dup) {
                  const useExisting = window.confirm(`⚠️ A client with this phone/email already exists:\n\n${dup.name}\n${dup.phone || ""} · ${dup.email || ""}\n\nOK — use the EXISTING client\nCancel — create a SEPARATE new client (e.g. a family member sharing a phone)`);
                  if (useExisting) {
                    setForm(f=>({...f,cid:dup.id}));
                    setShowNewClient(false);
                    setNewClient({name:"",phone:"",email:"",dob:"",loc:"Nova Med Spa"});
                    return;
                  }
                  if (!window.confirm(`Create a SEPARATE new client "${newClient.name}" that shares this phone/email?\n\nOK — yes, create the new client\nCancel — stop`)) return;
                  // fall through to create the new (separate) client below
                }
                const created = {...newClient,dob:newClient.dob||null,id:Date.now(),allergies:"None",med:"No contraindications",spent:0,visits:0,noshow:0,tags:[],notes:"",status:"active",cardOnFile:false,gfeStatus:"none",loyaltyPts:0,memberPlan:null};
                const { error: ce } = await supabase.from("clients").insert([created]);
                if(ce){alert("Failed to create client: "+ce.message);return;}
                setClients(cs=>[...cs,created]);
                setForm(f=>({...f,cid:created.id}));
                setShowNewClient(false);
                setNewClient({name:"",phone:"",email:"",dob:"",loc:"Nova Med Spa"});
                alert("Client saved! Now you can charge a deposit if needed.");
              }} style={{padding:"10px 16px",background:"#2563eb",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>💾 Save Client</button>
            )}
            {form.cid && (
              <button type="button" onClick={()=>{
                const clientObj = clients.find(x=>x.id===form.cid);
                if(!clientObj){alert("Client not found");return;}
                const amount = +form.dep > 0 ? +form.dep : null;
                setDepositCharge({client:clientObj, amount:amount});
              }} style={{padding:"10px 16px",background:"#2e8b57",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>💳 Charge Deposit{+form.dep > 0 ? " ($"+(+form.dep)+")" : ""}</button>
            )}
          </div>
        </Modal>
      )}
      {depositCharge && (
        <ChargeModal
          client={depositCharge.client}
          defaultAmount={depositCharge.amount}
          description={"Deposit for "+(form.treat||"appointment")}
          allowAmountEdit={true}
          onClose={()=>setDepositCharge(null)}
          onComplete={(p)=>{
            setDepositCharge(null);
            setForm(f=>({...f, dep: p.amount || f.dep}));
            alert("✅ Deposit of $"+p.amount+" charged successfully! The amount has been updated on the appointment.");
          }}
        />
      )}
    </div>
  );
}

// ── CHECKOUT POS ──────────────────────────────────────────────────────────────
function Checkout({appointments,setAppointments,clients}){
  const isMobile = useIsMobile();
  const { canViewSales } = useAuth();   // gate the sales/revenue totals
  const [sel,setSel] = useState(null);
  const [tip,setTip] = useState(0);
  const [tipPct,setTipPct] = useState(0);
  const [method,setMethod] = useState("card");
  const [discount,setDiscount] = useState(0);
  const [paid,setPaid] = useState(false);
  const [recentPayments, setRecentPayments] = useState([]);
  const [rangeKey, setRangeKey] = useState("today"); // today | yesterday | 7d | month | lastmonth | all | custom
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");
  const [refundingPayment, setRefundingPayment] = useState(null);
  const [splitCheckout, setSplitCheckout] = useState(null); // appt being paid via the split modal

  // Compute the [from, to] window in ISO based on the active range
  const range = (() => {
    const now = new Date();
    const startOfToday = new Date(now); startOfToday.setHours(0,0,0,0);
    const endOfToday = new Date(startOfToday); endOfToday.setDate(endOfToday.getDate()+1);
    if (rangeKey === "today") return { from: startOfToday, to: endOfToday, label: "Today" };
    if (rangeKey === "yesterday") { const f = new Date(startOfToday); f.setDate(f.getDate()-1); return { from: f, to: startOfToday, label: "Yesterday" }; }
    if (rangeKey === "7d") { const f = new Date(startOfToday); f.setDate(f.getDate()-6); return { from: f, to: endOfToday, label: "Last 7 days" }; }
    if (rangeKey === "month") { const f = new Date(now.getFullYear(), now.getMonth(), 1); return { from: f, to: endOfToday, label: now.toLocaleDateString("en-US",{month:"long"}) }; }
    if (rangeKey === "lastmonth") { const f = new Date(now.getFullYear(), now.getMonth()-1, 1); const t = new Date(now.getFullYear(), now.getMonth(), 1); return { from: f, to: t, label: "Last month" }; }
    if (rangeKey === "all") return { from: new Date("2000-01-01"), to: new Date(now.getTime() + 86400000), label: "All time" };
    if (rangeKey === "custom" && customFrom && customTo) {
      const f = new Date(customFrom + "T00:00:00");
      const t = new Date(customTo + "T23:59:59");
      return { from: f, to: t, label: customFrom + " → " + customTo };
    }
    return { from: startOfToday, to: endOfToday, label: "Today" };
  })();

  // Live-load payments in the active range — every charge anywhere in the CRM
  // shows up here instantly (no refresh needed).
  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data } = await supabase
        .from("payments")
        .select("*")
        .gte("created_at", range.from.toISOString())
        .lt("created_at", range.to.toISOString())
        .order("created_at", { ascending: false })
        .limit(500);
      if (active) setRecentPayments(data || []);
    };
    load();
    const ch = supabase.channel("checkout-payments-sync-"+rangeKey)
      .on("postgres_changes", { event: "*", schema: "public", table: "payments" }, load)
      .subscribe();
    return () => { active = false; supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, customFrom, customTo]);

  const filteredPayments = recentPayments.filter(p =>
    // ★ Hide voided payments — a voided row is a corrected mistake and must
    //   vanish from the POS list AND the totals. The query didn't exclude them,
    //   so a "deleted" cash mistake kept showing (and looked like Delete didn't
    //   work). This is what makes the 🗑 Delete actually remove it from view.
    p.status !== "voided" &&
    (methodFilter === "all" || (p.payment_method || "").toLowerCase() === methodFilter)
  );

  const ready = appointments.filter(a=>a.checkin==="in-treatment"||a.checkin==="checked-out");
  const subtotal = sel?(sel.price-discount):0;
  const total = subtotal+tip;
  const applyTip = (pct) => {setTipPct(pct);if(sel)setTip(Math.round(sel.price*(pct/100)));};

  // Aggregate stats for the active range
  const succeeded = filteredPayments.filter(p => p.status === "succeeded" || p.status === "completed");
  const refunds = filteredPayments.filter(p => p.status === "refunded" || Number(p.amount || 0) < 0);
  const grossRevenue = succeeded.reduce((s, p) => s + Math.max(0, Number(p.amount || 0)), 0);
  const refundedAmt = Math.abs(refunds.reduce((s, p) => s + Number(p.amount || 0), 0));
  const netRevenue = grossRevenue - refundedAmt;
  const totalTips = succeeded.reduce((s, p) => s + Number(p.metadata?.tip || 0), 0);

  // Method breakdown
  const byMethod = {};
  succeeded.forEach(p => {
    const m = (p.payment_method || "other").toLowerCase();
    byMethod[m] = (byMethod[m] || 0) + Number(p.amount || 0);
  });

  const exportCsv = () => {
    const rows = [["Date","Time","Client","Description","Method","Amount","Tip","Status","By"]];
    filteredPayments.forEach(p => {
      const d = new Date(p.created_at);
      rows.push([
        d.toLocaleDateString("en-US"),
        d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",hour12:true}),
        p.client_name || "Guest",
        (p.description || "").replace(/[\n,]/g, " "),
        p.payment_method || "",
        Number(p.amount || 0).toFixed(2),
        Number(p.metadata?.tip || 0).toFixed(2),
        p.status || "",
        p.created_by_name || "",
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions-${range.label.replace(/\s/g,"_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 360px",gap:18}}>
      <div>
        {/* TRANSACTIONS — every charge in any date range, synced live */}
        <div style={{padding:"18px 20px",background:"linear-gradient(135deg,"+C.goldBg+", #fff)",border:"1px solid "+C.borderGold,borderRadius:12,marginBottom:18}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,letterSpacing:"0.02em"}}>💰 Transactions — {range.label}</div>
              <div style={{fontSize:11,color:C.muted,marginTop:3}}>{filteredPayments.length} payment{filteredPayments.length!==1?"s":""} · synced live from anywhere in the CRM</div>
            </div>
            <button onClick={exportCsv} disabled={filteredPayments.length===0} style={{padding:"6px 14px",background:filteredPayments.length===0?C.light:"#fff",color:filteredPayments.length===0?C.dim:C.gold,border:"1px solid "+(filteredPayments.length===0?C.border:C.borderGold),borderRadius:99,fontSize:11,fontWeight:700,cursor:filteredPayments.length===0?"not-allowed":"pointer",fontFamily:F}}>⬇ Export CSV</button>
          </div>

          {/* Date range chips */}
          <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10}}>
            {[
              ["today","Today"],
              ["yesterday","Yesterday"],
              ["7d","Last 7 days"],
              ["month","This Month"],
              ["lastmonth","Last Month"],
              ["all","All Time"],
              ["custom","Custom..."],
            ].map(([key,label])=>{
              const active = rangeKey === key;
              return (
                <button key={key} onClick={()=>setRangeKey(key)} style={{
                  padding:"6px 12px",
                  fontSize:11.5,
                  fontWeight:active?800:600,
                  color:active?"#fff":C.muted,
                  background:active?"linear-gradient(135deg,"+C.gold+","+C.goldD+")":"#fff",
                  border:"1px solid "+(active?C.gold:C.border),
                  borderRadius:99,
                  cursor:"pointer",
                  fontFamily:F,
                  letterSpacing:"0.02em",
                  whiteSpace:"nowrap",
                  boxShadow:active?"0 2px 6px "+C.gold+"30":"none",
                }}>{label}</button>
              );
            })}
          </div>

          {/* Custom date pickers */}
          {rangeKey === "custom" && (
            <div style={{display:"flex",gap:8,marginBottom:12,padding:"10px 12px",background:"#fff",borderRadius:8,border:"1px solid "+C.borderGold,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontSize:11,color:C.muted,fontWeight:600}}>From</span>
              <input type="date" value={customFrom} onChange={e=>setCustomFrom(e.target.value)} style={{padding:"6px 10px",border:"1px solid "+C.border,borderRadius:6,fontSize:12,fontFamily:F,color:C.text,outline:"none"}}/>
              <span style={{fontSize:11,color:C.muted,fontWeight:600}}>To</span>
              <input type="date" value={customTo} onChange={e=>setCustomTo(e.target.value)} style={{padding:"6px 10px",border:"1px solid "+C.border,borderRadius:6,fontSize:12,fontFamily:F,color:C.text,outline:"none"}}/>
              {(!customFrom || !customTo) && <span style={{fontSize:10,color:C.muted,fontStyle:"italic"}}>pick both dates</span>}
            </div>
          )}

          {/* Card processing-fee admin panel (admin/director/Sarah only). */}
          {canViewSales && <CardFeeSettings/>}

          {/* Aggregate stats row — explicit so staff don't confuse Net vs Refunds.
              When there are no refunds, Refunds clearly says $0.00 in muted gray.
              ★ Revenue totals are restricted to admin/director/Sarah (canViewSales). */}
          {canViewSales && (<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))",gap:8,marginBottom:14}}>
            <div style={{padding:"10px 12px",background:"#fff",borderRadius:9,border:"1px solid "+C.borderSoft}}>
              <div style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Sales (Gross)</div>
              <div style={{fontSize:20,fontWeight:800,color:C.gold,fontFamily:S,letterSpacing:"-0.02em"}}>${grossRevenue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
              <div style={{fontSize:10,color:C.dim,marginTop:2}}>{succeeded.length} charge{succeeded.length===1?"":"s"}</div>
            </div>
            <div style={{padding:"10px 12px",background:refundedAmt>0?C.redL:"#fafafa",borderRadius:9,border:"1px solid "+(refundedAmt>0?C.red+"40":C.borderSoft)}}>
              <div style={{fontSize:10,color:refundedAmt>0?C.red:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Refunds</div>
              <div style={{fontSize:20,fontWeight:800,color:refundedAmt>0?C.red:C.dim,fontFamily:S,letterSpacing:"-0.02em"}}>−${refundedAmt.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
              <div style={{fontSize:10,color:C.dim,marginTop:2}}>{refunds.length} refund{refunds.length===1?"":"s"}</div>
            </div>
            <div style={{padding:"10px 12px",background:"#fff",borderRadius:9,border:"1px solid "+C.borderSoft}}>
              <div style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Net Revenue</div>
              <div style={{fontSize:20,fontWeight:800,color:C.green,fontFamily:S,letterSpacing:"-0.02em"}}>${netRevenue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
              <div style={{fontSize:10,color:C.dim,marginTop:2}}>Sales − refunds</div>
            </div>
            <div style={{padding:"10px 12px",background:"#fff",borderRadius:9,border:"1px solid "+C.borderSoft}}>
              <div style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>Tips</div>
              <div style={{fontSize:20,fontWeight:800,color:C.purple,fontFamily:S,letterSpacing:"-0.02em"}}>${totalTips.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
              <div style={{fontSize:10,color:C.dim,marginTop:2}}>To staff</div>
            </div>
          </div>)}

          {/* Method filter */}
          {Object.keys(byMethod).length > 0 && (
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:12}}>
              <button onClick={()=>setMethodFilter("all")} style={{padding:"4px 10px",fontSize:11,fontWeight:methodFilter==="all"?800:600,color:methodFilter==="all"?C.gold:C.muted,background:methodFilter==="all"?C.goldBg:"#fff",border:"1px solid "+(methodFilter==="all"?C.borderGold:C.borderSoft),borderRadius:99,cursor:"pointer",fontFamily:F}}>All methods</button>
              {Object.entries(byMethod).map(([m, amt]) => (
                <button key={m} onClick={()=>setMethodFilter(m)} style={{padding:"4px 10px",fontSize:11,fontWeight:methodFilter===m?800:600,color:methodFilter===m?C.gold:C.muted,background:methodFilter===m?C.goldBg:"#fff",border:"1px solid "+(methodFilter===m?C.borderGold:C.borderSoft),borderRadius:99,cursor:"pointer",fontFamily:F,textTransform:"capitalize"}}>
                  {m} <span style={{opacity:0.7,marginLeft:3,fontWeight:600}}>${amt.toLocaleString()}</span>
                </button>
              ))}
            </div>
          )}

          {/* Transactions list */}
          {filteredPayments.length === 0 ? (
            <div style={{padding:24,textAlign:"center",color:C.muted,fontSize:12,background:"#fff",borderRadius:9,border:"1px dashed "+C.borderGold}}>No transactions in this range</div>
          ) : (
            <div style={{maxHeight:340,overflowY:"auto",display:"flex",flexDirection:"column",gap:5}}>
              {filteredPayments.map(p => {
                const isRefund = p.status === "refunded" || Number(p.amount || 0) < 0;
                const isCash = (p.payment_method || "").toLowerCase() === "cash" || (p.method || "").toLowerCase() === "cash";
                const isFullyRefunded = p.refunded && Number(p.refund_amount || 0) >= Number(p.amount || 0);
                const created = new Date(p.created_at);
                return (
                  <div key={p.id} style={{padding:"11px 13px",background:"#fff",borderRadius:8,border:"1px solid "+C.borderSoft,borderLeft:"3px solid "+(isRefund?C.red:C.green),display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <div style={{flex:1,minWidth:160}}>
                      <div style={{fontSize:13.5,fontWeight:700,color:C.text}}>{p.client_name || "Guest"}</div>
                      <div style={{fontSize:11,color:C.muted,marginTop:1}}>
                        {p.description || "Payment"} · {created.toLocaleDateString("en-US",{month:"short",day:"numeric"})} at {created.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",hour12:true})}
                        {p.created_by_name?` · by ${(p.created_by_name||"").split(" ")[0]}`:""}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
                      {p.payment_method && <span style={{fontSize:10,padding:"2px 8px",background:C.light,color:C.muted,borderRadius:99,fontWeight:600,textTransform:"capitalize"}}>{p.payment_method}</span>}
                      {p.metadata?.tip > 0 && <span style={{fontSize:10,padding:"2px 8px",background:C.greenL,color:C.green,borderRadius:99,fontWeight:600}}>+${p.metadata.tip} tip</span>}
                      {isRefund && <span style={{fontSize:10,padding:"2px 8px",background:C.redL,color:C.red,borderRadius:99,fontWeight:700,letterSpacing:"0.04em"}}>REFUND</span>}
                      {p.refunded && !isRefund && (
                        <span style={{fontSize:10,padding:"2px 8px",background:"#fef3c7",color:"#92400e",borderRadius:99,fontWeight:700,letterSpacing:"0.04em"}}>
                          {isFullyRefunded ? "REFUNDED" : `−$${Number(p.refund_amount||0).toFixed(2)} REFUNDED`}
                        </span>
                      )}
                    </div>
                    <div style={{fontSize:16,fontWeight:800,color:isRefund?C.red:C.text,fontFamily:S,minWidth:80,textAlign:"right"}}>
                      {isRefund?"-":""}${Math.abs(Number(p.amount||0)).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
                    </div>
                    {/* ★ Action buttons — Refund (for any non-refund row not fully refunded), Delete (cash only, mistake fix) */}
                    {!isRefund && !isFullyRefunded && (
                      <button onClick={()=>setRefundingPayment(p)} title="Issue a refund for this charge"
                        style={{padding:"6px 10px",background:"#fff",color:C.red,border:`1px solid ${C.red}40`,borderRadius:6,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:F}}>↩ Refund</button>
                    )}
                    {isCash && p.status !== "voided" && (
                      <button onClick={async ()=>{
                        const reason = window.prompt(`Void this cash payment of $${Number(p.amount||0).toFixed(2)}?\n\nUse this ONLY for mistakes (e.g. wrong method picked).\n\nEnter the reason — kept for audit:`);
                        if (!reason || !reason.trim()) return;
                        // ★ SOFT-DELETE — never hard-delete a financial record.
                        //   Mark as voided with audit fields so we can always
                        //   forensically reconstruct what happened.
                        const { data: { user } } = await supabase.auth.getUser();
                        const { data: prof } = user ? await supabase.from("profiles").select("full_name,email").eq("id", user.id).single() : { data: null };
                        const voidedNotes = `${p.notes || ""}\n[VOIDED ${new Date().toISOString()} by ${prof?.full_name || prof?.email || "unknown"}] ${reason.trim()}`.trim();
                        const { error } = await supabase.from("payments").update({
                          status: "voided",
                          notes: voidedNotes,
                        }).eq("id", p.id);
                        if (error) {
                          alert("Void failed: " + error.message);
                          return;
                        }
                        // Refresh local state so the row disappears from
                        // active totals (the list filter excludes voided).
                        setRecentPayments(prev => prev.map(x => x.id === p.id ? { ...x, status: "voided", notes: voidedNotes } : x));
                      }} title="Delete this cash payment — use for mistakes (wrong amount/method). Removes it from the POS; kept for audit. NOT a customer refund."
                        style={{padding:"6px 10px",background:"#fff",color:C.muted,border:`1px solid ${C.border}`,borderRadius:6,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:F}}>🗑 Delete</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:12}}>Ready for Checkout</div>
        {ready.length===0&&<div style={{padding:"40px",textAlign:"center",color:C.dim,fontSize:13,background:C.light,borderRadius:10,border:`1px dashed ${C.border}`}}>No clients ready for checkout</div>}
        {ready.map(a=>{const p=PROVIDERS.find(x=>x.id===a.pid);return(
          <div key={a.id} onClick={()=>{setSel(a);setTip(0);setTipPct(0);setDiscount(0);setPaid(a.paid);}}
            style={{padding:"13px 16px",background:sel?.id===a.id?C.goldBg:C.card,border:`1.5px solid ${sel?.id===a.id?C.borderGold:C.border}`,borderRadius:10,cursor:"pointer",marginBottom:8,transition:"all 0.15s"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",gap:10,alignItems:"center"}}>
                <div style={{width:34,height:34,borderRadius:8,background:(p?.color||C.gold)+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:p?.color||C.gold}}>{a.cn?.split(" ").map(n=>n[0]).join("")}</div>
                <div><div style={{fontSize:13,fontWeight:600,color:C.text}}>{a.cn}</div><div style={{fontSize:11,color:C.muted}}>{a.treat} · {a.time}</div></div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:15,fontWeight:800,color:C.text}}>${a.price}</div>
                {a.paid?<span style={{fontSize:11,color:C.green,fontWeight:600}}>✓ Paid</span>:<span style={{fontSize:11,color:C.orange,fontWeight:600}}>Pending</span>}
              </div>
            </div>
          </div>
        );})}
      </div>

      <Card style={{position:isMobile?"relative":"sticky",top:0}}>
        <div style={{padding:"15px 18px",borderBottom:`1px solid ${C.border}`,background:C.goldBg}}>
          <div style={{fontSize:14,fontWeight:800,color:C.gold,fontFamily:S}}>💳 POS Checkout</div>
        </div>
        {sel?(
          <div style={{padding:18}}>
            <div style={{padding:"10px 13px",background:C.light,borderRadius:9,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text}}>{sel.cn}</div>
              <div style={{fontSize:11,color:C.muted}}>{sel.treat} · {sel.date}</div>
            </div>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:7}}>Tip</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:5,marginBottom:7}}>
                {[0,15,18,20].map(pct=>(
                  <button key={pct} onClick={()=>applyTip(pct)} style={{padding:"7px 4px",fontSize:12,fontWeight:tipPct===pct?700:400,background:tipPct===pct?C.gold:C.light,color:tipPct===pct?"#fff":C.muted,border:`1px solid ${tipPct===pct?C.gold:C.border}`,borderRadius:7,cursor:"pointer",fontFamily:F}}>{pct===0?"None":`${pct}%`}</button>
                ))}
              </div>
              <input type="number" value={tip} onChange={e=>{setTip(+e.target.value);setTipPct(0);}} placeholder="Custom tip $" style={{...inp}}/>
            </div>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:7}}>Discount</div>
              <input type="number" value={discount} onChange={e=>setDiscount(+e.target.value)} placeholder="Discount $" style={{...inp}}/>
            </div>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:7}}>Payment</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:5}}>
                {["card","cash","split"].map(m=>(
                  <button key={m} onClick={()=>setMethod(m)} style={{padding:"8px",fontSize:12,fontWeight:method===m?700:400,background:method===m?C.gold:C.light,color:method===m?"#fff":C.muted,border:`1px solid ${method===m?C.gold:C.border}`,borderRadius:7,cursor:"pointer",fontFamily:F,textTransform:"capitalize"}}>{m}</button>
                ))}
              </div>
            </div>
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginBottom:16}}>
              {[["Service",`$${sel.price}`],["Discount",discount>0?`-$${discount}`:"-"],["Tip",tip>0?`+$${tip}`:"-"],["TOTAL",`$${total}`]].map(([l,v],i)=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",fontSize:i===3?15:12,fontWeight:i===3?800:400,color:i===3?C.gold:C.muted,borderTop:i===3?`1px solid ${C.border}`:"none",marginTop:i===3?6:0}}>
                  <span>{l}</span><span>{v}</span>
                </div>
              ))}
            </div>
            {paid
              ?<div style={{padding:"13px",background:C.greenL,borderRadius:9,textAlign:"center",color:C.green,fontWeight:700,border:`1px solid rgba(46,139,87,0.25)`}}>✅ Paid — ${total}</div>
              :method==="split"
              ?<Btn full v="primary" sz="lg" onClick={()=>setSplitCheckout(sel)}>🧩 Split Payment — ${total}</Btn>
              :<Btn full v="primary" sz="lg" onClick={async ()=>{
                const { data: { user } } = await supabase.auth.getUser();
                const { data: prof } = user ? await supabase.from("profiles").select("full_name,email").eq("id", user.id).single() : { data: null };
                const updated = {status:"completed",checkin:"checked-out",paid:true,tip};
                await supabase.from("appointments").update(updated).eq("id",sel.id);
                // ★ Create a payments row + capture the id so we can link the wallet items back to it.
                const { data: payRow, error: payErr } = await supabase.from("payments").insert([{
                  client_id: sel.cid,
                  client_name: sel.cn,
                  appointment_id: sel.id,
                  amount: total,
                  status: "succeeded",
                  payment_method_type: method,
                  payment_method: method,
                  description: sel.treat || "POS checkout",
                  created_by_user: user?.id || null,
                  created_by_name: prof?.full_name || prof?.email || "Front Desk",
                  metadata: {
                    service_price: sel.price,
                    discount: discount,
                    tip: tip,
                    provider: sel.prov,
                    source: "pos_checkout",
                  },
                }]).select("id").single();
                if (payErr) {
                  alert("Payment save failed: " + payErr.message);
                  return;
                }
                // ★ AUTO-WALLET — POS checkout was missing this. Owner directive
                //   "every client who pays — what she bought goes to her wallet."
                //   The cart here is implicit: the single appointment service.
                if (sel.cid && sel.treat) {
                  try {
                    const _res = await addCartToWallet(sel.cid, sel.cn, [{
                      name: sel.treat,
                      price: sel.price || total,
                      qty: 1,
                    }], {
                      paymentId: payRow?.id || null,
                      paymentMethod: method,
                      userId: user?.id || null,
                      userName: prof?.full_name || prof?.email || "Front Desk",
                    });
                    if (_res.added === 0 && _res.errors?.length) {
                      alert("⚠️ Payment saved but wallet add failed: " + _res.errors[0]);
                    }
                  } catch (we) { alert("⚠️ Payment saved but wallet exception: " + (we?.message || we)); }
                }
                // ★ Deduct one session from any active package for this treatment.
                //   This runs AFTER addCartToWallet so the package we just
                //   created can be decremented for the appointment we're
                //   completing right now.
                await deductPackageIfAvailable(sel);
                setAppointments(as=>as.map(a=>a.id===sel.id?{...a,...updated}:a));
                setPaid(true);
              }}>Process ${total}</Btn>
            }
          </div>
        ):(
          <div style={{padding:"50px 18px",textAlign:"center",color:C.dim,fontSize:13}}>
            <Ic n="cash" s={30} c={C.light}/><div style={{marginTop:10}}>Select a client to checkout</div>
          </div>
        )}
      </Card>

      {/* ★ Refund modal — opens when the operator taps "↩ Refund" on a row */}
      {refundingPayment && <RefundModal payment={refundingPayment} onClose={()=>setRefundingPayment(null)}/>}

      {/* ★ Split payment — delegates to the full RecordPaymentModal (opened in
          split mode) so the sale is recorded as multiple tenders correctly. */}
      {splitCheckout && (
        <RecordPaymentModal
          client={{ id: splitCheckout.cid, name: splitCheckout.cn }}
          appointmentId={splitCheckout.id}
          defaultAmount={total}
          description={splitCheckout.treat || "POS checkout"}
          startSplit
          onClose={()=>setSplitCheckout(null)}
          onComplete={()=>{
            setSplitCheckout(null);
            setPaid(true);
            setAppointments(as=>as.map(a=>a.id===splitCheckout.id?{...a,paid:true,status:"completed",checkin:"checked-out"}:a));
          }}
        />
      )}
    </div>
  );
}

// ── CLIENTS EMR ───────────────────────────────────────────────────────────────
// ★ Pulls real signed consents from the DB for the selected client. Replaces
//   the hardcoded demo list that was hiding actual signatures (HIPAA, Filler
//   consent, Payment Auth, etc.) that staff and clients signed at check-in.
function ClientConsentsTab({ clientId }) {
  const [rows, setRows] = React.useState(null);
  // ★ Check-in questionnaires (yes/no intake answers) live in `checkins`,
  //   not in signed_consents — the profile never showed them until now.
  const [questionnaires, setQuestionnaires] = React.useState([]);
  React.useEffect(() => {
    if (!clientId) { setRows([]); setQuestionnaires([]); return; }
    let cancelled = false;
    (async () => {
      const [{ data }, { data: checkins }] = await Promise.all([
        supabase.from("signed_consents")
          .select("id,template_name,signed_at,appointment_id,signatures,answers,content,status")
          .eq("client_id", clientId)
          .order("signed_at", { ascending: false }),
        supabase.from("checkins")
          .select("id,appointment_id,visit_type,questionnaire_answers,checked_in_at")
          .eq("client_id", clientId)
          .order("checked_in_at", { ascending: false })
          .limit(50),
      ]);
      if (cancelled) return;
      setRows(data || []);
      setQuestionnaires((checkins || []).filter(c =>
        Array.isArray(c.questionnaire_answers) && c.questionnaire_answers.length > 0
        && c.questionnaire_answers.some(t => (t.answers || []).some(a => a.answer != null && a.answer !== ""))
      ));
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  if (rows === null) return <div style={{padding:20,textAlign:"center",color:C.muted,fontSize:12}}>Loading consents…</div>;

  // Render the Q&A pairs stored on a signed consent (template-embedded questions)
  const answersHtml = (answers) => {
    const entries = Object.entries(answers || {});
    if (!entries.length) return "";
    return `<div style="margin-top:18px"><div style="font-size:12px;font-weight:700;color:#333;margin-bottom:8px">Questions &amp; Answers</div>${entries.map(([k,v]) => `<div style="font-size:12px;padding:6px 10px;background:#f5f5f5;border-radius:6px;margin-bottom:4px"><strong>${String(k).replace(/</g,"&lt;")}:</strong> ${String(v).replace(/</g,"&lt;")}</div>`).join("")}</div>`;
  };

  return (
    <div>
      {rows.length === 0 ? (
        <div style={{padding:24,textAlign:"center",color:C.dim,fontSize:12,background:C.light,borderRadius:9,border:`1px dashed ${C.border}`,marginBottom:16}}>
          No signed consents on file yet for this patient. Consents signed at check-in show up here.
        </div>
      ) : (
      <>
      <div style={{fontSize:11,color:C.muted,marginBottom:10,fontWeight:600}}>{rows.length} signed consent{rows.length===1?"":"s"} on file</div>
      {rows.map(c => {
        const dt = c.signed_at ? new Date(c.signed_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—";
        const sig = c.signatures?.patient || c.signatures?.signature || null;
        const answerCount = Object.keys(c.answers || {}).length;
        return (
          <div key={c.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",background:C.light,borderRadius:9,border:`1px solid ${C.border}`,marginBottom:8,gap:10,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:180}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text}}>{c.template_name || "Consent"}</div>
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                Signed {dt}{c.appointment_id?` · for appt #${c.appointment_id}`:""}
                {answerCount > 0 && <span style={{marginLeft:6,padding:"1px 7px",background:"rgba(37,99,235,0.10)",color:"#2563eb",borderRadius:8,fontSize:10,fontWeight:700}}>{answerCount} answer{answerCount===1?"":"s"}</span>}
              </div>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <Bdg status="valid" label="e-Signed ✓"/>
              {sig && <button onClick={()=>{
                const w = window.open("", "_blank");
                if (!w) return;
                w.document.write(`<html><head><title>${c.template_name||"Consent"}</title></head><body style="font-family:system-ui;max-width:680px;margin:30px auto;padding:0 20px"><h2>${c.template_name||"Consent"}</h2><div style="font-size:12px;color:#666;margin-bottom:14px">Signed ${dt}</div><div style="white-space:pre-wrap;line-height:1.55;font-size:13px;border:1px solid #eee;padding:14px;border-radius:8px;background:#fafafa">${(c.content||"").replace(/</g,"&lt;")}</div>${answersHtml(c.answers)}${sig?`<div style="margin-top:18px"><div style="font-size:11px;color:#666;margin-bottom:6px">Signature:</div><img src="${sig}" alt="signature" style="max-width:320px;border:1px solid #ddd;border-radius:6px"/></div>`:""}</body></html>`);
                w.document.close();
              }} style={{padding:"5px 10px",background:"#fff",border:`1px solid ${C.borderGold}`,borderRadius:6,fontSize:10,fontWeight:700,color:C.goldD,cursor:"pointer",fontFamily:F}}>View</button>}
            </div>
          </div>
        );
      })}
      </>
      )}

      {/* ★ CHECK-IN QUESTIONNAIRES — yes/no intake answers captured during
          check-in (stored on the checkins row, separate from consents). */}
      {questionnaires.length > 0 && (
        <div style={{marginTop:20}}>
          <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>📋 Check-In Questionnaires</div>
          {questionnaires.map(ck => {
            const dt = ck.checked_in_at ? new Date(ck.checked_in_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—";
            return (
              <div key={ck.id} style={{padding:"12px 14px",background:C.light,borderRadius:9,border:`1px solid ${C.border}`,marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:6}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.text}}>
                    {dt}{ck.visit_type ? ` · ${ck.visit_type === "cosmetic" ? "✨ Cosmetic" : "💊 Health"} visit` : ""}{ck.appointment_id ? ` · appt #${ck.appointment_id}` : ""}
                  </div>
                </div>
                {(ck.questionnaire_answers || []).map((tpl, ti) => (
                  <div key={ti} style={{marginBottom:8}}>
                    {tpl.template_name && <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>{tpl.template_name}</div>}
                    {(tpl.answers || []).filter(a => a.answer != null && a.answer !== "").map((a, ai) => {
                      const isYes = /^yes/i.test(String(a.answer));
                      return (
                        <div key={ai} style={{display:"flex",justifyContent:"space-between",gap:10,padding:"7px 10px",background:"#fff",borderRadius:6,marginBottom:4,border:`1px solid ${isYes ? "rgba(192,57,43,0.25)" : C.border}`}}>
                          <span style={{fontSize:12,color:C.text,flex:1}}>{a.question}</span>
                          <span style={{fontSize:12,fontWeight:800,color:isYes ? C.red : C.green,flexShrink:0}}>{String(a.answer)}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Clients({clients,setClients,appointments, setAppointments, pendingClientFocus, clearPendingClientFocus, returnTo, onReturn}){
  const [smsClient, setSmsClient] = useState(null);
  const [walletClient, setWalletClient] = useState(null);
  const [chargeClient, setChargeClient] = useState(null);
  const [giftBuyer, setGiftBuyer] = useState(null); // ★ gift card bought for someone else (2026-10-22)
  const [recordPayClient, setRecordPayClient] = useState(null);
  const isMobile = useIsMobile();
  // iPad portrait (≤1024) is too cramped for the 360px list + EMR side-by-side.
  // We treat it like mobile — when a client is picked, the EMR takes the full
  // screen and the list disappears (with a Back button to return).
  const isCompact = useIsCompact();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sel,setSel] = useState(null);
  const [manageCards,setManageCards] = useState(false);
  const [tab,setTab] = useState("overview");
  const [showF,setShowF] = useState(false);
  const [form,setForm] = useState({});
  const [recordingAppt, setRecordingAppt] = useState(null);
  // ★ Custom / manual chart entry — staff opens this when they need to
  //   record a treatment that didn't go through the normal appointment
  //   flow (e.g. retroactive entry, treatment given elsewhere, historical
  //   record import). The modal collects {treatment, date, provider},
  //   creates a virtual appointment row marked source="manual_chart",
  //   then opens the bulletproof TreatmentRecord on it.
  const [addingChart, setAddingChart] = useState(false);
  const [manualChartForm, setManualChartForm] = useState({ treatment: "", date: new Date().toISOString().split("T")[0], provider_name: "", time: "12:00" });
  const [servicesList, setServicesList] = useState([]);
  // Dropdown visibility for the searchable treatment picker
  const [showTreatPicker, setShowTreatPicker] = useState(false);

  // Delete a charting note (clinical or post-care) off an appointment. Clears
  // just that field; markers/photos and the treatment_recorded flag are left
  // intact. Optimistic local update + DB write.
  const [deletingNote, setDeletingNote] = useState(null); // `${apptId}:${field}` while in flight
  const deleteNote = async (apptId, field) => {
    const label = field === "clinical_notes" ? "clinical note" : "post-care note";
    if (!window.confirm(`Delete this ${label}? This can't be undone.`)) return;
    setDeletingNote(`${apptId}:${field}`);
    const { error } = await supabase.from("appointments").update({ [field]: null }).eq("id", apptId);
    setDeletingNote(null);
    if (error) { alert("Could not delete the note: " + error.message); return; }
    setAppointments(as => as.map(a => a.id === apptId ? { ...a, [field]: null } : a));
  };

  // Delete a whole charting entry. Manual/custom entries (tagged [MANUAL_CHART])
  // exist only for charting, so we remove the row outright. Real appointments
  // carry billing/history, so the confirm spells out that the entire visit goes
  // away (recorded payments are NOT touched). Optimistic local removal.
  const [deletingEntry, setDeletingEntry] = useState(null);
  const deleteChartEntry = async (a) => {
    const isManual = (a.notes || "").includes("[MANUAL_CHART]");
    const msg = isManual
      ? `Delete this chart entry — "${a.treat}" (${a.date})? This can't be undone.`
      : `Delete the ENTIRE appointment "${a.treat}" on ${a.date}?\n\nIt will be removed from charting AND history. Payments already recorded are NOT deleted. This can't be undone.`;
    if (!window.confirm(msg)) return;
    setDeletingEntry(a.id);
    const { error } = await supabase.from("appointments").delete().eq("id", a.id);
    setDeletingEntry(null);
    if (error) { alert("Could not delete this entry: " + error.message); return; }
    setAppointments(as => as.filter(x => x.id !== a.id));
  };
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("services").select("id,name,category").eq("active", true).order("category").order("name");
      setServicesList(data || []);
    })();
  }, []);
  const [quickFilter, setQuickFilter] = useState("all"); // all | vip | gfe | card | recent | new
  const [sortBy, setSortBy] = useState("recent"); // recent | name | spent
  const [renderLimit, setRenderLimit] = useState(150); // virtualisation: render only first N

  // Debounce search input so we don't refilter 2,500 clients on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 150);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Pre-compute indexes ONCE per appointments change. Without these, every
  // sort comparison scans 5,600+ rows — multiply by 2,500 clients and the
  // page hangs.
  const apptsByClient = useMemo(() => {
    const map = new Map();
    (appointments || []).forEach(a => {
      if (!a?.cid) return;
      const list = map.get(a.cid) || [];
      list.push(a);
      map.set(a.cid, list);
    });
    return map;
  }, [appointments]);

  // ★ Waitlist lookup — Set of client IDs currently on the waitlist (status
  // = "waiting"). Used to show a ⏳ ON WAITLIST badge across the client list
  // + EMR + anywhere a client name is rendered. Realtime-synced.
  const [waitlistedClientIds, setWaitlistedClientIds] = useState(new Set());
  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from("waitlist_entries")
        .select("client_id").eq("status", "waiting");
      setWaitlistedClientIds(new Set((data || []).map(r => r.client_id).filter(Boolean)));
    };
    load();
    const ch = supabase.channel("waitlist-client-tags")
      .on("postgres_changes", { event:"*", schema:"public", table:"waitlist_entries" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);


  const lastApptDateByClient = useMemo(() => {
    const map = new Map();
    for (const [cid, list] of apptsByClient) {
      let latest = "";
      for (const a of list) if (a.date && a.date > latest) latest = a.date;
      if (latest) map.set(cid, latest);
    }
    return map;
  }, [apptsByClient]);

  const recentClientIds = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    const cutoffStr = cutoff.toISOString().split("T")[0];
    const set = new Set();
    for (const [cid, lastDate] of lastApptDateByClient) {
      if (lastDate >= cutoffStr) set.add(cid);
    }
    return set;
  }, [lastApptDateByClient]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = clients.filter(c => {
      if (q) {
        const hay = ((c.name||"") + " " + (c.phone||"") + " " + (c.email||"")).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (quickFilter === "vip") return c.memberPlan || (c.spent || 0) >= 1500;
      if (quickFilter === "gfe") return c.gfeStatus === "expired" || c.gfeStatus === "pending";
      if (quickFilter === "card") return c.cardOnFile;
      if (quickFilter === "recent") return recentClientIds.has(c.id);
      if (quickFilter === "new") return (c.visits || 0) <= 1;
      return true;
    });
    list.sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "spent") return (b.spent || 0) - (a.spent || 0);
      // recent — uses the pre-computed map, no per-compare scans of appts
      const lastA = lastApptDateByClient.get(a.id) || "";
      const lastB = lastApptDateByClient.get(b.id) || "";
      if (lastA !== lastB) return lastB.localeCompare(lastA);
      return (b.id || 0) - (a.id || 0);
    });
    return list;
  }, [clients, search, quickFilter, sortBy, recentClientIds, lastApptDateByClient]);

  // Reset render limit when filter/search changes — show top-150 of new results
  useEffect(() => { setRenderLimit(150); }, [search, quickFilter, sortBy]);

  // Cheap lookup that uses the index instead of scanning the appointments array
  const cAppts = (id) => apptsByClient.get(id) || [];

  // Auto-open a client if the user navigated here via "View Client" shortcut
  const [navMissError, setNavMissError] = useState(null);
  useEffect(() => {
    if (!pendingClientFocus) return;
    let cancelled = false;
    (async () => {
      // Compare loosely: cid arrives from an appointment row and can be a
      // string here and a number there.
      const target = clients.find(c => String(c.id) === String(pendingClientFocus));
      if (target) {
        setSel(target);
        setTab("overview");
        setNavMissError(null);
        clearPendingClientFocus && clearPendingClientFocus();
        return;
      }
      // ★ Not in the loaded list — that does NOT mean it does not exist.
      //   The old code assumed it did and showed 'client not found', which is
      //   what made every bot-booked appointment look like it had no profile.
      //   The bot creates the client properly; the row simply arrived after
      //   this page loaded. Ask the database before giving up.
      const { data: fetched } = await supabase.from("clients")
        .select("*").eq("id", pendingClientFocus).maybeSingle();
      if (cancelled) return;
      if (fetched) {
        const norm = {...fetched, cardOnFile: fetched.card_on_file, gfeStatus: fetched.gfe_status, gfeExpiry: fetched.gfe_expiry, loyaltyPts: fetched.loyalty_pts, memberPlan: fetched.member_plan};
        setClients(cs => cs.some(c => String(c.id) === String(norm.id)) ? cs : [...cs, norm]);
        setSel(norm);
        setTab("overview");
        setNavMissError(null);
      } else {
        const orphan = (appointments || []).find(a => String(a.cid) === String(pendingClientFocus));
        setNavMissError({
          missing_client_id: pendingClientFocus,
          appointment_name: orphan?.cn || null,
          appointment_phone: null,
        });
      }
      clearPendingClientFocus && clearPendingClientFocus();
    })();
    return () => { cancelled = true; };
  }, [pendingClientFocus, clients]);

  const counts = {
    all: clients.length,
    recent: clients.filter(c => recentClientIds.has(c.id)).length,
    vip: clients.filter(c => c.memberPlan || (c.spent || 0) >= 1500).length,
    gfe: clients.filter(c => c.gfeStatus === "expired" || c.gfeStatus === "pending").length,
    card: clients.filter(c => c.cardOnFile).length,
    new: clients.filter(c => (c.visits || 0) <= 1).length,
  };

  return (
    <div style={{
      display:"grid",
      // List + EMR side-by-side only on real desktop (>1024px). On iPad
      // portrait and phones, we stack: show list OR EMR (full width), with a
      // Back button to return to the list.
      gridTemplateColumns: sel && !isCompact ? "360px 1fr" : "1fr",
      gap:20,
    }}>
      {/* List column — hidden when a client is selected on compact screens */}
      {(!sel || !isCompact) && (
      <div>
        {/* Header: title + count + new button */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:14,flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontSize:22,fontWeight:700,color:C.text,fontFamily:S,letterSpacing:"-0.01em",lineHeight:1}}>Clients</div>
            <div style={{fontSize:12,color:C.muted,marginTop:3}}>{filtered.length} of {clients.length} shown</div>
          </div>
          <Btn icon="plus" onClick={()=>{setForm({name:"",phone:"",email:"",dob:"",allergies:"",med:"No contraindications",notes:"",loc:"Nova Med Spa",status:"active",cardOnFile:false,gfeStatus:"pending",loyaltyPts:0,memberPlan:null,tags:[]});setShowF(true);}}>New Client</Btn>
        </div>

        {/* Navigation miss banner */}
        {navMissError && (
          <div style={{padding:"12px 14px",background:C.redL,border:"1px solid "+C.red+"40",borderRadius:10,marginBottom:12,fontSize:12,color:C.red,lineHeight:1.5}}>
            <strong>{navMissError.appointment_name || "That client"}</strong> isn't in your Clients list yet (likely AI booked the appointment before saving full details). The appointment is real — but the client record is missing.{" "}
            <button onClick={()=>setNavMissError(null)} style={{background:"none",border:"none",color:C.red,fontWeight:700,cursor:"pointer",textDecoration:"underline",padding:0,marginLeft:4}}>Dismiss</button>
          </div>
        )}

        {/* Search + filters */}
        <div style={{position:"relative",marginBottom:10}}>
          <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",color:C.dim,pointerEvents:"none"}}><Ic n="srch" s={16}/></span>
          <input
            value={searchInput}
            onChange={e=>setSearchInput(e.target.value)}
            placeholder="Search by name, phone, or email…"
            style={{width:"100%",padding:"12px 14px 12px 42px",background:"#fff",border:"1.5px solid "+C.border,borderRadius:12,fontSize:14,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box",boxShadow:"0 1px 3px rgba(0,0,0,0.04)"}}
            onFocus={e=>{e.target.style.borderColor=C.gold;e.target.style.boxShadow="0 0 0 3px "+C.goldBg;}}
            onBlur={e=>{e.target.style.borderColor=C.border;e.target.style.boxShadow="0 1px 3px rgba(0,0,0,0.04)";}}
          />
          {searchInput && (
            <button onClick={()=>{setSearchInput("");setSearch("");}} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:C.light,border:"none",borderRadius:99,width:24,height:24,cursor:"pointer",color:C.muted,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
          )}
        </div>

        {/* Quick filter chips */}
        <div style={{display:"flex",gap:5,overflowX:"auto",marginBottom:14,paddingBottom:4}}>
          {[
            ["all", "All", counts.all],
            ["recent", "Recent", counts.recent],
            ["vip", "VIP", counts.vip],
            ["gfe", "GFE", counts.gfe],
            ["card", "Card", counts.card],
            ["new", "New", counts.new],
          ].map(([key, label, count]) => (
            <button key={key} onClick={()=>setQuickFilter(key)} style={{
              padding:"6px 12px",
              fontSize:11.5,
              fontWeight:700,
              color:quickFilter===key?"#fff":C.muted,
              background:quickFilter===key?"linear-gradient(135deg, "+C.gold+", "+C.goldD+")":"#fff",
              border:"1px solid "+(quickFilter===key?C.gold:C.border),
              borderRadius:99,
              cursor:"pointer",
              fontFamily:F,
              whiteSpace:"nowrap",
              transition:"all 0.15s",
              boxShadow:quickFilter===key?"0 2px 6px "+C.gold+"30":"none",
            }}>
              {label}
              <span style={{marginLeft:5,opacity:0.7,fontWeight:500,fontSize:10}}>{count}</span>
            </button>
          ))}
          <div style={{flex:1,minWidth:8}}/>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{padding:"6px 10px",fontSize:11,fontWeight:600,color:C.muted,background:"#fff",border:"1px solid "+C.border,borderRadius:99,cursor:"pointer",fontFamily:F,outline:"none"}}>
            <option value="recent">Sort: Recent</option>
            <option value="name">Sort: A–Z</option>
            <option value="spent">Sort: Top Spender</option>
          </select>
        </div>

        {/* Client list */}
        {filtered.length === 0 ? (
          <div style={{padding:36,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:12}}>
            <div style={{fontSize:36,marginBottom:6,opacity:0.3}}>🔍</div>
            <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>No clients match</div>
            <div style={{fontSize:11,color:C.muted}}>{search ? "Try a different search term" : "Try a different filter, or add a new client"}</div>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:7,maxHeight:"calc(100vh - 280px)",overflowY:"auto",paddingRight:4}}>
            {filtered.slice(0, renderLimit).map(c=>(
              <div key={c.id} onClick={()=>{setSel(c===sel?null:c);setTab("overview");}}
                style={{
                  padding:"14px 16px",
                  background:sel?.id===c.id?"linear-gradient(135deg, "+C.goldBg+", #fff)":C.card,
                  border:"1.5px solid "+(sel?.id===c.id?C.gold:C.border),
                  borderRadius:12,
                  cursor:"pointer",
                  transition:"all 0.18s ease",
                  boxShadow:sel?.id===c.id?"0 4px 14px "+C.gold+"20":"0 1px 3px rgba(0,0,0,0.04)",
                }}
                onMouseEnter={e=>{if(sel?.id!==c.id){e.currentTarget.style.borderColor=C.borderGold;e.currentTarget.style.transform="translateX(2px)";}}}
                onMouseLeave={e=>{if(sel?.id!==c.id){e.currentTarget.style.borderColor=C.border;e.currentTarget.style.transform="translateX(0)";}}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div style={{display:"flex",gap:11,alignItems:"center",minWidth:0,flex:1}}>
                  <div style={{width:42,height:42,borderRadius:11,background:"linear-gradient(135deg, "+C.goldBg+", "+C.gold+"15)",border:"1px solid "+C.borderGold,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:C.goldD,flexShrink:0,fontFamily:S}}>{(c.name||"?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}</div>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{fontSize:14.5,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.name}</div>
                    {/* ★ Show BOTH phone + email when both exist so staff can pick how to contact at a glance */}
                    {c.phone && <div style={{fontSize:11.5,color:C.gold,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginTop:1}}>📞 {c.phone}</div>}
                    {c.email && <div style={{fontSize:11,color:C.muted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>📧 {c.email}</div>}
                    {!c.phone && !c.email && <div style={{fontSize:11.5,color:C.dim,fontStyle:"italic",marginTop:1}}>no contact on file</div>}
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
                  <div style={{fontSize:14,fontWeight:800,color:C.gold,fontFamily:S}}>${(c.spent||0).toLocaleString()}</div>
                  <div style={{fontSize:10,color:C.dim,marginTop:1}}>{c.visits||0} visit{(c.visits||0)!==1?"s":""}</div>
                </div>
              </div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {waitlistedClientIds.has(c.id) && (
                  <span style={{fontSize:10,padding:"2px 8px",background:"linear-gradient(135deg,#b48c3c,#8a6f2e)",color:"#fff",borderRadius:50,fontWeight:800,letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>⏳ On Waitlist</span>
                )}
                {c.status==="hold"&&<Bdg status="hold" label="⚠️ Hold"/>}
                {c.gfeStatus==="expired"&&<Bdg status="expired" label="GFE Expired"/>}
                {c.gfeStatus==="pending"&&<Bdg status="pending" label="GFE Needed"/>}
                {c.cardOnFile&&<Bdg status="active" label="💳 Card"/>}
                {c.memberPlan&&<Bdg status="active" label={c.memberPlan}/>}
              </div>
            </div>
          ))}
          {filtered.length > renderLimit && (
            <button onClick={()=>setRenderLimit(n=>n+250)} style={{padding:"12px 16px",marginTop:6,background:C.light,border:"1px dashed "+C.borderGold,borderRadius:10,color:C.gold,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,letterSpacing:"0.04em"}}>
              ⬇ Show {Math.min(250, filtered.length-renderLimit)} more · ({renderLimit} of {filtered.length} shown)
            </button>
          )}
          </div>
        )}
      </div>
      )}

      {sel&&(
        <Card style={{display:"flex",flexDirection:"column",maxHeight:isCompact?"none":"80vh",overflow:"hidden"}}>
          {/* ★ Back buttons — always shown when this profile was opened from
              an appointment (so iPad/phone users can get back to the same
              calendar day in one tap). The standard "Back to Patients" is
              only shown on compact like before. */}
          {(isCompact || returnTo?.tab === "appointments") && (
            <div style={{display:"flex",gap:8,margin:"12px 12px 0",flexWrap:"wrap"}}>
              {returnTo?.tab === "appointments" && (
                <button onClick={() => { setSel(null); onReturn && onReturn(); }} style={{
                  padding:"10px 14px",
                  background: C.gold,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: F,
                  minHeight: 44,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}>← Back to Appointments</button>
              )}
              {isCompact && (
                <button onClick={()=>setSel(null)} style={{
                  padding:"10px 14px",
                  background:C.surface,
                  border:`1px solid ${C.border}`,
                  borderRadius:8,
                  fontSize:13,
                  fontWeight:700,
                  color:C.gold,
                  cursor:"pointer",
                  fontFamily:F,
                  minHeight:44,
                }}>← Back to Patients</button>
              )}
            </div>
          )}
          <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",gap:12,alignItems:"center",flexShrink:0,background:C.goldBg,flexWrap:"wrap"}}>
            <div style={{width:44,height:44,borderRadius:10,background:C.goldBg,border:`2px solid ${C.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:C.gold,flexShrink:0}}>{(sel.name||"?").split(" ").map(n=>n[0]).join("")}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:15,fontWeight:800,color:C.text,fontFamily:S,marginBottom:3,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                {sel.name}
                {waitlistedClientIds.has(sel.id) && (
                  <span style={{fontSize:10,padding:"2px 9px",background:"linear-gradient(135deg,#b48c3c,#8a6f2e)",color:"#fff",borderRadius:50,fontWeight:800,letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>⏳ On Waitlist</span>
                )}
              </div>
              {/* ★ Show only the fields that exist, gold-tinted phone for easy spotting */}
              <div style={{fontSize:11,color:C.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",gap:10,flexWrap:"wrap"}}>
                {sel.phone && <a href={`tel:${String(sel.phone).replace(/[^\d+]/g,"")}`} style={{color:C.gold,fontWeight:600,textDecoration:"none"}}>📞 {sel.phone}</a>}
                {sel.email && <span>📧 {sel.email}</span>}
                {!sel.phone && !sel.email && <span style={{color:C.dim,fontStyle:"italic"}}>No contact on file — add via Edit</span>}
              </div>
            </div>
            {/* Action buttons — wrap to next line if the iPad/phone runs out of width */}
            <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:isCompact?"flex-start":"flex-end",width:isCompact?"100%":"auto"}}>
              <Btn v="sec" sz="sm" icon="edit" onClick={()=>{setForm({...sel});setShowF(true);}}>Edit</Btn>
              <Btn v="sec" sz="sm" onClick={()=>setSmsClient(sel)}>💬 SMS</Btn>
              <Btn v="sec" sz="sm" onClick={()=>setWalletClient(sel)}>💼 Wallet</Btn>
              <Btn v="sec" sz="sm" onClick={()=>setChargeClient(sel)}>💳 Charge</Btn>
              <Btn v="sec" sz="sm" onClick={()=>setGiftBuyer(sel)}>🎁 Gift Card</Btn>
              <Btn v="sec" sz="sm" onClick={()=>setRecordPayClient(sel)}>📋 Record</Btn>
              <PortalShare client={sel}/>
              {!isCompact && (
                <button onClick={()=>setSel(null)} style={{background:C.light,border:"none",borderRadius:7,padding:5,cursor:"pointer",color:C.muted,display:"flex",minWidth:36,minHeight:36,alignItems:"center",justifyContent:"center"}}><Ic n="x" s={14}/></button>
              )}
            </div>
          </div>

          <div style={{display:"flex",gap:0,padding:"0 20px",borderBottom:`1px solid ${C.border}`,flexShrink:0,overflowX:"auto"}}>
            {[["overview","Overview"],["history","History"],["billing","💳 Billing"],["invoices","🧾 Invoices"],["charting","Charting"],["photos","Photos"],["documents","📄 Documents"],["forms","Consents"],["fullhistory","Full Log"]].map(([id,lbl])=>(
              <button key={id} onClick={()=>setTab(id)} style={{padding:"9px 14px",fontSize:12,fontWeight:tab===id?700:500,color:tab===id?C.gold:C.muted,background:"none",border:"none",borderBottom:`2px solid ${tab===id?C.gold:"transparent"}`,cursor:"pointer",fontFamily:F,marginBottom:-1,whiteSpace:"nowrap"}}>{lbl}</button>
            ))}
          </div>

          <div style={{flex:1,overflow:"auto",padding:20}}>
            {tab==="overview"&&(
              <div style={{display:"flex",flexDirection:"column",gap:16}}>
                {/* Upcoming appointments — prominent at the top */}
                {(() => {
                  const today = new Date().toISOString().split("T")[0];
                  const upcoming = cAppts(sel.id)
                    .filter(a => a.date >= today && a.status !== "cancelled" && a.status !== "no-show" && a.checkin !== "checked-out")
                    .sort((a,b) => (a.date+a.time).localeCompare(b.date+b.time));
                  return (
                    <div style={{padding:16,background:upcoming.length>0?"linear-gradient(135deg,"+C.goldBg+", #fff)":C.light,border:"1.5px solid "+(upcoming.length>0?C.borderGold:C.border),borderRadius:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:upcoming.length>0?10:0,flexWrap:"wrap",gap:8}}>
                        <div style={{fontSize:13,fontWeight:800,color:C.gold,fontFamily:S,letterSpacing:"0.02em"}}>📅 Upcoming Appointments ({upcoming.length})</div>
                        {upcoming.length === 0 && <div style={{fontSize:11,color:C.muted}}>No upcoming visits</div>}
                      </div>
                      {upcoming.map(a => (
                        <div key={a.id} style={{padding:"10px 12px",background:"#fff",borderRadius:9,border:"1px solid "+C.border,marginBottom:6,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                          <div style={{fontSize:12,fontWeight:700,color:C.gold,minWidth:84}}>{fmtAppointmentDate(a.date,{month:"short",day:"numeric"})}</div>
                          <div style={{fontSize:11,color:C.muted,minWidth:64}}>{format12Hour(a.time)}</div>
                          <div style={{flex:1,minWidth:140}}>
                            <div style={{fontSize:13,fontWeight:700,color:C.text}}>{a.treat}</div>
                            <div style={{fontSize:11,color:C.muted}}>with {a.prov || "—"} · {a.dur || 60}m{a.room?" · "+a.room:""}</div>
                          </div>
                          <Bdg status={a.checkin||a.status||"upcoming"}/>
                          {a.needs_verbal_confirmation && !a.verbal_confirmed_at && (
                            <span style={{fontSize:9,padding:"2px 6px",background:C.orangeL,color:C.orange,borderRadius:99,fontWeight:700,letterSpacing:"0.04em"}}>📞 NEEDS CONFIRM</span>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
                  <div>
                    {[["Phone",sel.phone],["Email",sel.email],["DOB",sel.dob],["Location",sel.loc],["Last Visit",sel.last],["Visits",sel.visits],["Lifetime Value",`$${sel.spent?.toLocaleString()}`],["Loyalty Points",`${sel.loyaltyPts||0} pts`],["Card on File",sel.cardOnFile?"✓ Yes":"No"]].map(([k,v])=>(
                      <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                        <span style={{color:C.muted}}>{k}</span>
                        <span style={{color:k==="Lifetime Value"?C.gold:k==="Card on File"&&sel.cardOnFile?C.green:C.text,fontWeight:["Lifetime Value","Loyalty Points"].includes(k)?700:400}}>{v}{k==="Card on File"&&<button onClick={()=>setManageCards(true)} style={{marginLeft:8,padding:"2px 9px",fontSize:10.5,fontWeight:700,background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:6,cursor:"pointer",fontFamily:F}}>Manage</button>}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{padding:12,background:sel.med?.includes("⚠️")?C.redL:C.light,borderRadius:9,marginBottom:10,border:`1px solid ${sel.med?.includes("⚠️")?"rgba(192,57,43,0.2)":C.border}`}}>
                      <div style={{fontSize:10,fontWeight:700,color:sel.med?.includes("⚠️")?C.red:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Medical History</div>
                      <div style={{fontSize:13,color:sel.med?.includes("⚠️")?C.red:C.text}}>{sel.med}</div>
                    </div>
                    <div style={{padding:12,background:C.light,borderRadius:9,border:`1px solid ${C.border}`}}>
                      <div style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Allergies</div>
                      <div style={{fontSize:13,color:C.text}}>{sel.allergies}</div>
                    </div>
                    {manageCards && <ManageCardsModal client={sel} onClose={()=>setManageCards(false)} onChanged={(has)=>{
                      setSel(s=>s&&s.id===sel.id?{...s,cardOnFile:has,card_on_file:has}:s);
                      setClients(cs=>cs.map(c=>c.id===sel.id?{...c,cardOnFile:has,card_on_file:has}:c));
                    }}/>}
                    {/* Active packages widget */}
                    <ClientPackagesCompact clientId={sel.id}/>
                  </div>
                </div>
              </div>
            )}

            {tab==="history"&&(
              <div>
                {cAppts(sel.id).length===0&&<div style={{padding:"30px",textAlign:"center",color:C.dim}}>No appointments yet</div>}
                <div style={{position:"relative"}}>
                  <div style={{position:"absolute",left:14,top:0,bottom:0,width:2,background:C.border}}/>
                  {cAppts(sel.id).map(a=>(
                    <div key={a.id} style={{display:"flex",gap:14,marginBottom:14,paddingLeft:36,position:"relative"}}>
                      <div style={{position:"absolute",left:8,top:8,width:12,height:12,borderRadius:"50%",background:(SC[a.status]?.c)||C.gold,border:`2px solid ${C.surface}`,zIndex:1}}/>
                      <div style={{flex:1,padding:"11px 14px",background:C.light,borderRadius:9,border:`1px solid ${C.border}`,transition:"background 0.15s"}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                          <div style={{fontSize:13,fontWeight:600,color:C.text}}>{a.treat}</div>
                          <div style={{fontSize:13,fontWeight:700,color:C.gold}}>${a.price}</div>
                        </div>
                        <div style={{fontSize:11,color:C.muted,marginBottom:8}}>{a.date} · {a.prov}</div>
                        {/* ★ Appointment notes in History (owner request 2026-10-14). Staff
                            notes show prominently; system tags like "[RESCHEDULED … by Ruby]"
                            are listed smaller underneath. */}
                        {String(a.notes||"").trim() && (()=>{
                          const raw = String(a.notes);
                          const sys = raw.match(/\[[A-Z][A-Z _-]{3,}[^\]]*\]/g) || [];
                          const text = sys.reduce((s,t)=>s.replace(t,""),raw).replace(/\s{2,}/g," ").trim();
                          return (
                            <div style={{marginBottom:8,padding:"8px 10px",background:C.surface,border:`1px solid ${C.border}`,borderLeft:`3px solid ${C.gold}`,borderRadius:6}}>
                              {text && <div style={{fontSize:12,color:C.text,whiteSpace:"pre-wrap",lineHeight:1.45}}>📝 {text}</div>}
                              {sys.map((t,i)=>(
                                <div key={i} style={{fontSize:10.5,color:C.muted,marginTop:text||i?4:0}}>{t.slice(1,-1)}</div>
                              ))}
                            </div>
                          );
                        })()}
                        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                          <Bdg status={a.status}/>
                          {a.treatment_recorded && <Bdg status="active" label="✓ Treatment Recorded"/>}
                          <button onClick={()=>setRecordingAppt(a)} style={{padding:"5px 11px",background:a.treatment_recorded?C.greenL:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:a.treatment_recorded?C.green:"#fff",border:a.treatment_recorded?`1px solid ${C.green}40`:"none",borderRadius:6,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:F,marginLeft:"auto"}}>
                            {a.treatment_recorded ? "📋 View/Edit Record" : "✍️ Record Treatment"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab==="charting"&&(
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:10,flexWrap:"wrap"}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S}}>Treatment Records</div>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <div style={{fontSize:11,color:C.muted}}>{cAppts(sel.id).filter(a=>a.treatment_recorded).length} of {cAppts(sel.id).length} visits charted</div>
                    <button onClick={()=>{
                      // Reset the form to defaults whenever the modal opens
                      setManualChartForm({ treatment: "", date: new Date().toISOString().split("T")[0], provider_name: "", time: "12:00" });
                      setAddingChart(true);
                    }} style={{padding:"7px 14px",background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>
                      ＋ Add Custom Chart
                    </button>
                  </div>
                </div>
                {cAppts(sel.id).filter(a=>{
                  // Show appointments where charting is meaningful: already
                  // recorded, marked completed, OR currently in-progress so
                  // staff can chart in real time while the client is in the
                  // room. Without "waiting"/"in-progress" the chart tab was
                  // empty for clients who'd just checked in.
                  // ★ ALSO show any visit whose date is TODAY or earlier and
                  //   isn't cancelled — this fixes AI-booked appointments
                  //   (status "confirmed" / checkin "upcoming"): the client is
                  //   a real client, but their bot appt never becomes
                  //   "checked-in", so the nurse couldn't chart it. Now any
                  //   past/today appointment is chartable regardless of who
                  //   booked it. Future appts stay hidden until their day.
                  const todayStr = new Date().toLocaleDateString("en-CA");
                  return (
                    a.status==="completed"
                    || a.treatment_recorded
                    || a.checkin==="waiting"
                    || a.checkin==="in-treatment"   // ★ the REAL in-progress status in this app
                    || a.checkin==="checked-out"
                    || a.checkin==="in-progress"
                    || a.checkin==="checked-in"
                    || (a.date && a.date <= todayStr && a.status!=="cancelled")
                  );
                }).map(a=>{
                  const markers = a.chart_markers || [];
                  const byProduct = {};
                  markers.forEach(m=>{
                    if(!byProduct[m.product]) byProduct[m.product] = {color:m.color,total:0,unit:m.unit,count:0};
                    byProduct[m.product].total += +m.amount||0;
                    byProduct[m.product].count++;
                  });
                  return (
                    <div key={a.id} style={{marginBottom:14,padding:14,background:a.treatment_recorded?C.light:"rgba(180,140,60,0.04)",borderRadius:9,border:`1px solid ${a.treatment_recorded?C.border:C.borderGold}`,borderLeft:a.treatment_recorded?`3px solid ${C.green}`:`3px dashed ${C.gold}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10,gap:10,flexWrap:"wrap"}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:C.text}}>{a.treat}</div>
                          <div style={{fontSize:11,color:C.muted}}>{a.date}{a.time?` · ${a.time}`:""}{a.provider_name?` · 👤 ${a.provider_name}`:a.prov?` · ${a.prov}`:""}</div>
                        </div>
                        <div style={{display:"flex",gap:6,alignItems:"center"}}>
                          <button onClick={()=>setRecordingAppt(a)} style={{padding:"6px 12px",background:a.treatment_recorded?"#fff":`linear-gradient(135deg,${C.gold},${C.goldD})`,color:a.treatment_recorded?C.gold:"#fff",border:a.treatment_recorded?`1px solid ${C.borderGold}`:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>
                            {a.treatment_recorded?"📋 View/Edit":"✍️ Record Treatment"}
                          </button>
                          <button onClick={()=>deleteChartEntry(a)} disabled={deletingEntry===a.id} title="Delete this charting entry" style={{padding:"6px 10px",background:"#fff",color:C.red,border:`1px solid ${C.red}40`,borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>
                            {deletingEntry===a.id?"…":"🗑"}
                          </button>
                        </div>
                      </div>
                      {!a.treatment_recorded && (
                        <div style={{padding:"10px 12px",background:"#fff",borderRadius:7,fontSize:11,color:C.muted,fontStyle:"italic"}}>
                          No treatment recorded yet. Click "Record Treatment" to add chart markers, photos, and aftercare notes.
                        </div>
                      )}
                      {a.treatment_recorded && (
                        <>
                          {Object.keys(byProduct).length>0 && (
                            <div style={{marginBottom:10}}>
                              <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",marginBottom:6}}>Products Used ({markers.length} sites)</div>
                              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                {Object.entries(byProduct).map(([name,info])=>(
                                  <div key={name} style={{padding:"6px 12px",background:"#fff",border:`1.5px solid ${info.color}`,borderRadius:50,fontSize:11,fontWeight:600,color:C.text,display:"flex",alignItems:"center",gap:6}}>
                                    <span style={{width:9,height:9,borderRadius:"50%",background:info.color}}/>
                                    <span>{name}</span>
                                    <span style={{fontWeight:800,color:info.color}}>{info.total} {info.unit}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {a.clinical_notes && (
                            <div style={{padding:"10px 12px",background:"#fff",borderRadius:7,marginBottom:8,borderLeft:`3px solid ${C.red}`}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4,gap:8}}>
                                <div style={{fontSize:9,fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:"0.05em"}}>📝 Clinical Notes (staff only)</div>
                                <button onClick={()=>deleteNote(a.id,"clinical_notes")} disabled={deletingNote===`${a.id}:clinical_notes`} title="Delete this note" style={{padding:"2px 8px",background:"#fff",color:C.red,border:`1px solid ${C.red}40`,borderRadius:5,fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>{deletingNote===`${a.id}:clinical_notes`?"…":"🗑 Delete"}</button>
                              </div>
                              <div style={{fontSize:12,color:C.text,whiteSpace:"pre-wrap",lineHeight:1.5}}>{a.clinical_notes}</div>
                            </div>
                          )}
                          {a.post_care_notes && (
                            <div style={{padding:"10px 12px",background:"#fff",borderRadius:7,borderLeft:`3px solid ${C.green}`}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4,gap:8}}>
                                <div style={{fontSize:9,fontWeight:700,color:C.green,textTransform:"uppercase",letterSpacing:"0.05em"}}>💖 Post-Care (visible to client)</div>
                                <button onClick={()=>deleteNote(a.id,"post_care_notes")} disabled={deletingNote===`${a.id}:post_care_notes`} title="Delete this note" style={{padding:"2px 8px",background:"#fff",color:C.green,border:`1px solid ${C.green}40`,borderRadius:5,fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>{deletingNote===`${a.id}:post_care_notes`?"…":"🗑 Delete"}</button>
                              </div>
                              <div style={{fontSize:12,color:C.text,whiteSpace:"pre-wrap",lineHeight:1.5}}>{a.post_care_notes}</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
                {(()=>{ const todayStr=new Date().toLocaleDateString("en-CA"); return cAppts(sel.id).filter(a=>a.status==="completed"||a.treatment_recorded||a.checkin==="waiting"||a.checkin==="in-treatment"||a.checkin==="checked-out"||a.checkin==="in-progress"||a.checkin==="checked-in"||(a.date&&a.date<=todayStr&&a.status!=="cancelled")).length===0; })()&&<div style={{padding:"30px",textAlign:"center",color:C.dim}}>No visits to chart yet. Charting opens on a visit's date — or use “＋ Add Custom Chart” to record one now.</div>}
              </div>
            )}

            {tab==="billing"&&(
              <BillingTabContent client={sel} appointments={cAppts(sel.id)}/>
            )}

            {tab==="invoices"&&(
              <InvoicesTab client={sel}/>
            )}

            {tab==="photos"&&(
              <PhotosTabContent client={sel} appointments={cAppts(sel.id)} onRecordTreatment={setRecordingAppt}/>
            )}

            {tab==="fullhistory"&&<ClientHistory client={sel} />}

            {tab==="documents"&&<ClientDocuments clientId={sel?.id}/>}
            {tab==="forms"&&<ClientConsentsTab clientId={sel?.id}/>}
          </div>
        </Card>
      )}

      {smsClient && <SMSModal client={smsClient} onClose={()=>setSmsClient(null)} />}
      {recordingAppt && sel && <TreatmentRecord appointment={recordingAppt} client={sel} onClose={()=>setRecordingAppt(null)} onSaved={async ()=>{
        const { data } = await supabase.from("appointments").select("*").eq("id", recordingAppt.id).single();
        if (data) setRecordingAppt(data);
      }}/>}

      {/* ★ Custom / manual chart entry modal — used when staff needs to
          record a treatment that didn't go through the normal appointment
          flow (retroactive, given elsewhere, historical import). */}
      {addingChart && sel && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:480,padding:24,maxHeight:"92vh",overflowY:"auto"}}>
            <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:4}}>＋ Add Custom Chart</div>
            <div style={{fontSize:11,color:C.muted,marginBottom:18}}>For {sel.name} — use this for retroactive entries or treatments performed outside a scheduled appointment.</div>

            <div style={{marginBottom:12,position:"relative"}}>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Treatment</label>
              <input
                value={manualChartForm.treatment}
                onChange={e=>{ setManualChartForm({...manualChartForm, treatment: e.target.value}); setShowTreatPicker(true); }}
                onFocus={()=>setShowTreatPicker(true)}
                onBlur={()=>setTimeout(()=>setShowTreatPicker(false), 150)}
                placeholder="Type to search — Botox, Facial, IV Therapy..."
                autoComplete="off"
                style={{width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"}}
              />
              {showTreatPicker && (() => {
                const q = (manualChartForm.treatment || "").toLowerCase().trim();
                // ★ When browsing (no search), show the FULL list (scrollable) —
                //   not just the first 12, which were all Facials because the list
                //   is sorted by category. When searching, cap at 12 matches.
                const matches = servicesList.filter(s =>
                  !q || (s.name || "").toLowerCase().includes(q) || (s.category || "").toLowerCase().includes(q)
                ).slice(0, q ? 12 : servicesList.length);
                if (matches.length === 0) return null;
                return (
                  <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:10,background:"#fff",border:"1px solid "+C.border,borderRadius:8,marginTop:4,boxShadow:"0 4px 16px rgba(0,0,0,0.08)",maxHeight:240,overflowY:"auto"}}>
                    {matches.map(s => (
                      <div
                        key={s.id}
                        onMouseDown={(e)=>{ e.preventDefault(); setManualChartForm({...manualChartForm, treatment: s.name}); setShowTreatPicker(false); }}
                        style={{padding:"10px 12px",cursor:"pointer",fontSize:13,color:C.text,borderBottom:"1px solid "+C.border,display:"flex",justifyContent:"space-between",alignItems:"center"}}
                        onMouseEnter={(e)=>{ e.currentTarget.style.background = C.goldBg; }}
                        onMouseLeave={(e)=>{ e.currentTarget.style.background = "#fff"; }}
                      >
                        <span style={{fontWeight:600}}>{s.name}</span>
                        {s.category && <span style={{fontSize:10,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em"}}>{s.category}</span>}
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div style={{fontSize:10,color:C.muted,marginTop:5,fontStyle:"italic"}}>Pick from the list or type your own. Used to match against the wallet for auto-deduct.</div>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Date</label>
                <input type="date" value={manualChartForm.date} onChange={e=>setManualChartForm({...manualChartForm, date: e.target.value})} style={{width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Time</label>
                <input type="time" value={manualChartForm.time} onChange={e=>setManualChartForm({...manualChartForm, time: e.target.value})} style={{width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
              </div>
            </div>

            <div style={{marginBottom:18}}>
              <label style={{fontSize:10,fontWeight:700,color:C.gold,textTransform:"uppercase",display:"block",marginBottom:5}}>Provider (optional)</label>
              <input value={manualChartForm.provider_name} onChange={e=>setManualChartForm({...manualChartForm, provider_name: e.target.value})} placeholder="Who performed the treatment" style={{width:"100%",padding:"10px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"}}/>
            </div>

            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button onClick={()=>setAddingChart(false)} style={{padding:"9px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
              <button
                disabled={!manualChartForm.treatment.trim() || !manualChartForm.date}
                onClick={async ()=>{
                  const treatment = manualChartForm.treatment.trim();
                  if (!treatment) return;
                  // ★ Create a virtual appointment so the entry shows up in
                  //   the chart list AND in the timeline. Marked source so
                  //   it can be distinguished from real bookings (no Stripe
                  //   charge, no SMS, no comm side-effects).
                  const { data: { user } } = await supabase.auth.getUser();
                  const { data: prof } = user ? await supabase.from("profiles").select("full_name,email").eq("id", user.id).maybeSingle() : { data: null };
                  const newId = Date.now();
                  // NOTE: appointments has no `source` column — the
                  //   "manual chart" marker lives in the `notes` field so
                  //   we can still filter for it without schema changes.
                  const newAppt = {
                    id: newId,
                    cid: sel.id,
                    cn: sel.name,
                    treat: treatment,
                    date: manualChartForm.date,
                    time: manualChartForm.time || "12:00",
                    dur: 60,
                    status: "completed",
                    checkin: "checked-out",
                    paid: false,
                    dep: 0,
                    price: 0,
                    provider_name: manualChartForm.provider_name || null,
                    loc: "Nova Med Spa",
                    notes: "[MANUAL_CHART] Added " + new Date().toLocaleString() + " by " + (prof?.full_name || prof?.email || "staff"),
                  };
                  const { error } = await supabase.from("appointments").insert([newAppt]);
                  if (error) {
                    alert("Failed to create chart entry: " + error.message);
                    return;
                  }
                  // Update local state so the new entry appears in the list immediately
                  setAppointments(prev => [...prev, newAppt]);
                  setAddingChart(false);
                  // Open TreatmentRecord on the newly created appointment so staff
                  // can immediately fill in the chart fields.
                  setRecordingAppt(newAppt);
                }}
                style={{padding:"9px 16px",background:!manualChartForm.treatment.trim()?C.light:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:!manualChartForm.treatment.trim()?C.muted:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:!manualChartForm.treatment.trim()?"not-allowed":"pointer",fontFamily:F}}
              >
                Create & Chart
              </button>
            </div>
          </div>
        </div>
      )}
      {chargeClient && <ChargeModal client={chargeClient} onClose={()=>setChargeClient(null)} />}
      {giftBuyer && <GiftCardModal buyer={giftBuyer} onClose={()=>setGiftBuyer(null)} />}
      {recordPayClient && <RecordPaymentModal client={recordPayClient} onClose={()=>setRecordPayClient(null)} />}
      {walletClient && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{width:"100%",maxWidth:560,maxHeight:"85vh",overflowY:"auto",background:"#fff",borderRadius:14,position:"relative"}}>
            {/* ★ The close (×) button lives inside ClientWallet itself —
                duplicating it here showed two X buttons stacked on top
                of each other. ClientWallet receives onClose and renders
                its own header X. */}
            <ClientWallet client={walletClient} onClose={()=>setWalletClient(null)} />
          </div>
        </div>
      )}

      {showF&&(
        <Modal title={form.id?"Edit Client":"New Client"} onClose={()=>setShowF(false)} w={640}>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
            <Fld label="Full Name" required col="1/-1"><input value={form.name||""} onChange={e=>setForm({...form,name:e.target.value})} style={inp}/></Fld>
            <Fld label="Phone"><input value={form.phone||""} onChange={e=>setForm({...form,phone:e.target.value})} style={inp}/></Fld>
            <Fld label="Email"><input value={form.email||""} onChange={e=>setForm({...form,email:e.target.value})} style={inp}/></Fld>
            <Fld label="Date of Birth"><input type="date" value={form.dob||""} onChange={e=>setForm({...form,dob:e.target.value})} style={inp}/></Fld>
            <Fld label="Location"><select value={form.loc||"Beverly Hills"} onChange={e=>setForm({...form,loc:e.target.value})} style={{...inp}}>{LOCS.map(l=><option key={l}>{l}</option>)}</select></Fld>
            <Fld label="Allergies" col="1/-1"><input value={form.allergies||""} onChange={e=>setForm({...form,allergies:e.target.value})} placeholder="None" style={inp}/></Fld>
            <Fld label="Medical History" col="1/-1"><textarea value={form.med||""} onChange={e=>setForm({...form,med:e.target.value})} rows={2} style={{...inp,resize:"vertical"}}/></Fld>
            <Fld label="Notes" col="1/-1"><textarea value={form.notes||""} onChange={e=>setForm({...form,notes:e.target.value})} rows={2} style={{...inp,resize:"vertical"}}/></Fld>
            <Fld label="Status"><select value={form.status||"active"} onChange={e=>setForm({...form,status:e.target.value})} style={{...inp}}><option value="active">Active</option><option value="hold">On Hold</option><option value="inactive">Inactive</option></select></Fld>
            <Fld label="Card on File">
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",marginTop:10}}>
                <input type="checkbox" checked={!!form.cardOnFile} onChange={e=>setForm({...form,cardOnFile:e.target.checked})} style={{accentColor:C.gold}}/>
                <span style={{fontSize:13,color:C.text}}>Card on file</span>
              </label>
            </Fld>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:18}}>
            <Btn v="ghost" onClick={()=>{setShowF(false);}}>Cancel</Btn>
            <Btn onClick={async ()=>{
              if(!form.name)return;
              const toDb = (c) => ({id:c.id,name:c.name,phone:c.phone,email:c.email,dob:c.dob,allergies:c.allergies,med:c.med,spent:c.spent||0,visits:c.visits||0,last:c.last,noshow:c.noshow||0,tags:c.tags||[],notes:c.notes,status:c.status||"active",loc:c.loc,card_on_file:c.cardOnFile||false,gfe_status:c.gfeStatus||"pending",gfe_expiry:c.gfeExpiry,loyalty_pts:c.loyaltyPts||0,member_plan:c.memberPlan});
              if(form.id){
                const { error } = await supabase.from("clients").update(toDb(form)).eq("id",form.id);
                if (error) { alert("Save failed: " + error.message); return; }
                setClients(cs=>cs.map(c=>c.id===form.id?{...c,...form}:c));
              }else{
                // ★ Duplicate guard — same phone or email already on file?
                const _digits = (form.phone || "").replace(/\D/g, "").slice(-10);
                const _emailLc = (form.email || "").trim().toLowerCase();
                const dup = clients.find(c => {
                  const cd = (c.phone || "").replace(/\D/g, "").slice(-10);
                  return (_digits && cd === _digits) || (_emailLc && (c.email || "").trim().toLowerCase() === _emailLc);
                });
                if (dup && !window.confirm(`⚠️ ${dup.name} already exists with this phone/email.\n\nCreate ANOTHER client anyway? (Usually you want Cancel and to search for the existing one.)`)) return;
                const newClient={...form,id:Date.now(),tags:[],spent:0,visits:0,last:"New",noshow:0,loyaltyPts:0};
                const { error } = await supabase.from("clients").insert([toDb(newClient)]);
                if (error) { alert("Save failed: " + error.message); return; }
                setClients(cs=>[...cs,newClient]);
              }
              setShowF(false);
            }}>Save Client</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── PHOTOS TAB - Aggregated gallery of all treatment photos for a client ───
function BillingTabContent({client, appointments}) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    (async ()=>{
      setLoading(true);
      const { data } = await supabase.from("payments").select("*").eq("client_id", client.id).order("created_at", { ascending: false });
      setPayments(data || []);
      setLoading(false);
    })();
  }, [client.id]);

  if (loading) return <div style={{padding:30,textAlign:"center",color:C.dim,fontSize:13}}>Loading billing…</div>;

  const succeeded = payments.filter(p => p.status === "succeeded" || p.status === "completed");
  const refunds = payments.filter(p => p.status === "refunded" || (p.amount || 0) < 0);
  const totalPaid = succeeded.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalRefunded = Math.abs(refunds.reduce((s, p) => s + Number(p.amount || 0), 0));
  const netSpent = totalPaid - totalRefunded;

  const pendingAppts = appointments.filter(a => !a.paid && a.status !== "cancelled" && a.status !== "no-show");
  const outstanding = pendingAppts.reduce((s, a) => s + Number(a.price || 0), 0);

  const walletBalance = Number(client.wallet_balance ?? client.wallet ?? 0);
  const loyaltyPts = Number(client.loyaltyPts || client.loyalty_pts || 0);
  const hasCardOnFile = !!(client.card_on_file || client.cardOnFile);

  // ★ Card-guarantee link (facial hold policy, owner directive 2026-08-31):
  //   texts the client a secure /card page that saves a card via Stripe —
  //   nothing charged at booking; $25 <24h change / $50 no-show enforceable.
  const sendCardGuaranteeLink = async () => {
    const phone = client.phone || "";
    if (!phone) { alert("Client has no phone on file — add one first."); return; }
    const first = (client.name || "there").trim().split(/\s+/)[0];
    const link = `https://www.elyonhq.com/card?cid=${client.id}&n=${encodeURIComponent(first)}`;
    const msg = `Hi ${first}, it's Nova Med Spa 💛 To hold your appointment, please save a card on file — you won't be charged today. Free cancellation up to 24 hours before your visit; changes within 24h are $25 (credited to your treatment); no-shows $50. Secure link: ${link}`;
    if (!window.confirm(`Text the card-guarantee link to ${client.name} (${phone})?`)) return;
    const { data, error } = await supabase.functions.invoke("send-sms", {
      body: { to: phone, message: msg, sender: "system", source: "card_guarantee" },
    });
    if (error || data?.ok === false) alert("SMS failed: " + (error?.message || data?.error || data?.skipped || "unknown"));
    else alert("Card-guarantee link sent ✓");
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(170px, 1fr))",gap:12}}>
        <div style={{padding:"14px 16px",background:"linear-gradient(135deg, "+C.goldBg+", #fff)",border:"1px solid "+C.borderGold,borderRadius:11}}>
          <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",fontWeight:700,letterSpacing:"0.06em",marginBottom:4}}>Wallet Balance</div>
          <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S}}>${walletBalance.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
          <div style={{fontSize:10,color:C.muted,marginTop:3}}>Pre-paid credit available</div>
        </div>
        <div style={{padding:"14px 16px",background:C.card,border:"1px solid "+(hasCardOnFile?C.green+"50":C.border),borderRadius:11}}>
          <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",fontWeight:700,letterSpacing:"0.06em",marginBottom:4}}>Card on File</div>
          <div style={{fontSize:18,fontWeight:800,color:hasCardOnFile?C.green:C.red,fontFamily:S}}>{hasCardOnFile?"✓ Saved":"✗ None"}</div>
          <button onClick={sendCardGuaranteeLink} style={{marginTop:6,padding:"5px 10px",fontSize:10.5,fontWeight:700,background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:7,cursor:"pointer",fontFamily:F}}>📲 Send card link</button>
        </div>
        <div style={{padding:"14px 16px",background:C.card,border:"1px solid "+C.border,borderRadius:11}}>
          <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",fontWeight:700,letterSpacing:"0.06em",marginBottom:4}}>Lifetime Spent</div>
          <div style={{fontSize:22,fontWeight:800,color:C.text,fontFamily:S}}>${netSpent.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
          <div style={{fontSize:10,color:C.muted,marginTop:3}}>{succeeded.length} payment{succeeded.length!==1?"s":""}{totalRefunded>0?` · ${refunds.length} refund${refunds.length!==1?"s":""}`:""}</div>
        </div>
        <div style={{padding:"14px 16px",background:outstanding>0?C.redL:C.card,border:"1px solid "+(outstanding>0?C.red+"40":C.border),borderRadius:11}}>
          <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",fontWeight:700,letterSpacing:"0.06em",marginBottom:4}}>Outstanding</div>
          <div style={{fontSize:22,fontWeight:800,color:outstanding>0?C.red:C.text,fontFamily:S}}>${outstanding.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
          <div style={{fontSize:10,color:C.muted,marginTop:3}}>{pendingAppts.length} unpaid appt{pendingAppts.length!==1?"s":""}</div>
        </div>
        <div style={{padding:"14px 16px",background:C.card,border:"1px solid "+C.border,borderRadius:11}}>
          <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",fontWeight:700,letterSpacing:"0.06em",marginBottom:4}}>Loyalty Points</div>
          <div style={{fontSize:22,fontWeight:800,color:C.purple,fontFamily:S}}>{loyaltyPts.toLocaleString()}</div>
          <div style={{fontSize:10,color:C.muted,marginTop:3}}>Earned</div>
        </div>
      </div>

      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S}}>Invoices & Payment History</div>
          <div style={{fontSize:10,color:C.muted}}>{payments.length} total transaction{payments.length!==1?"s":""}</div>
        </div>
        {payments.length === 0 ? (
          <div style={{padding:30,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:10}}>
            <div style={{fontSize:32,marginBottom:6,opacity:0.4}}>🧾</div>
            <div style={{fontSize:12,color:C.muted}}>No payments yet for this client</div>
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {payments.map(p => {
              const isRefund = p.status === "refunded" || Number(p.amount || 0) < 0;
              const amount = Number(p.amount || 0);
              return (
                <div key={p.id} style={{padding:"12px 14px",background:C.card,border:"1px solid "+C.border,borderLeft:"3px solid "+(isRefund?C.red:p.status==="succeeded"||p.status==="completed"?C.green:C.muted),borderRadius:9,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:180}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{p.description || (isRefund?"Refund":"Payment")}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                      {new Date(p.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                      {p.payment_method ? ` · ${p.payment_method}` : ""}
                      {p.created_by_name ? ` · ${p.created_by_name}` : ""}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    {p.metadata?.tip > 0 && <span style={{fontSize:10,padding:"2px 7px",background:C.greenL,color:C.green,borderRadius:99,fontWeight:600}}>tip ${p.metadata.tip}</span>}
                    {p.appointment_id && <span style={{fontSize:10,padding:"2px 7px",background:C.goldBg,color:C.gold,borderRadius:99,fontWeight:600}}>Appt #{p.appointment_id}</span>}
                    <span style={{fontSize:10,padding:"2px 7px",background:isRefund?C.redL:p.status==="succeeded"||p.status==="completed"?C.greenL:C.light,color:isRefund?C.red:p.status==="succeeded"||p.status==="completed"?C.green:C.muted,borderRadius:99,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.04em"}}>{p.status || "—"}</span>
                  </div>
                  <div style={{fontSize:16,fontWeight:800,color:isRefund?C.red:C.text,fontFamily:S,minWidth:90,textAlign:"right"}}>
                    {isRefund ? "-" : ""}${Math.abs(amount).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// INVOICES TAB — every payment we've taken for this client, with
// a "resend receipt" button per row so the front desk can re-deliver
// any receipt without leaving the profile.
// ─────────────────────────────────────────────────────────────────
function InvoicesTab({ client }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null); // id of payment currently being resent

  useEffect(() => {
    if (!client?.id) return;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("payments")
        .select("*")
        .eq("client_id", client.id)
        .order("created_at", { ascending: false });
      setPayments(data || []);
      setLoading(false);
    })();
    // Live updates so a fresh payment shows up immediately
    const ch = supabase.channel("invoices-" + client.id)
      .on("postgres_changes", { event: "*", schema: "public", table: "payments", filter: "client_id=eq." + client.id }, async () => {
        const { data } = await supabase.from("payments").select("*").eq("client_id", client.id).order("created_at", { ascending: false });
        setPayments(data || []);
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [client?.id]);

  const resend = async (p) => {
    if (!client?.email) { alert("Client has no email on file — add one in their profile first."); return; }
    setResending(p.id);
    try {
      const { data, error } = await supabase.functions.invoke("send-receipt", {
        body: {
          to_email: client.email,
          to_name: client.name,
          // Same itemized breakdown as the invoice view (src/receipt.js).
          ...receiptEmailFields(p),
          service: p.description || "Payment",
          provider: p.provider || "",
          date: new Date(p.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
          payment_method: (p.method || p.payment_method || "card").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
          last4: p.card_last4 || "",
          transaction_id: p.stripe_payment_intent_id || p.external_reference || p.id,
          notes: p.notes || "",
        }
      });
      if (error || data?.error) throw new Error(error?.message || data?.error || "Send failed");
      alert("✅ Receipt resent to " + client.email);
    } catch (e) {
      alert("❌ Failed to resend: " + e.message);
    } finally {
      setResending(null);
    }
  };

  // Opens the invoice as a print-friendly HTML page in a new window. The user
  // can read it on screen, hit File → Print → Save as PDF to keep a local
  // copy, or print it for the client at the front desk. Cleaner than wiring
  // a server-side PDF generator and works offline.
  const viewPdf = (p) => {
    const dt = new Date(p.created_at);
    const dateStr = dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const timeStr = dt.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    const method = (p.method || p.payment_method || "card").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    const last4 = p.card_last4 ? ` ····${p.card_last4}` : "";
    const txnId = p.stripe_payment_intent_id || p.external_reference || p.id || "";
    const amount = (+p.amount || 0).toFixed(2);
    const refunded = p.refunded;
    const html = `<!DOCTYPE html>
<html><head><title>Invoice ${txnId}</title>
<meta charset="utf-8"/>
<style>
  @page { size: letter; margin: 0.5in; }
  body { font-family: Helvetica, Arial, sans-serif; color: #1a1a2e; background: #f8f5f0; margin: 0; padding: 24px; }
  .card { max-width: 600px; margin: 0 auto; background: #fff; border: 1px solid #e8e4df; border-radius: 14px; padding: 36px; box-shadow: 0 2px 10px rgba(0,0,0,0.05); }
  .header { text-align: center; padding-bottom: 18px; border-bottom: 2px solid #b48c3c; margin-bottom: 22px; }
  .spa { font-family: 'Playfair Display','Cormorant Garamond',Georgia,serif; font-size: 26px; font-weight: 700; color: #b48c3c; margin-bottom: 4px; }
  .addr { font-size: 12px; color: #6b7280; }
  h1 { font-family: 'Playfair Display','Cormorant Garamond',Georgia,serif; font-size: 22px; color: #1a1a2e; margin: 0 0 4px 0; }
  .meta { font-size: 12px; color: #6b7280; margin-bottom: 22px; }
  .row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f3f4f6; font-size: 14px; }
  .label { color: #6b7280; }
  .val { color: #1a1a2e; font-weight: 600; text-align: right; }
  .total { display: flex; justify-content: space-between; margin-top: 18px; padding-top: 14px; border-top: 2px solid #b48c3c; font-size: 20px; font-weight: 800; }
  .refund { color: #c0392b; }
  .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #6b7280; }
  .print { background:#b48c3c; color:#fff; border:none; padding:10px 22px; border-radius:8px; font-size:13px; font-weight:700; cursor:pointer; }
  @media print { .no-print { display: none; } body { background: #fff; padding: 0; } .card { box-shadow: none; border: none; } }
</style></head><body>
<div class="card">
  <div class="header">
    <div class="spa">Nova Med Spa</div>
    <div class="addr">1451 Old Country Rd, Plainview, NY 11803 · 516-543-3940</div>
  </div>
  <h1>Invoice / Receipt${refunded ? " — REFUNDED" : ""}</h1>
  <div class="meta">Transaction ID: ${txnId}</div>

  <div class="row"><span class="label">Client</span><span class="val">${(client?.name || "").replace(/[<>]/g, "")}</span></div>
  ${client?.email ? `<div class="row"><span class="label">Email</span><span class="val">${client.email.replace(/[<>]/g, "")}</span></div>` : ""}
  <div class="row"><span class="label">Date</span><span class="val">${dateStr} at ${timeStr}</span></div>
  ${p.provider ? `<div class="row"><span class="label">Provider</span><span class="val">${p.provider.replace(/[<>]/g, "")}</span></div>` : ""}
  <div class="row"><span class="label">Payment Method</span><span class="val">${method}${last4}</span></div>
  <div style="margin:16px 0 4px;font-size:11px;font-weight:700;color:#b48c3c;text-transform:uppercase;letter-spacing:0.08em">Details</div>
  ${receiptRowsHtml(p)}
  ${p.notes ? `<div class="row"><span class="label">Notes</span><span class="val">${p.notes.replace(/[<>]/g, "")}</span></div>` : ""}

  <div class="total ${refunded ? "refund" : ""}">
    <span>${refunded ? "Amount Refunded" : "Total Paid"}</span>
    <span>$${amount}</span>
  </div>

  <div class="footer">
    Thank you for choosing Nova Med Spa.<br>
    Questions about this invoice? Reply to your receipt email or call 516-543-3940.
  </div>
</div>

<div class="no-print" style="max-width:600px; margin:18px auto; text-align:center;">
  <button class="print" onclick="window.print()">🖨 Print / Save as PDF</button>
</div>
</body></html>`;
    // Use a Blob URL with an anchor click — works around popup blockers that
    // would block a bare window.open(). The user-initiated click event +
    // blob: scheme bypasses most blockers.
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  if (loading) return <div style={{padding:30,textAlign:"center",color:C.muted}}>Loading invoices…</div>;
  if (payments.length === 0) {
    return (
      <div style={{padding:40,textAlign:"center",background:C.light,borderRadius:10,border:"1px dashed "+C.border}}>
        <div style={{fontSize:36,marginBottom:8,opacity:0.4}}>🧾</div>
        <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>No invoices yet</div>
        <div style={{fontSize:11,color:C.muted}}>Every payment you record for this client appears here automatically, with a receipt the client received by email.</div>
      </div>
    );
  }

  const lifetime = payments.filter(p => p.status === "succeeded" && !p.refunded).reduce((s, p) => s + (+p.amount || 0), 0);
  const refunded = payments.filter(p => p.refunded).reduce((s, p) => s + (+p.amount || 0), 0);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S}}>Invoices & Receipts</div>
        <div style={{display:"flex",gap:14,fontSize:12}}>
          <span style={{color:C.muted}}>Lifetime: <strong style={{color:C.green}}>${lifetime.toFixed(2)}</strong></span>
          {refunded > 0 && <span style={{color:C.muted}}>Refunded: <strong style={{color:C.red}}>${refunded.toFixed(2)}</strong></span>}
          <span style={{color:C.muted}}>Invoices: <strong style={{color:C.text}}>{payments.length}</strong></span>
        </div>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {payments.map(p => {
          const dt = new Date(p.created_at);
          const method = (p.method || p.payment_method || "card").replace(/_/g, " ");
          const isCard = /card|terminal|stripe/i.test(method);
          return (
            <div key={p.id} style={{padding:"12px 14px",background:"#fff",border:"1px solid "+C.border,borderRadius:9,borderLeft:"3px solid "+(p.refunded?C.red:p.status==="succeeded"?C.green:C.muted)}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                    <div style={{fontSize:16,fontWeight:800,color:p.refunded?C.muted:C.text,textDecoration:p.refunded?"line-through":"none",fontFamily:S}}>${(+p.amount||0).toFixed(2)}</div>
                    {p.refunded && <span style={{fontSize:9,padding:"2px 7px",background:C.redL,color:C.red,borderRadius:10,fontWeight:700}}>REFUNDED</span>}
                    {!p.refunded && p.status === "succeeded" && <span style={{fontSize:9,padding:"2px 7px",background:C.greenL,color:C.green,borderRadius:10,fontWeight:700}}>PAID</span>}
                    <span style={{fontSize:10,padding:"2px 7px",background:C.light,color:C.muted,borderRadius:10,fontWeight:700,textTransform:"uppercase"}}>{isCard ? "💳" : "💵"} {method}{p.card_last4 ? ` ····${p.card_last4}` : ""}</span>
                  </div>
                  {p.description && <div style={{fontSize:12,color:C.text,marginBottom:3}}>{p.description}</div>}
                  <div style={{fontSize:10,color:C.muted}}>
                    {dt.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric",year:"numeric"})} · {dt.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}
                    {p.created_by_name && " · by " + p.created_by_name.split(" ")[0]}
                  </div>
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {p.receipt_url && (
                    <a href={p.receipt_url} target="_blank" rel="noreferrer" style={{padding:"7px 12px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,textDecoration:"none"}}>📄 Stripe Receipt</a>
                  )}
                  <button onClick={() => viewPdf(p)} style={{padding:"7px 12px",background:"rgba(37,99,235,0.08)",color:"#2563eb",border:"1px solid rgba(37,99,235,0.3)",borderRadius:7,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>👁 View / PDF</button>
                  <button onClick={() => resend(p)} disabled={resending === p.id || !client?.email} style={{padding:"7px 12px",background:resending===p.id?C.light:"#fff",color:resending===p.id?C.muted:C.text,border:"1px solid "+C.border,borderRadius:7,fontSize:11,fontWeight:700,cursor:(resending===p.id||!client?.email)?"not-allowed":"pointer",fontFamily:F}}>
                    {resending === p.id ? "Sending…" : "✉ Resend Receipt"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PhotosTabContent({client, appointments, onRecordTreatment}) {
  const [photos, setPhotos] = useState([]);
  const [checkinPhotos, setCheckinPhotos] = useState([]); // [{checkin_id, appointment_id, date, photos:[{angle,url}, ...]}]
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [filter, setFilter] = useState("all");

  // Centralised loader so we can call it from both initial mount and realtime triggers
  const reload = async () => {
    const [photosRes, checkinsRes] = await Promise.all([
      supabase.from("treatment_photos").select("*").eq("client_id", client.id).order("created_at", {ascending:false}),
      supabase.from("checkins").select("id,appointment_id,face_photos,checked_in_at,visit_type").eq("client_id", client.id).order("checked_in_at", {ascending:false}),
    ]);
    setPhotos(photosRes.data || []);
    const cp = (checkinsRes.data || [])
      .filter(c => Array.isArray(c.face_photos) && c.face_photos.length > 0)
      .map(c => ({
        checkin_id: c.id,
        appointment_id: c.appointment_id,
        date: c.created_at,
        visit_type: c.visit_type,
        photos: c.face_photos,
      }));
    setCheckinPhotos(cp);
  };

  useEffect(()=>{
    setLoading(true);
    (async ()=>{
      // Match checkins both ways: client_id may be stored as bigint while
      // React state holds it as number — usually fine, but defensive .or()
      // covers a few legacy rows where Bani's id ended up as text.
      const cidNum = +client.id;
      const cidStr = String(client.id);
      const [photosRes, checkinsRes] = await Promise.all([
        supabase.from("treatment_photos").select("*").eq("client_id", client.id).order("created_at", {ascending:false}),
        supabase.from("checkins")
          .select("id,appointment_id,client_id,face_photos,checked_in_at,visit_type")
          .or(`client_id.eq.${cidNum},client_id.eq.${cidStr}`)
          .order("checked_in_at", {ascending:false}),
      ]);
      console.log("[PhotosTab] client.id =", client.id, "checkins found:", checkinsRes.data?.length, "error:", checkinsRes.error);
      setPhotos(photosRes.data || []);
      const cp = (checkinsRes.data || [])
        .filter(c => Array.isArray(c.face_photos) && c.face_photos.length > 0)
        .map(c => ({
          checkin_id: c.id,
          appointment_id: c.appointment_id,
          date: c.checked_in_at,
          visit_type: c.visit_type,
          photos: c.face_photos,
        }));
      console.log("[PhotosTab] checkinPhotos after filter:", cp.length);
      setCheckinPhotos(cp);
      setLoading(false);
    })();

    // ★ Live updates — if a photo is uploaded from TreatmentRecord while this
    // tab is open, refresh so the new photo shows up without a manual reload.
    const ch = supabase.channel("client-photos-"+client.id)
      .on("postgres_changes", { event: "*", schema: "public", table: "treatment_photos", filter: "client_id=eq."+client.id }, reload)
      .on("postgres_changes", { event: "*", schema: "public", table: "checkins", filter: "client_id=eq."+client.id }, reload)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  const beforePhotos = photos.filter(p=>p.type==="before");
  const afterPhotos = photos.filter(p=>p.type==="after");
  const otherPhotos = photos.filter(p=>!["before","after"].includes(p.type));
  const visible = filter==="all" ? photos : photos.filter(p=>p.type===filter);

  // Build map of appointment_id -> appointment for showing date
  const apptMap = {};
  appointments.forEach(a => { apptMap[a.id] = a; });

  if (loading) return <div style={{padding:"30px",textAlign:"center",color:C.dim}}>Loading photos...</div>;

  return (
    <div>
      {checkinPhotos.length > 0 && (
        <div style={{marginBottom:22}}>
          <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>📸 Check-In Face Photos ({checkinPhotos.length} {checkinPhotos.length === 1 ? "visit" : "visits"})</div>
          {checkinPhotos.map((c, i) => (
            <div key={c.checkin_id} style={{background:C.card,border:"1px solid "+C.border,borderRadius:10,padding:12,marginBottom:10}}>
              <div style={{fontSize:11,color:C.muted,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>{new Date(c.date).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric",year:"numeric"})} · {new Date(c.date).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",hour12:true})}</span>
                {c.visit_type && <span style={{padding:"2px 8px",background:c.visit_type==="cosmetic"?C.goldBg:C.blueL,color:c.visit_type==="cosmetic"?C.gold:C.blue,borderRadius:10,fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>{c.visit_type}</span>}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(110px, 1fr))",gap:8}}>
                {c.photos.map((p, j) => (
                  <button key={j} onClick={()=>setLightbox({url:p.url, caption:`${p.angle?.replace("_"," ")||"photo"} · ${new Date(c.date).toLocaleDateString()}`})} style={{padding:0,border:"1px solid "+C.border,borderRadius:8,overflow:"hidden",cursor:"pointer",background:"none"}}>
                    <img src={p.url} alt={p.angle} style={{width:"100%",aspectRatio:"1",objectFit:"cover",display:"block"}}/>
                    <div style={{padding:"4px 6px",fontSize:10,fontWeight:600,color:C.muted,background:C.light,textTransform:"capitalize"}}>{(p.angle||"").replace(/_/g," ")}</div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S}}>Treatment Photos ({photos.length})</div>
        <div style={{fontSize:10,color:C.muted}}>📸 Photos are added per visit. Open a treatment record to upload.</div>
      </div>

      {photos.length===0 ? (
        <div style={{padding:"40px 20px",textAlign:"center",background:C.light,borderRadius:10,border:`1px dashed ${C.border}`}}>
          <div style={{fontSize:32,marginBottom:8}}>📸</div>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:6,fontFamily:S}}>No photos yet</div>
          <div style={{fontSize:11,color:C.muted,marginBottom:14}}>Photos are uploaded per-visit. Open a visit's treatment record to add before/after photos.</div>
          {appointments.filter(a=>a.status==="completed").slice(0,3).map(a=>(
            <button key={a.id} onClick={()=>onRecordTreatment(a)} style={{padding:"7px 14px",margin:4,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>
              ✍️ Record: {a.treat} ({a.date})
            </button>
          ))}
        </div>
      ) : (
        <>
          <div style={{display:"flex",gap:5,marginBottom:14,padding:5,background:C.light,borderRadius:8,border:`1px solid ${C.border}`,width:"fit-content"}}>
            {[
              ["all", `All (${photos.length})`],
              ["before", `📸 Before (${beforePhotos.length})`],
              ["after", `✨ After (${afterPhotos.length})`],
              otherPhotos.length>0 && ["progress", `📈 Other (${otherPhotos.length})`],
            ].filter(Boolean).map(([id,label])=>(
              <button key={id} onClick={()=>setFilter(id)} style={{padding:"6px 12px",background:filter===id?`linear-gradient(135deg,${C.gold},${C.goldD})`:"transparent",color:filter===id?"#fff":C.muted,border:"none",borderRadius:6,fontSize:11,fontWeight:filter===id?700:600,cursor:"pointer",fontFamily:F}}>{label}</button>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
            {visible.map(p=>{
              const appt = apptMap[p.appointment_id];
              return (
                <div key={p.id} onClick={()=>setLightbox(p)} style={{aspectRatio:"3/4",background:"#000",borderRadius:10,overflow:"hidden",cursor:"pointer",border:`1px solid ${C.border}`,position:"relative"}}>
                  <img src={p.url} alt={p.caption||p.type} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  <div style={{position:"absolute",top:6,left:6,padding:"2px 8px",background:p.type==="before"?C.red:p.type==="after"?C.green:C.purple,color:"#fff",borderRadius:50,fontSize:9,fontWeight:700,letterSpacing:"0.05em"}}>{p.type.toUpperCase()}</div>
                  {!p.visible_to_client && <div style={{position:"absolute",top:6,right:6,padding:"2px 6px",background:"rgba(0,0,0,0.7)",color:"#fff",borderRadius:4,fontSize:9,fontWeight:600}}>🔒 Staff</div>}
                  <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"8px",background:"linear-gradient(to top, rgba(0,0,0,0.85), transparent)",color:"#fff",fontSize:10}}>
                    {appt ? `${appt.treat} · ${appt.date}` : new Date(p.created_at).toLocaleDateString()}
                    {p.caption && <div style={{marginTop:2,opacity:0.9}}>{p.caption}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {lightbox && (
        <div onClick={()=>setLightbox(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.95)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:14,cursor:"pointer"}}>
          <button onClick={()=>setLightbox(null)} style={{position:"absolute",top:18,right:18,width:38,height:38,background:"rgba(255,255,255,0.15)",border:"none",borderRadius:"50%",color:"#fff",fontSize:20,cursor:"pointer",fontWeight:600}}>×</button>
          <img src={lightbox.url} alt={lightbox.caption} style={{maxWidth:"100%",maxHeight:"90vh",borderRadius:8}} onClick={e=>e.stopPropagation()}/>
        </div>
      )}
    </div>
  );
}

// ── GFE MANAGEMENT ────────────────────────────────────────────────────────────
function GFEManagement({clients}){
  const [selGFE,setSelGFE] = useState(null);
  const [showNew,setShowNew] = useState(false);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{padding:"14px 18px",background:C.goldBg,border:`1px solid ${C.borderGold}`,borderRadius:10}}>
        <div style={{fontSize:13,fontWeight:700,color:C.gold,marginBottom:4}}>⚕️ What is a Good Faith Exam (GFE)?</div>
        <div style={{fontSize:12,color:C.muted,lineHeight:1.6}}>Every med spa client must have a GFE by a licensed Medical Director before injectable treatments. GFEs expire every 6-12 months. Elyon tracks all GFEs and alerts before expiry.</div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
        {[["Valid GFEs",GFE_FORMS.filter(g=>g.status==="valid").length,C.green,"shield","Cleared for treatment"],["Pending GFEs",GFE_FORMS.filter(g=>g.status==="pending").length,C.orange,"clock","Need scheduling"],["Expired GFEs",GFE_FORMS.filter(g=>g.status==="expired").length,C.red,"warn","Cannot treat — urgent"]].map(([l,v,c,icon,sub])=>(
          <Card key={l} style={{padding:"16px 18px"}}>
            <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:8}}>
              <div style={{width:32,height:32,borderRadius:8,background:c+"15",display:"flex",alignItems:"center",justifyContent:"center"}}><Ic n={icon} s={16} c={c}/></div>
              <div style={{fontSize:22,fontWeight:800,color:c,fontFamily:S}}>{v}</div>
            </div>
            <div style={{fontSize:12,color:C.muted,fontWeight:500}}>{l}</div>
            <div style={{fontSize:11,color:C.dim}}>{sub}</div>
          </Card>
        ))}
      </div>

      <Card>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>All GFE Records</div>
          <Btn sz="sm" v="sec" icon="plus" onClick={()=>setShowNew(true)}>New GFE</Btn>
        </div>
        {GFE_FORMS.map(g=>(
          <div key={g.id} style={{padding:"13px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <div style={{width:34,height:34,borderRadius:8,background:(SC[g.status]?.bg)||C.light,display:"flex",alignItems:"center",justifyContent:"center"}}><Ic n="shield" s={15} c={(SC[g.status]?.c)||C.muted}/></div>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{g.client}</div>
                <div style={{fontSize:11,color:C.muted}}>MD: {g.provider} · {g.treats.slice(0,2).join(", ")}</div>
                {g.expiry&&<div style={{fontSize:11,color:C.dim}}>Expires: {g.expiry}</div>}
              </div>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {g.signed&&<span style={{fontSize:11,color:C.green,background:C.greenL,padding:"2px 8px",borderRadius:8,fontWeight:600}}>e-Signed ✓</span>}
              <Bdg status={g.status}/>
              <Btn sz="sm" v="sec" onClick={()=>setSelGFE(g)}>View</Btn>
            </div>
          </div>
        ))}
      </Card>

      {selGFE&&(
        <Modal title="GFE Record Details" onClose={()=>setSelGFE(null)} w={500}>
          <div style={{padding:"14px",background:C.goldBg,borderRadius:9,border:`1px solid ${C.borderGold}`,marginBottom:16}}>
            <div style={{fontSize:15,fontWeight:700,color:C.text,fontFamily:S,marginBottom:4}}>{selGFE.client}</div>
            <Bdg status={selGFE.status}/>
          </div>
          {[["Medical Director",selGFE.provider],["Exam Date",selGFE.date||"Not yet completed"],["Expiry Date",selGFE.expiry||"N/A"],["Treatments Covered",selGFE.treats.join(", ")||"None"],["Digital Signature",selGFE.signed?"✓ Signed":"Not signed"]].map(([k,v])=>(
            <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
              <span style={{color:C.muted}}>{k}</span>
              <span style={{color:C.text,fontWeight:500}}>{v}</span>
            </div>
          ))}
          <div style={{display:"flex",gap:8,marginTop:16}}>
            {selGFE.status==="expired"&&<Btn sz="sm" v="primary">Schedule New GFE</Btn>}
            <Btn sz="sm" v="ghost">Download PDF</Btn>
            <Btn sz="sm" v="ghost" onClick={()=>setSelGFE(null)}>Close</Btn>
          </div>
        </Modal>
      )}

      {showNew&&(
        <Modal title="New Good Faith Exam" onClose={()=>setShowNew(false)}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Fld label="Client" required><select style={{...inp}}><option>Select client...</option>{clients.map(c=><option key={c.id}>{c.name}</option>)}</select></Fld>
            <Fld label="Medical Director"><select style={{...inp}}><option value="">Select…</option>{PROVIDERS.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></Fld>
            <Fld label="Exam Date"><input type="date" style={inp}/></Fld>
            <Fld label="Treatments Covered">
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {["Botox","Lip Filler","Cheek Filler","Sculptra","Kybella"].map(t=>(
                  <label key={t} style={{display:"flex",gap:8,alignItems:"center",cursor:"pointer",fontSize:13,color:C.text}}>
                    <input type="checkbox" style={{accentColor:C.gold}}/>{t}
                  </label>
                ))}
              </div>
            </Fld>
            <div style={{padding:"10px 13px",background:C.goldBg,borderRadius:8,border:`1px solid ${C.borderGold}`,fontSize:12,color:C.gold}}>📋 A consent form will be sent to the client for e-signature automatically.</div>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:18}}>
            <Btn v="ghost" onClick={()=>setShowNew(false)}>Cancel</Btn>
            <Btn onClick={()=>setShowNew(false)}>Create GFE & Send Form</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── MEMBERSHIPS ───────────────────────────────────────────────────────────────
function Memberships({clients}){
  const [members] = useState(INIT_MEMBERS);
  const totalMRR = members.reduce((s,m)=>s+m.price*m.active,0);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
        {[["Monthly Recurring Revenue",`$${totalMRR.toLocaleString()}`,C.gold,"star"],["Total Members",members.reduce((s,m)=>s+m.active,0),C.green,"users"],["Active Plans",members.length,C.purple,"members"]].map(([l,v,c,icon])=>(
          <StatCard key={l} label={l} value={v} color={c} icon={icon}/>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
        {members.map(m=>(
          <Card key={m.id} style={{padding:20,borderTop:`3px solid ${m.color}`}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
              <div>
                <div style={{fontSize:15,fontWeight:800,color:C.text,fontFamily:S,marginBottom:3}}>{m.name}</div>
                <div style={{fontSize:22,fontWeight:800,color:m.color,fontFamily:S}}>${m.price}<span style={{fontSize:12,fontWeight:400,color:C.muted}}>/{m.period}</span></div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:20,fontWeight:800,color:C.text,fontFamily:S}}>{m.active}</div>
                <div style={{fontSize:11,color:C.muted}}>members</div>
              </div>
            </div>
            {m.perks.map((p,i)=><div key={i} style={{fontSize:12,color:C.muted,display:"flex",gap:6,marginBottom:4}}><span style={{color:m.color}}>✓</span>{p}</div>)}
            <div style={{marginTop:12,display:"flex",gap:8}}>
              <Btn sz="sm" v="sec">Add Member</Btn>
              <Btn sz="sm" v="ghost" icon="edit">Edit</Btn>
            </div>
          </Card>
        ))}
      </div>

    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// RefundModal — full or partial refund with optional client email
// ════════════════════════════════════════════════════════════════════
function RefundModal({ payment, onClose }) {
  const totalAmount = Number(payment.amount || 0);
  const alreadyRefunded = Number(payment.refund_amount || 0);
  const maxRefundable = +(totalAmount - alreadyRefunded).toFixed(2);
  const [amount, setAmount] = useState(maxRefundable);
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      const refundAmt = Math.min(Math.max(0, Number(amount) || 0), maxRefundable);
      if (refundAmt <= 0) throw new Error("Pick a refund amount above $0");
      const { data, error: fnErr } = await supabase.functions.invoke("refund-payment", {
        body: { payment_id: payment.id, amount: refundAmt, reason: reason || null, notify },
      });
      if (fnErr) {
        let real = fnErr.message || "Refund failed";
        // Show the function's real reason (e.g. "no Stripe reference — refund in Stripe first").
        try { if (fnErr.context?.json) { const j = await fnErr.context.json(); if (j?.error) real = j.error; } } catch (_) {}
        try {
          const ctxBody = fnErr.context?.body;
          if (ctxBody) {
            const parsed = typeof ctxBody === "string" ? JSON.parse(ctxBody) : ctxBody;
            if (parsed?.error) real = parsed.error;
          }
        } catch (_) {}
        throw new Error(real);
      }
      if (data?.error) throw new Error(data.error);
      setResult(data);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(3px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,maxWidth:480,width:"100%",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}}>
        <div style={{padding:"18px 22px",borderBottom:`1px solid ${C.border}`,background:C.redL}}>
          <div style={{fontSize:17,fontWeight:800,color:C.red,fontFamily:S}}>↩ Issue Refund</div>
          <div style={{fontSize:12,color:C.muted,marginTop:4}}>{payment.client_name || "Guest"} · {payment.description || "Payment"}</div>
        </div>

        {result ? (
          <>
            <div style={{padding:24,textAlign:"center"}}>
              <div style={{fontSize:42,marginBottom:8}}>✅</div>
              <div style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:6}}>Refund Issued</div>
              <div style={{fontSize:13,color:C.muted,marginBottom:12}}>
                ${Number(result.refunded_amount).toFixed(2)} refunded{result.fully_refunded ? " (fully)" : " (partial)"}.
                {payment.stripe_payment_intent_id ? " Funds return to the original card in 5–10 business days." : " Hand the amount to the client now if cash."}
              </div>
              {notify && payment.client_id ? (
                <div style={{fontSize:11,color:C.green,fontWeight:600}}>📧 Confirmation email sent to the client</div>
              ) : null}
            </div>
            <div style={{padding:"12px 22px",borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"flex-end"}}>
              <button onClick={onClose} style={{padding:"9px 18px",background:C.gold,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div style={{padding:22,display:"flex",flexDirection:"column",gap:14}}>
              <div style={{padding:"10px 14px",background:C.light,borderRadius:8,fontSize:12,color:C.text}}>
                <div><strong>Original charge:</strong> ${totalAmount.toFixed(2)} · {payment.payment_method || "—"}</div>
                {alreadyRefunded > 0 && <div style={{color:"#92400e",marginTop:4}}>Previously refunded: ${alreadyRefunded.toFixed(2)}</div>}
                <div style={{color:C.muted,marginTop:4,fontSize:11}}>Up to <strong>${maxRefundable.toFixed(2)}</strong> can be refunded now.</div>
              </div>

              <div>
                <label style={{display:"block",fontSize:11,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>Refund Amount ($)</label>
                <input type="number" min="0.01" max={maxRefundable} step="0.01" value={amount} onChange={e=>setAmount(e.target.value)}
                  style={{...inp,fontSize:18,fontWeight:700,color:C.red,fontFamily:S,padding:"12px 14px"}}/>
                <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}}>
                  <button type="button" onClick={()=>setAmount(maxRefundable)} style={{padding:"4px 10px",background:C.redL,color:C.red,border:`1px solid ${C.red}40`,borderRadius:99,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:F}}>Full ${maxRefundable.toFixed(2)}</button>
                  <button type="button" onClick={()=>setAmount(+(maxRefundable/2).toFixed(2))} style={{padding:"4px 10px",background:"#fff",color:C.muted,border:`1px solid ${C.border}`,borderRadius:99,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:F}}>Half</button>
                </div>
              </div>

              <div>
                <label style={{display:"block",fontSize:11,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>Reason (optional)</label>
                <select value={reason} onChange={e=>setReason(e.target.value)} style={inp}>
                  <option value="">— Select reason —</option>
                  <option value="requested_by_customer">Requested by customer</option>
                  <option value="duplicate">Duplicate charge</option>
                  <option value="service_issue">Service issue / not satisfied</option>
                  <option value="wrong_amount">Wrong amount charged</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",padding:"10px 14px",background:notify?C.goldBg:C.light,border:`1.5px solid ${notify?C.borderGold:C.border}`,borderRadius:10}}>
                <input type="checkbox" checked={notify} onChange={e=>setNotify(e.target.checked)} style={{marginTop:2,accentColor:C.gold,width:18,height:18,flexShrink:0}}/>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:2}}>📧 Email refund confirmation</div>
                  <div style={{fontSize:10,color:C.muted}}>Sends a refund receipt with the amount + service to the client's email on file.</div>
                </div>
              </label>

              {error && <div style={{padding:"10px 12px",background:C.redL,color:C.red,border:`1px solid ${C.red}40`,borderRadius:7,fontSize:12}}>❌ {error}</div>}
            </div>

            <div style={{padding:"12px 22px",borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"flex-end",gap:8,background:C.light}}>
              <button onClick={onClose} disabled={busy} style={{padding:"9px 16px",background:"#fff",color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontWeight:700,cursor:busy?"not-allowed":"pointer",fontFamily:F}}>Cancel</button>
              <button onClick={submit} disabled={busy || Number(amount)<=0 || Number(amount)>maxRefundable} style={{padding:"9px 18px",background:busy?C.light:C.red,color:busy?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:busy?"not-allowed":"pointer",fontFamily:F,opacity:(busy||Number(amount)<=0||Number(amount)>maxRefundable)?0.6:1}}>
                {busy?"Processing…":`↩ Refund $${(Number(amount)||0).toFixed(2)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── INVENTORY ─────────────────────────────────────────────────────────────────
function Inventory(){
  const [items] = useState(INVENTORY);
  const lowStock = items.filter(i=>i.stock<=i.min);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {lowStock.length>0&&(
        <div style={{padding:"12px 16px",background:C.redL,border:"1px solid rgba(192,57,43,0.2)",borderRadius:9}}>
          <div style={{fontSize:12,fontWeight:700,color:C.red,marginBottom:6}}>⚠️ Low Stock — Reorder Needed</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {lowStock.map(i=>(
              <span key={i.id} style={{fontSize:11,padding:"3px 10px",background:"rgba(192,57,43,0.1)",border:"1px solid rgba(192,57,43,0.2)",borderRadius:20,color:C.red,fontWeight:600}}>{i.name} ({i.stock}/{i.min})</span>
            ))}
          </div>
        </div>
      )}
      <Card style={{overflow:"auto"}}>
        <div style={{padding:"13px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between"}}>
          <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Inventory</div>
          <Btn sz="sm" v="sec" icon="plus">Add Item</Btn>
        </div>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:600}}>
          <thead>
            <tr style={{background:C.light}}>
              {["Item","Category","Stock","Min","Cost/Unit","Supplier","Status"].map(h=>(
                <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.07em",borderBottom:`1px solid ${C.border}`}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map(item=>(
              <tr key={item.id} style={{borderBottom:`1px solid ${C.border}`}}>
                <td style={{padding:"11px 14px",fontSize:13,fontWeight:600,color:C.text}}>{item.name}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:C.muted}}>{item.cat}</td>
                <td style={{padding:"11px 14px",fontSize:13,fontWeight:700,color:item.stock<=item.min?C.red:C.green}}>{item.stock}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:C.muted}}>{item.min}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:C.text}}>${item.cost}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:C.muted}}>{item.supplier}</td>
                <td style={{padding:"11px 14px"}}>{item.stock<=item.min?<Bdg status="expired" label="Low Stock"/>:<Bdg status="active" label="In Stock"/>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ── ANALYTICS ─────────────────────────────────────────────────────────────────
function Analytics({clients,appointments}){
  const done = appointments.filter(a=>a.status==="completed");
  const totalRev = done.reduce((s,a)=>s+a.price,0);
  const tips = done.reduce((s,a)=>s+(a.tip||0),0);
  const ns = appointments.filter(a=>a.status==="no-show").length;
  const mRevs = [8200,9400,7800,totalRev||0];
  const maxMR = Math.max(...mRevs,1);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:12}}>
        <StatCard label="Total Revenue" value={`$${totalRev.toLocaleString()}`} color={C.gold} icon="chart" trend={18}/>
        <StatCard label="Tips Collected" value={`$${tips.toLocaleString()}`} color={C.green} icon="star"/>
        <StatCard label="Avg Client LTV" value={`$${Math.round(clients.reduce((s,c)=>s+(c.spent||0),0)/Math.max(clients.length,1)).toLocaleString()}`} color={C.purple} icon="users" trend={8}/>
        <StatCard label="No-Show Rate" value={`${appointments.length?Math.round((ns/appointments.length)*100):0}%`} color={C.red} icon="warn"/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:16}}>
        <Card style={{padding:20}}>
          <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:18}}>Monthly Revenue</div>
          <div style={{display:"flex",gap:12,alignItems:"flex-end",height:140}}>
            {["Oct","Nov","Dec","Jan"].map((m,i)=>(
              <div key={m} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:7}}>
                <div style={{fontSize:11,fontWeight:700,color:i===3?C.gold:C.muted}}>${(mRevs[i]/1000).toFixed(1)}k</div>
                <div style={{width:"100%",height:`${(mRevs[i]/maxMR)*120}px`,background:i===3?`linear-gradient(180deg,${C.gold},${C.goldD})`:`${C.gold}25`,borderRadius:"5px 5px 0 0"}}/>
                <div style={{fontSize:12,color:C.muted}}>{m}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card style={{padding:20}}>
          <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:14}}>Top Clients</div>
          {[...clients].sort((a,b)=>(b.spent||0)-(a.spent||0)).slice(0,5).map((c,i)=>(
            <div key={c.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <div style={{width:18,fontSize:12,fontWeight:700,color:i===0?C.gold:C.muted}}>#{i+1}</div>
                <div style={{fontSize:12,color:C.text}}>{(c.name||"").split(" ")[0]}</div>
              </div>
              <div style={{fontSize:12,fontWeight:700,color:C.gold}}>${(c.spent||0).toLocaleString()}</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ── PAYROLL ───────────────────────────────────────────────────────────────────
function Payroll({appointments}){
  const done = appointments.filter(a=>a.status==="completed");
  const calcPayroll = (p) => {
    const pa = done.filter(a=>a.pid===p.id);
    const revenue = pa.reduce((s,a)=>s+a.price,0);
    const tips = pa.reduce((s,a)=>s+(a.tip||0),0);
    const commission = Math.round(revenue*(p.commPct/100));
    const base = p.salary||((p.hourly||0)*160);
    return {revenue,tips,commission,base,total:base+commission+tips,treatments:pa.length};
  };
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Payroll — {new Date().toLocaleDateString("en-US",{month:"long",year:"numeric"})}</div>
        <div style={{display:"flex",gap:8}}>
          <Btn v="sec" sz="sm">Export Excel</Btn>
          <Btn v="ghost" sz="sm">Export PDF</Btn>
        </div>
      </div>
      {PROVIDERS.map(p=>{

const c = calcPayroll(p);
        return (
          <Card key={p.id} style={{padding:20}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
              <div style={{width:42,height:42,borderRadius:10,background:p.color+"20",border:`1px solid ${p.color}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:p.color,flexShrink:0}}>{p.ini}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:700,color:C.text}}>{p.name}</div>
                <div style={{fontSize:11,color:C.muted}}>{p.role} · {c.treatments} treatments</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:20,fontWeight:800,color:C.gold,fontFamily:S}}>${c.total.toLocaleString()}</div>
                <div style={{fontSize:11,color:C.muted}}>total pay</div>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
              {[["Base",`$${c.base.toLocaleString()}`,C.blue],["Commission",`$${c.commission.toLocaleString()}`,C.green],["Tips",`$${c.tips.toLocaleString()}`,C.purple],["Revenue",`$${c.revenue.toLocaleString()}`,C.gold]].map(([l,v,col])=>(
                <div key={l} style={{padding:"9px 12px",background:C.light,borderRadius:8,textAlign:"center"}}>
                  <div style={{fontSize:14,fontWeight:800,color:col,fontFamily:S}}>{v}</div>
                  <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.05em"}}>{l}</div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ── USER MANAGEMENT ───────────────────────────────────────────────────────────
function UserManagement(){
  const [users,setUsers] = useState(USERS);
  const [showF,setShowF] = useState(false);
  const [form,setForm] = useState({});
  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Team & User Management</div>
          <div style={{fontSize:12,color:C.muted,marginTop:3}}>Manage staff accounts, roles and permissions</div>
        </div>
        <Btn icon="plus" onClick={()=>{setForm({name:"",email:"",role:"frontdesk",status:"active"});setShowF(true);}}>Add User</Btn>
      </div>
      <Card>
        {users.map(u=>(
          <div key={u.id} style={{padding:"14px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:38,height:38,borderRadius:9,background:C.goldBg,border:`1px solid ${C.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:C.gold,flexShrink:0}}>{u.name.split(" ").map(n=>n[0]).join("")}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>{u.name}</div>
              <div style={{fontSize:11,color:C.muted}}>{u.email} · Last login: {u.lastLogin}</div>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
              <Bdg status={u.role}/>
              <Bdg status={u.status}/>
              <Btn sz="sm" v="ghost" icon="edit">Edit</Btn>
              {u.status==="active"
                ?<Btn sz="sm" v="danger" onClick={()=>setUsers(us=>us.map(x=>x.id===u.id?{...x,status:"inactive"}:x))}>Deactivate</Btn>
                :<Btn sz="sm" v="success" onClick={()=>setUsers(us=>us.map(x=>x.id===u.id?{...x,status:"active"}:x))}>Activate</Btn>
              }
            </div>
          </div>
        ))}
      </Card>

      {showF&&(
        <Modal title="Add Team Member" onClose={()=>setShowF(false)}>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <Fld label="Full Name" required><input value={form.name||""} onChange={e=>setForm({...form,name:e.target.value})} style={inp}/></Fld>
            <Fld label="Email" required><input type="email" value={form.email||""} onChange={e=>setForm({...form,email:e.target.value})} style={inp}/></Fld>
            <Fld label="Role"><select value={form.role||"frontdesk"} onChange={e=>setForm({...form,role:e.target.value})} style={{...inp}}>
              <option value="admin">Admin — Full Access</option>
              <option value="provider">Provider (MD/NP/RN)</option>
              <option value="frontdesk">Front Desk</option>
              <option value="aesthetician">Aesthetician</option>
            </select></Fld>
            <div style={{padding:"10px 13px",background:C.goldBg,borderRadius:8,border:`1px solid ${C.borderGold}`,fontSize:12,color:C.gold}}>📧 An invitation email will be sent to set up their password.</div>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:18}}>
            <Btn v="ghost" onClick={()=>{setShowF(false);}}>Cancel</Btn>
            <Btn onClick={()=>{if(form.name&&form.email){setUsers(us=>[...us,{...form,id:Date.now(),lastLogin:"Never"}]);setShowF(false);}}}>Send Invite</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── BOOKING LINK ──────────────────────────────────────────────────────────────
function BookingLink({appointments,setAppointments,clients}){
  const [step,setStep] = useState("page");
  const [settings,setSettings] = useState({spaName:"Beverly Hills Aesthetics",slug:"beverly-hills-aesthetics",primaryColor:C.gold,requireDeposit:true,depositAmount:100,showPrices:true,allowedTreats:["Botox","Lip Filler","HydraFacial","Chemical Peel"],message:"Welcome to a world of elevated aesthetics."});
  const [copied,setCopied] = useState("");
  const [bStep,setBStep] = useState(1);
  const [bForm,setBForm] = useState({treat:"",date:"",time:"",name:"",phone:"",email:""});
  const [bDone,setBDone] = useState(false);
  const url = `https://book.elyonhq.com/${settings.slug}`;
  const embedCode = `<iframe src="${url}" width="100%" height="700" frameborder="0" style="border-radius:12px"></iframe>`;
  const btnCode = `<a href="${url}" target="_blank" style="display:inline-block;padding:14px 28px;background:${settings.primaryColor};color:#fff;font-family:sans-serif;font-size:14px;font-weight:700;border-radius:8px;text-decoration:none">BOOK NOW</a>`;
  const copy = (text,key) => {navigator.clipboard?.writeText(text).catch(()=>{});setCopied(key);setTimeout(()=>setCopied(""),2000);};

  const submitBooking = () => {
    if(!bForm.treat||!bForm.date||!bForm.name)return;
    const newAppt = {id:Date.now(),cid:Date.now(),cn:bForm.name,treat:bForm.treat,pid:null,prov:"",date:bForm.date,time:bForm.time||"10:00",dur:TDUR[bForm.treat]||60,price:TPRICE[bForm.treat]||500,status:"confirmed",room:"",dep:settings.requireDeposit?settings.depositAmount:0,loc:"Nova Med Spa",checkin:"upcoming",tip:0,paid:false,notes:"Online booking"};
    setAppointments(as=>[...as,newAppt]);
    setBDone(true);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      <div style={{display:"flex",gap:4,background:C.light,padding:3,borderRadius:9,border:`1px solid ${C.border}`,width:"fit-content"}}>
        {[["page","Page"],["hours","Hours"],["providers","Providers"],["blocktime","📅 Block / Extra Days"],["holidays","Holidays"],["addons","Add-ons"],["preview","Preview"],["share","Share & Embed"]].map(([v,l])=>(
          <button key={v} onClick={()=>setStep(v)} style={{padding:"6px 14px",fontSize:12,fontWeight:step===v?700:500,color:step===v?"#fff":C.muted,background:step===v?C.gold:"none",borderRadius:7,border:"none",cursor:"pointer",fontFamily:F}}>{l}</button>
        ))}
      </div>

      {step==="page"&&(<PageCustomization />)}
      {step==="holidays" && <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:24}}><HolidayHoursEditor /></div>}
      {step==="hours" && <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:24}}><BusinessHoursEditor /></div>}
      {step==="providers" && <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:24}}><ProviderAvailabilityEditor /></div>}
      {step==="blocktime" && <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:24}}><ProviderBlockHours /></div>}

      {step==="preview"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:C.muted,marginBottom:10,textTransform:"uppercase",letterSpacing:"0.08em"}}>Client View</div>
            <div style={{border:`2px solid ${C.border}`,borderRadius:14,overflow:"hidden",boxShadow:"0 8px 30px rgba(0,0,0,0.1)"}}>
              <div style={{background:settings.primaryColor,padding:"20px 24px",color:"#fff"}}>
                <div style={{fontSize:19,fontWeight:800,fontFamily:S,marginBottom:3}}>{settings.spaName}</div>
                <div style={{fontSize:12,opacity:0.85}}>{settings.message}</div>
              </div>
              {!bDone?(
                <div style={{padding:20,background:"#fff"}}>
                  <div style={{display:"flex",gap:6,marginBottom:18}}>
                    {["Treatment","Date & Time","Your Info"].map((s,i)=>(
                      <div key={s} style={{display:"flex",alignItems:"center",gap:4,flex:1}}>
                        <div style={{width:20,height:20,borderRadius:"50%",background:bStep>i+1?C.green:bStep===i+1?settings.primaryColor:C.border,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>{bStep>i+1?"✓":i+1}</div>
                        <div style={{fontSize:10,color:bStep===i+1?C.text:C.dim}}>{s}</div>
                        {i<2&&<div style={{flex:1,height:1,background:C.border}}/>}
                      </div>
                    ))}
                  </div>
                  {bStep===1&&(
                    <div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
                        {settings.allowedTreats.map(t=>(
                          <div key={t} onClick={()=>setBForm(f=>({...f,treat:t}))} style={{padding:"10px 12px",border:`1.5px solid ${bForm.treat===t?settings.primaryColor:C.border}`,borderRadius:9,cursor:"pointer",background:bForm.treat===t?settings.primaryColor+"12":"#fff"}}>
                            <div style={{fontSize:12,fontWeight:600,color:C.text}}>{t}</div>
                            {settings.showPrices&&<div style={{fontSize:11,color:C.muted}}>${TPRICE[t]}</div>}
                          </div>
                        ))}
                      </div>
                      <button onClick={()=>bForm.treat&&setBStep(2)} style={{width:"100%",padding:"11px",background:settings.primaryColor,color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>Continue →</button>
                    </div>
                  )}
                  {bStep===2&&(
                    <div>
                      <Fld label="Date"><input type="date" value={bForm.date} onChange={e=>setBForm(f=>({...f,date:e.target.value}))} style={inp}/></Fld>
                      {bForm.date&&(
                        <div style={{marginTop:12}}>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:14}}>
                            {["9:00 AM","10:00 AM","11:00 AM","1:00 PM","2:00 PM","3:00 PM"].map(t=>(
                              <div key={t} onClick={()=>setBForm(f=>({...f,time:t}))} style={{padding:"8px",textAlign:"center",fontSize:11,fontWeight:bForm.time===t?700:400,background:bForm.time===t?settings.primaryColor:"#f8f8f8",color:bForm.time===t?"#fff":C.text,border:`1px solid ${bForm.time===t?settings.primaryColor:C.border}`,borderRadius:8,cursor:"pointer"}}>{t}</div>
                            ))}
                          </div>
                        </div>
                      )}
                      {settings.requireDeposit&&<div style={{marginTop:10,padding:"9px 12px",background:C.goldBg,borderRadius:8,fontSize:11,color:C.gold,border:`1px solid ${C.borderGold}`}}>💳 ${settings.depositAmount} deposit required</div>}
                      <div style={{display:"flex",gap:8,marginTop:14}}>
                        <button onClick={()=>setBStep(1)} style={{flex:1,padding:"10px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>← Back</button>
                        <button onClick={()=>bForm.date&&bForm.time&&setBStep(3)} style={{flex:2,padding:"10px",background:settings.primaryColor,color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>Continue →</button>
                      </div>
                    </div>
                  )}
                  {bStep===3&&(
                    <div>
                      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:14}}>
                        <Fld label="Full Name"><input value={bForm.name} onChange={e=>setBForm(f=>({...f,name:e.target.value}))} style={inp}/></Fld>
                        <Fld label="Phone"><input value={bForm.phone} onChange={e=>setBForm(f=>({...f,phone:e.target.value}))} style={inp}/></Fld>
                        <Fld label="Email"><input value={bForm.email} onChange={e=>setBForm(f=>({...f,email:e.target.value}))} style={inp}/></Fld>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>setBStep(2)} style={{flex:1,padding:"10px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>← Back</button>
                        <button onClick={submitBooking} style={{flex:2,padding:"10px",background:settings.primaryColor,color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>Confirm ✓</button>
                      </div>
                    </div>
                  )}
                </div>
              ):(
                <div style={{padding:"30px",textAlign:"center",background:"#fff"}}>
                  <div style={{fontSize:36,marginBottom:10}}>✨</div>
                  <div style={{fontSize:17,fontWeight:800,color:C.text,fontFamily:S,marginBottom:6}}>Booking Confirmed!</div>
                  <div style={{fontSize:12,color:C.muted,marginBottom:16}}>{bForm.name}, your {bForm.treat} is confirmed for {bForm.date}.</div>
                  <div style={{padding:"10px",background:C.greenL,borderRadius:8,fontSize:12,color:C.green,marginBottom:14,border:`1px solid rgba(46,139,87,0.2)`}}>✅ Added to CRM calendar</div>
                  <button onClick={()=>{setBDone(false);setBStep(1);setBForm({treat:"",date:"",time:"",name:"",phone:"",email:""}); }} style={{padding:"9px 18px",background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Book Another</button>
                </div>
              )}
            </div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <Card style={{padding:18}}>
              <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>Your Booking URL</div>
              <div style={{display:"flex",gap:8,alignItems:"center",padding:"9px 12px",background:C.light,borderRadius:8,border:`1px solid ${C.border}`,marginBottom:10}}>
                <Ic n="link" s={14} c={C.muted}/>
                <div style={{flex:1,fontSize:11,color:C.text,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{url}</div>
                <Btn sz="sm" v={copied==="url"?"success":"sec"} onClick={()=>copy(url,"url")}>{copied==="url"?"✓":"Copy"}</Btn>
              </div>
            </Card>
            <Card style={{padding:18}}>
              <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:8}}>Website Embed</div>
              <div style={{background:"#0f0f1e",borderRadius:8,padding:12,marginBottom:8,position:"relative"}}>
                <pre style={{margin:0,fontSize:10,color:"#b48c3c",fontFamily:"monospace",lineHeight:1.6,whiteSpace:"pre-wrap",wordBreak:"break-all"}}>{embedCode}</pre>
                <button onClick={()=>copy(embedCode,"embed")} style={{position:"absolute",top:6,right:6,background:"rgba(255,255,255,0.1)",border:"none",borderRadius:6,padding:"3px 8px",color:"#fff",fontSize:10,cursor:"pointer"}}>{copied==="embed"?"✓":"Copy"}</button>
              </div>
            </Card>
            <Card style={{padding:18}}>
              <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:8}}>Book Now Button</div>
              <div style={{background:"#0f0f1e",borderRadius:8,padding:12,marginBottom:10,position:"relative"}}>
                <pre style={{margin:0,fontSize:10,color:"#b48c3c",fontFamily:"monospace",lineHeight:1.6,whiteSpace:"pre-wrap",wordBreak:"break-all"}}>{btnCode}</pre>
                <button onClick={()=>copy(btnCode,"btn")} style={{position:"absolute",top:6,right:6,background:"rgba(255,255,255,0.1)",border:"none",borderRadius:6,padding:"3px 8px",color:"#fff",fontSize:10,cursor:"pointer"}}>{copied==="btn"?"✓":"Copy"}</button>
              </div>
              <div style={{padding:"14px",background:C.light,borderRadius:8,display:"flex",justifyContent:"center"}}>
                <a href="#" style={{display:"inline-block",padding:"11px 24px",background:settings.primaryColor,color:"#fff",fontFamily:F,fontSize:13,fontWeight:700,borderRadius:8,textDecoration:"none"}}>BOOK NOW</a>
              </div>
            </Card>
          </div>
        </div>
      )}

      {step==="addons"&&(<AddonsManager />)}
      {step==="share"&&(<EmbedShare />)}
    </div>
  );
}

// ── AI ASSISTANT ──────────────────────────────────────────────────────────────
function AIAssistant({clients,appointments}){
  const [msgs,setMsgs] = useState([{role:"ai",text:"Hi! I'm your Elyon AI Assistant. Ask me about revenue opportunities, at-risk clients, follow-ups, or anything about your practice!"}]);
  const [input,setInput] = useState("");
  const RESP = {
    "follow up":"📋 Follow-ups: open the Pipeline and recently-completed appointments to see which clients to check in with.",
    "churn":"⚠️ At-risk clients: check Client Insights for clients with high no-show rates or no recent visits, then send a re-engagement SMS.",
    "revenue":"💰 Revenue opportunities: review clients due for repeat treatments, package upsells, and anyone without a membership.",
    "gfe":"⚕️ GFE status: open the GFE tab to see which clients need a Good Faith Exam before treatment.",
    "schedule":"📅 Today's Schedule:\n\n" + appointments.filter(a=>a.date===TODAY).map(a=>`• ${a.time} — ${a.cn} · ${a.treat} · $${a.price}`).join("\n")||"No appointments today",
  };
  const qs = ["Follow-ups","Churn risks","Revenue opportunities","GFE alerts","Today's schedule"];
  const send = (t) => {
    const txt = t||input;if(!txt.trim())return;
    setMsgs(m=>[...m,{role:"user",text:txt}]);setInput("");
    setTimeout(()=>{
      const key = Object.keys(RESP).find(k=>txt.toLowerCase().includes(k));
      setMsgs(m=>[...m,{role:"ai",text:key?RESP[key]:`Based on your ${clients.length} clients and ${appointments.length} appointments. What would you like to dig into?`}]);
    },500);
  };

  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 260px",gap:18,height:"calc(100vh - 180px)"}}>
      <Card style={{display:"flex",flexDirection:"column"}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",gap:10,alignItems:"center",background:C.goldBg}}>
          <div style={{width:38,height:38,borderRadius:9,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,display:"flex",alignItems:"center",justifyContent:"center"}}><Ic n="bot" s={17} c="#fff"/></div>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Elyon AI Assistant</div>
            <div style={{fontSize:11,color:C.green,display:"flex",alignItems:"center",gap:4}}><span style={{width:6,height:6,borderRadius:"50%",background:C.green,display:"inline-block"}}/> Online</div>
          </div>
        </div>
        <div style={{flex:1,overflow:"auto",padding:18,display:"flex",flexDirection:"column",gap:12}}>
          {msgs.map((m,i)=>(
            <div key={i} style={{display:"flex",gap:8,flexDirection:m.role==="user"?"row-reverse":"row"}}>
              {m.role==="ai"&&<div style={{width:28,height:28,borderRadius:7,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><Ic n="bot" s={13} c="#fff"/></div>}
              <div style={{maxWidth:"80%",padding:"10px 14px",background:m.role==="user"?`linear-gradient(135deg,${C.gold},${C.goldD})`:C.light,color:m.role==="user"?"#fff":C.text,borderRadius:m.role==="user"?"12px 4px 12px 12px":"4px 12px 12px 12px",fontSize:13,lineHeight:1.65,whiteSpace:"pre-line",border:`1px solid ${m.role==="user"?C.gold:C.border}`}}>{m.text}</div>
            </div>
          ))}
        </div>
        <div style={{padding:"12px 18px",borderTop:`1px solid ${C.border}`}}>
          <div style={{display:"flex",gap:5,marginBottom:8,flexWrap:"wrap"}}>
            {qs.map(s=><button key={s} onClick={()=>send(s)} style={{fontSize:11,padding:"4px 10px",background:C.goldBg,color:C.gold,border:`1px solid ${C.borderGold}`,borderRadius:16,cursor:"pointer",fontFamily:F}}>{s}</button>)}
          </div>
          <div style={{display:"flex",gap:8}}>
            <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder="Ask anything..." style={{...inp,flex:1}}/>
            <Btn onClick={()=>send()} icon="send">Send</Btn>
          </div>
        </div>
      </Card>
      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        <Card style={{padding:16}}>
          <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>🤖 Live Insights</div>
          {[["Revenue Opportunity","3 Botox clients overdue",C.gold],["GFE Alert","2 clients need GFE now",C.red],["Upsell Ready","Dana — VIP upsell",C.green],["No-Show Risk","Jessica — 25% rate",C.orange]].map(([t,msg,c])=>(
            <div key={t} style={{padding:"9px 11px",background:c+"10",border:`1px solid ${c}20`,borderRadius:8,marginBottom:7}}>
              <div style={{fontSize:10,fontWeight:700,color:c,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:2}}>{t}</div>
              <div style={{fontSize:11,color:C.text}}>{msg}</div>
            </div>
          ))}
        </Card>
        <Card style={{padding:16}}>
          <div style={{fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:10}}>📊 Practice Score</div>
          {[["Retention",82,C.green],["Revenue Growth",73,C.gold],["No-Show Risk",14,C.red],["Satisfaction",91,C.teal]].map(([l,v,c])=>(
            <div key={l} style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}><span style={{color:C.muted}}>{l}</span><span style={{fontWeight:700,color:c}}>{v}</span></div>
              <div style={{height:4,background:C.light,borderRadius:2}}><div style={{height:"100%",width:`${v}%`,background:c,borderRadius:2}}/></div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ── NAV ───────────────────────────────────────────────────────────────────────
const NAV = [
  {id:"dashboard",label:"Dashboard",icon:"dash",group:"core"},
  {id:"frontdesk",label:"Front Desk",icon:"desk",group:"core"},
  {id:"appointments",label:"Appointments",icon:"cal",group:"core"},
  {id:"checkout",label:"Checkout / POS",icon:"cash",group:"core"},
  {id:"takepay",label:"Take Payment",icon:"cash",group:"core"},
  {id:"clients",label:"Patients",icon:"clip",group:"clinical"},
  {id:"gfe",label:"GFE Management",icon:"shield",group:"clinical"},
  // "Providers" merged into the unified "Team & Providers" section (owner
  // directive 2026-08-15) — one place for accounts, roles, services & hours.
  // ★ Single hub for everything paper-related — consents, pre/post care
  //   reminders, questionnaires, and clinical note templates. Replaces the
  //   separate "Consents" and "Pre/Post Messages" entries.
  {id:"patientpaperwork",label:"Patient Paperwork",icon:"shield",group:"clinical"},
  {id:"memberships",label:"Memberships",icon:"members",group:"business"},
  {id:"services",label:"Services & Retail",icon:"sparkles",group:"business"},
  {id:"packages",label:"Packages",icon:"package",group:"business"},
  {id:"payroll",label:"Payroll",icon:"payroll",group:"business"},
  // Working hours measured from real activity (owner request 2026-10-05).
  {id:"staffhours",label:"Smart Hours",icon:"payroll",group:"business"},
  {id:"bookinglink",label:"Booking Link",icon:"link",group:"growth"},
  {id:"sales",label:"Sales",icon:"dollar",group:"growth"},
  {id:"analytics",label:"Analytics",icon:"chart",group:"growth"},
  // Revenue from the practice's first month, joining the AestheticRecord
  // history to live Elyon payments (owner directive 2026-09-02).
  {id:"reports",label:"Business Reports",icon:"chart",group:"growth"},
  {id:"clientinsights",label:"Client Insights",icon:"users",group:"growth"},
  {id:"winback",label:"Win-Back Engine",icon:"trending",group:"growth"},
  {id:"coldoutreach",label:"Cold Outreach",icon:"send",group:"growth"},
  {id:"mission",label:"Today's Mission",icon:"trending",group:"growth"},
  // Sophia AI voice calls + recordings (owner 2026-11-02). Sales-level access.
  {id:"sophiacalls",label:"Sophia Calls",icon:"send",group:"growth"},
  {id:"leadengine",label:"Lead Engine",icon:"trending",group:"growth"},
  {id:"pipeline",label:"Pipeline Board",icon:"trending",group:"growth"},
  {id:"botoxrecall",label:"Botox Recall",icon:"trending",group:"growth"},
  {id:"treatments",label:"Treatment Analytics",icon:"chart",group:"growth"},
  {id:"users",label:"Team & Providers",icon:"key",group:"settings"},
  {id:"messages",label:"Messages",icon:"send",group:"core"},
  {id:"ai",label:"AI Assistant",icon:"bot",group:"settings"},
  {id:"bottraining",label:"Bot Training",icon:"bot",group:"settings"},
  {id:"automations",label:"Reminders & Automations",icon:"bot",group:"settings"},
];

const GROUPS = {core:"Operations",clinical:"Clinical",business:"Business",growth:"Growth",settings:"Platform"};

// ── APP ───────────────────────────────────────────────────────────────────────
export default function CoreCRM(){
  const isMobile = useIsMobile();
  const { profile, hasAccess, canViewSales } = useAuth();
  const [tab,setTab] = useState("dashboard");
  // ★ Smart Hours: one ping per minute in which the user actually interacted.
  useActivityHeartbeat(profile, tab);
  // Live-call panel → "Book appointment" switches to the Appointments tab.
  useEffect(() => {
    const go = (e) => { if (e.detail) setTab(e.detail); };
    window.addEventListener("elyon:goto-tab", go);
    return () => window.removeEventListener("elyon:goto-tab", go);
  }, []);
  // Start EMPTY, not with demo data — otherwise the old sample schedule
  // (Noah Bennett / "Dr. Ashley Monroe" etc.) flashes on screen for a moment
  // before the real Supabase data loads. Real data is fetched on mount below.
  const [clients,setClients] = useState([]);
  const [appointments,setAppointments] = useState([]);
  const [pendingClientFocus, setPendingClientFocus] = useState(null); // clientId — Clients tab will pick this up on render
  // ★ Track WHERE the user came from when opening a client profile, so the
  //   "Back" button can return them to the right calendar day. Set when
  //   opening client from an appointment; null when opening from sidebar.
  const [clientReturnTo, setClientReturnTo] = useState(null); // { tab: "appointments", date: "YYYY-MM-DD" } | null
  const [appointmentsJumpDate, setAppointmentsJumpDate] = useState(null);

  // Allow any descendant to jump straight to a client profile from anywhere
  const openClientProfile = (clientId, returnTo = null) => {
    if (!clientId) return;
    setPendingClientFocus(clientId);
    setClientReturnTo(returnTo);
    setTab("clients");
  };
  const returnFromClientProfile = () => {
    if (clientReturnTo?.tab === "appointments") {
      if (clientReturnTo.date) setAppointmentsJumpDate(clientReturnTo.date);
      setTab("appointments");
      setClientReturnTo(null);
      setPendingClientFocus(null);
    }
  };
  const [collapsed,setCollapsed] = useState(isMobile);
  const [sidebarOpen,setSidebarOpen] = useState(false);
  const [loc,setLoc] = useState("Beverly Hills");
  const confirmed = appointments.filter(a=>a.status==="confirmed").length;

  // ★ Unread inbound SMS count — drives the red badge on the Messages tab in
  // the sidebar so the owner sees at a glance how many threads need a reply.
  // Excludes auto-handled opt-out keywords (STOP/UNSUBSCRIBE/END/QUIT) which
  // the SMS carrier processes automatically — no staff reply needed. Without
  // that filter the badge can get "stuck" on a number like 12 when half of
  // those are STOP duplicates from the same client.
  const [unreadMsgsCount, setUnreadMsgsCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        // Pull recent unread inbound — last 60 days only, so historical
        // unread messages from before the read-tracking feature shipped
        // don't pile up forever.
        const cutoff = new Date(Date.now() - 60 * 86400000).toISOString();
        const { data } = await supabase.from("bot_messages")
          .select("id,message_text,conversation_id,created_at")
          .eq("direction", "inbound")
          .eq("read", false)
          .gte("created_at", cutoff)
          .order("created_at", { ascending: false })
          .limit(500);
        const optOutRe = /^\s*(stop|unsubscribe|end|quit|stopall|cancel|unstop|opt[-\s]?out)\s*[.!]?\s*$/i;
        // Dedupe by conversation+text+ROUNDED-second so 2 identical "Stop"
        // SMS that the carrier double-fires count as one unread.
        const seen = new Set();
        let realUnread = 0;
        for (const m of data || []) {
          const t = (m.message_text || "").trim();
          if (!t || optOutRe.test(t)) continue;
          const key = `${m.conversation_id}|${t.toLowerCase()}|${m.created_at.slice(0,16)}`;
          if (seen.has(key)) continue;
          seen.add(key);
          realUnread++;
        }
        if (alive) setUnreadMsgsCount(realUnread);
      } catch (_) { /* swallow — badge just won't update this tick */ }
    };
    refresh();
    // Live channel: any insert/update to bot_messages re-counts.
    const ch = supabase.channel("nav-unread")
      .on("postgres_changes", { event: "*", schema: "public", table: "bot_messages" }, refresh)
      .subscribe();
    // Also poll every 30s as a safety net for missed realtime events.
    const t = setInterval(refresh, 30000);
    return () => { alive = false; clearInterval(t); supabase.removeChannel(ch); };
  }, []);

  // Paginated fetcher — Supabase caps select queries at 1000 rows by default,
  // which silently dropped any client (or row) past that. With 2,500+ AR-
  // imported clients, anyone added after the cutoff (like Gg Kiki at id
  // 1779822438161) was invisible until this fix.
  const fetchAllClients = async () => {
    const all = [];
    let from = 0;
    const PAGE = 1000;
    while (true) {
      const { data: chunk } = await supabase.from("clients").select("*").order("id").range(from, from + PAGE - 1);
      if (!chunk || chunk.length === 0) break;
      all.push(...chunk);
      if (chunk.length < PAGE) break;
      from += PAGE;
    }
    return all;
  };

  // Load from Supabase on mount + seed initial data if empty
  useEffect(()=>{
    const loadData = async () => {
      const clientsData = await fetchAllClients();
      if (clientsData && clientsData.length > 0) {
        setClients(clientsData.map(c => ({...c, cardOnFile: c.card_on_file, gfeStatus: c.gfe_status, gfeExpiry: c.gfe_expiry, loyaltyPts: c.loyalty_pts, memberPlan: c.member_plan})));
      } else {
        // Empty (or a failed/empty load) → show nothing. NEVER auto-insert the
        // demo clients into the live DB — that's how "Noah Bennett" & friends
        // ended up polluting the real schedule in the first place.
        setClients([]);
      }

      const { data: apptsData } = await (async () => { const all = []; let from = 0; const PAGE = 1000; while (true) { const { data: chunk } = await supabase.from("appointments").select("*").order("id").range(from, from + PAGE - 1); if (!chunk || !chunk.length) break; all.push(...chunk); if (chunk.length < PAGE) break; from += PAGE; } return { data: all }; })();
      if (apptsData && apptsData.length > 0) {
        setAppointments(apptsData);
      } else {
        setAppointments([]); // never auto-seed demo appointments into the live DB
      }
    };
    loadData().catch(err => console.error("Load error:", err));
  },[]);

  // ★ Realtime sync for CLIENTS (2026-09-28). The list was loaded once on
  //   mount and never refreshed, which produced two separate bug reports that
  //   turned out to be the same thing:
  //     • "the card never saves" — save_payment_method flips
  //       clients.card_on_file in the database, but this array still held the
  //       stale row, so the profile kept showing no card until a page reload.
  //       158 cards were saved correctly the whole time.
  //     • "bot bookings have no client profile" — the bot DOES create the
  //       client row, but a client created after page load was missing here,
  //       so opening the EMR fell through to the 'client not found' error.
  //   Patch from the payload rather than re-fetching 3,000 rows on every
  //   change, matching the appointments subscription below.
  useEffect(()=>{
    const norm = (c) => ({...c, cardOnFile: c.card_on_file, gfeStatus: c.gfe_status, gfeExpiry: c.gfe_expiry, loyaltyPts: c.loyalty_pts, memberPlan: c.member_plan});
    const ch = supabase.channel("clients-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "clients" }, (payload) => {
        const row = payload.new || payload.old;
        if (!row?.id) return;
        setClients(cs => {
          if (payload.eventType === "DELETE") return cs.filter(c => String(c.id) !== String(row.id));
          const i = cs.findIndex(c => String(c.id) === String(row.id));
          if (i === -1) return [...cs, norm(payload.new)];
          const next = cs.slice();
          next[i] = { ...next[i], ...norm(payload.new) };
          return next;
        });
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  },[]);

// ★ Realtime sync for appointments — PATCH state locally from the payload
//   instead of re-fetching the whole table on every change. Previously each
//   booking/checkin/edit re-loaded all 1000+ rows, which froze iPads for a
//   second or two per change. Now: insert → push, update → swap, delete →
//   filter. Only falls back to a full reload if the payload is suspicious.
  useEffect(()=>{
    let pending = null;
    const fullReloadDebounced = () => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(async () => {
        const all = []; let from = 0; const PAGE = 1000;
        while (true) {
          const { data: chunk } = await supabase.from("appointments").select("*").order("id").range(from, from + PAGE - 1);
          if (!chunk || !chunk.length) break;
          all.push(...chunk);
          if (chunk.length < PAGE) break;
          from += PAGE;
        }
        if (all.length) setAppointments(all);
      }, 600);
    };
    const ch = supabase.channel("appointments-realtime-sync")
      .on("postgres_changes", {event:"*", schema:"public", table:"appointments"}, (payload) => {
        try {
          if (payload.eventType === "INSERT" && payload.new) {
            setAppointments(prev => prev.some(a => a.id === payload.new.id) ? prev : [...prev, payload.new]);
            return;
          }
          if (payload.eventType === "UPDATE" && payload.new) {
            setAppointments(prev => prev.map(a => a.id === payload.new.id ? { ...a, ...payload.new } : a));
            return;
          }
          if (payload.eventType === "DELETE" && payload.old) {
            setAppointments(prev => prev.filter(a => a.id !== payload.old.id));
            return;
          }
          // Unknown shape → fall back to a debounced full reload
          fullReloadDebounced();
        } catch (_) { fullReloadDebounced(); }
      }).subscribe();
    return () => { if (pending) clearTimeout(pending); supabase.removeChannel(ch); };
  },[]);

  // ★ SAFETY NET for the schedule (owner-reported 2026-06-18: a booked client,
  //   Lauri Ventrice, never appeared on the schedule — the realtime INSERT was
  //   missed because the watching device had been asleep). Realtime alone can
  //   silently drop events when a phone/iPad suspends the socket. So we also
  //   re-fetch today+future appointments every 60s AND whenever the app regains
  //   focus, then MERGE by id (upsert) — a missed booking self-heals within a
  //   minute and can never leave a client invisible. Light query: only upcoming
  //   rows, not the full history.
  useEffect(() => {
    const pad = (n) => String(n).padStart(2, "0");
    const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const refetchUpcoming = async () => {
      try {
        const from = ymd(new Date(Date.now() - 86400000)); // yesterday, to be safe near midnight
        const { data } = await supabase.from("appointments").select("*").gte("date", from).order("id").limit(2000);
        if (!data || !data.length) return;
        setAppointments(prev => {
          const byId = new Map(prev.map(a => [a.id, a]));
          for (const a of data) byId.set(a.id, a); // authoritative upsert from DB
          return Array.from(byId.values());
        });
      } catch (_) { /* offline — try again next tick */ }
    };
    refetchUpcoming();
    const iv = setInterval(refetchUpcoming, 60000);
    const onVis = () => { if (typeof document !== "undefined" && document.visibilityState === "visible") refetchUpcoming(); };
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVis);
    if (typeof window !== "undefined") window.addEventListener("focus", refetchUpcoming);
    return () => {
      clearInterval(iv);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVis);
      if (typeof window !== "undefined") window.removeEventListener("focus", refetchUpcoming);
    };
  }, []);

  // ★ Realtime sync for clients — same in-place patching approach. With
  //   2,400+ client rows this saves ~150–300ms per change on the iPad.
  useEffect(()=>{
    const massage = (c) => ({...c, cardOnFile: c.card_on_file, gfeStatus: c.gfe_status, gfeExpiry: c.gfe_expiry, loyaltyPts: c.loyalty_pts, memberPlan: c.member_plan});
    let pending = null;
    const fullReloadDebounced = () => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(async () => {
        const data = await fetchAllClients();
        if (data) setClients(data.map(massage));
      }, 600);
    };
    const ch = supabase.channel("clients-realtime-sync")
      .on("postgres_changes", {event:"*", schema:"public", table:"clients"}, (payload) => {
        try {
          if (payload.eventType === "INSERT" && payload.new) {
            setClients(prev => prev.some(c => c.id === payload.new.id) ? prev : [...prev, massage(payload.new)]);
            return;
          }
          if (payload.eventType === "UPDATE" && payload.new) {
            setClients(prev => prev.map(c => c.id === payload.new.id ? { ...c, ...massage(payload.new) } : c));
            return;
          }
          if (payload.eventType === "DELETE" && payload.old) {
            setClients(prev => prev.filter(c => c.id !== payload.old.id));
            return;
          }
          fullReloadDebounced();
        } catch (_) { fullReloadDebounced(); }
      }).subscribe();
    return () => { if (pending) clearTimeout(pending); supabase.removeChannel(ch); };
  },[]);

    // Helper to log activity
  const logActivity = async (action, entityType, entityId, details) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: prof } = await supabase.from("profiles").select("full_name, email").eq("id", user.id).single();
      await supabase.from("activity_log").insert([{user_id: user.id, user_email: prof?.email || user.email, user_name: prof?.full_name || user.email, action, entity_type: entityType, entity_id: String(entityId), details}]);
    } catch(e) { console.error("Log error:", e); }
  };

  // ★ Filter sidebar items by the current role's permissions before grouping.
  // Admin/owner see everything. Other roles see only what's granted to them in
  // Users & Roles. Items the user has no access to are hidden from the sidebar
  // AND the corresponding tab content below refuses to render.
  const isAdminLike = ["admin", "owner", "director"].includes((profile?.role || "").toLowerCase());
  // ★ Sales/revenue is restricted to admin/director/Sarah (canViewSales) — this
  //   overrides the generic admin/permission rule for the "sales" item only.
  // "users" (Team & Providers) is the merged section — staff who only hold the
  // old 'providers' permission must still see it (they get the providers view).
  const allowNav = (n) => n.id === "sales" ? canViewSales
    : n.id === "staffhours" ? isAdminLike
    : n.id === "reports" ? canViewSales
    : n.id === "users" ? (isAdminLike || hasAccess("users") || hasAccess("providers"))
    : (isAdminLike || hasAccess(n.id));
  const visibleNav = NAV.filter(allowNav);
  const grouped = visibleNav.reduce((acc,n)=>{if(!acc[n.group])acc[n.group]=[];acc[n.group].push(n);return acc;},{});

  // Helper used below to gate tab content (defense in depth — sidebar + tab body)
  const canView = (id) => id === "sales" ? canViewSales
    // Business Reports shows revenue, so it follows the same restriction as
    // Sales — owner/director only, not every role with a generic permission.
    : id === "reports" ? canViewSales
    // Call recordings: same people who see sales.
    : id === "sophiacalls" ? canViewSales
    : id === "users" ? (isAdminLike || hasAccess("users") || hasAccess("providers"))
    : (isAdminLike || hasAccess(id));

  // If the user lands on a tab they can't view (e.g., admin removed access while they were on it),
  // bounce them to the first allowed tab.
  useEffect(() => {
    if (!profile) return;
    if (canView(tab)) return;
    const firstAllowed = visibleNav[0]?.id || "dashboard";
    setTab(firstAllowed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.role, tab]);

  const SidebarContent = () => (
    <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
      <div style={{padding:"18px 14px 10px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${C.sidebarBorder}`,marginBottom:6}}>
        <ElyonLogo collapsed={collapsed&&!isMobile}/>
        {!isMobile&&<button onClick={()=>setCollapsed(!collapsed)} style={{background:"transparent",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.3)",padding:4,display:"flex",borderRadius:6}}><Ic n="arr" s={14}/></button>}
        {isMobile&&<button onClick={()=>setSidebarOpen(false)} style={{background:"transparent",border:"none",cursor:"pointer",color:"rgba(255,255,255,0.5)",padding:4,display:"flex"}}><Ic n="x" s={18}/></button>}
      </div>
      <nav style={{flex:1,padding:"0 7px",overflow:"auto",scrollbarWidth:"none"}}>
        {Object.entries(grouped).map(([group,items])=>(
          <div key={group} style={{marginBottom:4}}>
            {(!collapsed||isMobile)&&<div style={{fontSize:9,fontWeight:800,color:"#d4af37",textTransform:"uppercase",letterSpacing:"0.15em",padding:"8px 10px 3px"}}>{GROUPS[group]}</div>}
            {items.map(n=>(
              <button key={n.id} onClick={()=>{setTab(n.id);if(isMobile)setSidebarOpen(false);}}
                style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:collapsed&&!isMobile?"10px 0":"8px 10px",justifyContent:collapsed&&!isMobile?"center":"flex-start",borderRadius:8,border:"none",background:tab===n.id?"rgba(180,140,60,0.2)":"transparent",color:tab===n.id?"#b48c3c":"rgba(255,255,255,0.75)",cursor:"pointer",fontSize:13,fontWeight:tab===n.id?800:600,fontFamily:F,marginBottom:1,transition:"all 0.15s",textAlign:"left",position:"relative"}}>
                <Ic n={n.icon} s={15} c={tab===n.id?"#b48c3c":"rgba(255,255,255,0.6)"}/>
                {(!collapsed||isMobile)&&<span style={{flex:1}}>{n.label}</span>}
                {(!collapsed||isMobile)&&n.id==="appointments"&&confirmed>0&&<span style={{fontSize:9,fontWeight:800,background:C.gold,color:"#fff",borderRadius:9,padding:"1px 5px"}}>{confirmed}</span>}
                {/* ★ Red unread badge on Messages — pulses gently so the owner notices new SMS at a glance */}
                {(!collapsed||isMobile)&&n.id==="messages"&&unreadMsgsCount>0&&<span style={{fontSize:9,fontWeight:800,background:C.red,color:"#fff",borderRadius:9,padding:"2px 6px",boxShadow:"0 0 0 0 rgba(192,57,43,0.6)",animation:"elyonUnreadPulse 1.6s ease-out infinite",minWidth:14,textAlign:"center"}}>{unreadMsgsCount>99?"99+":unreadMsgsCount}</span>}
                {/* Collapsed sidebar: tiny red dot anchored to the icon so the badge is still visible without the label */}
                {collapsed&&!isMobile&&n.id==="messages"&&unreadMsgsCount>0&&<span style={{position:"absolute",top:6,right:8,width:8,height:8,background:C.red,borderRadius:"50%",boxShadow:"0 0 0 2px "+C.sidebar}}/>}
                {tab===n.id&&<div style={{position:"absolute",left:0,top:"50%",transform:"translateY(-50%)",width:2.5,height:18,background:C.gold,borderRadius:"0 2px 2px 0"}}/>}
              </button>
            ))}
          </div>
        ))}
      </nav>
      {(!collapsed||isMobile)&&(
        <div style={{padding:"10px 10px 16px",borderTop:`1px solid ${C.sidebarBorder}`}}>
          <div style={{display:"flex",alignItems:"center",gap:9,padding:"8px 10px",background:"rgba(180,140,60,0.1)",borderRadius:9,border:`1px solid rgba(180,140,60,0.2)`}}>
            <div style={{width:30,height:30,borderRadius:7,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#fff",flexShrink:0}}>{(profile?.full_name||profile?.email||"U").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}</div>
            <div>
              <div style={{fontSize:11,color:"#fff",fontWeight:700}}>{profile?.full_name||profile?.email||"User"}</div>
              <div style={{fontSize:9,color:"rgba(180,140,60,0.7)"}}>{(profile?.role||"Staff").replace(/\b\w/g,c=>c.toUpperCase())} · {loc}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{display:"flex",minHeight:"100vh",background:C.bg,fontFamily:F,color:C.text}}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&family=Cormorant+Garamond:wght@400;600;700;800&display=swap" rel="stylesheet"/>

      {/* Mobile Overlay */}
      {isMobile&&sidebarOpen&&(
        <div onClick={()=>setSidebarOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:199,backdropFilter:"blur(2px)"}}/>
      )}

      {/* Sidebar — Desktop */}
      {!isMobile&&(
        <div style={{width:collapsed?64:240,background:C.sidebar,display:"flex",flexDirection:"column",flexShrink:0,transition:"width 0.22s",position:"fixed",top:0,bottom:0,left:0,zIndex:100,borderRight:`1px solid ${C.sidebarBorder}`,overflow:"hidden"}}>
          <SidebarContent/>
        </div>
      )}

      {/* Sidebar — Mobile Drawer */}
      {isMobile&&(
        <div style={{width:240,background:C.sidebar,display:"flex",flexDirection:"column",flexShrink:0,position:"fixed",top:0,bottom:0,left:sidebarOpen?0:-240,zIndex:200,borderRight:`1px solid ${C.sidebarBorder}`,overflow:"hidden",transition:"left 0.25s"}}>
          <SidebarContent/>
        </div>
      )}

      {/* Main */}
      <div style={{marginLeft:isMobile?0:collapsed?58:220,flex:1,display:"flex",flexDirection:"column",transition:"margin-left 0.22s",minHeight:"100vh"}}>
        {/* Topbar */}
        <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"0 16px 0 "+(isMobile?"16px":"20px"),height:54,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:50,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {isMobile&&(
              <button onClick={()=>setSidebarOpen(true)} style={{background:"none",border:"none",cursor:"pointer",color:C.muted,padding:4,display:"flex",marginRight:4}}>
                <Ic n="menu" s={20}/>
              </button>
            )}
            <div style={{fontSize:isMobile?16:20,fontWeight:800,color:C.text,fontFamily:S}}>{NAV.find(n=>n.id===tab)?.label}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {!isMobile&&(
              <select value={loc} onChange={e=>setLoc(e.target.value)} style={{...inp,width:"auto",fontSize:12,padding:"5px 10px"}}>
                {LOCS.map(l=><option key={l}>{l}</option>)}
              </select>
            )}
            <div style={{fontSize:10,color:C.green,padding:"4px 9px",background:C.greenL,borderRadius:7,fontWeight:700,display:"flex",alignItems:"center",gap:4,border:`1px solid rgba(46,139,87,0.2)`}}>
              <span style={{width:5,height:5,borderRadius:"50%",background:C.green,display:"inline-block"}}/>
              {isMobile?"SECURE":"HIPAA SECURE"}
            </div>
          </div>
        </div>

        {/* Content — each tab is wrapped in canView() to refuse rendering for unauthorized roles */}
        <div style={{flex:1,padding:isMobile?"14px":"22px 24px",overflow:"auto"}}>
          {tab==="dashboard"&&canView("dashboard")&&<Dashboard clients={clients} appointments={appointments} openClientProfile={openClientProfile}/>}
          {tab==="frontdesk"&&canView("frontdesk")&&<FrontDesk appointments={appointments} setAppointments={setAppointments} clients={clients} openClientProfile={openClientProfile}/>}
          {tab==="appointments"&&canView("appointments")&&<Appointments appointments={appointments} setAppointments={setAppointments} clients={clients} setClients={setClients} openClientProfile={openClientProfile} jumpToDate={appointmentsJumpDate} clearJumpDate={()=>setAppointmentsJumpDate(null)}/>}
          {tab==="checkout"&&canView("checkout")&&<Checkout appointments={appointments} setAppointments={setAppointments} clients={clients}/>}
          {tab==="takepay"&&canView("takepay")&&<TakePaymentPage/>}
          {tab==="clients"&&canView("clients")&&<Clients clients={clients} setClients={setClients} appointments={appointments} setAppointments={setAppointments} pendingClientFocus={pendingClientFocus} clearPendingClientFocus={()=>setPendingClientFocus(null)} returnTo={clientReturnTo} onReturn={returnFromClientProfile}/>}
          {tab==="gfe"&&canView("gfe")&&<GFEManagement clients={clients}/>}
          {/* Providers merged into Team & Providers; old tab id kept for stale
              sessions — renders the same unified section. */}
          {tab==="providers"&&canView("providers")&&<UserMgmtNew/>}
          {/* ★ Patient Paperwork hub — Consents + Pre/Post + Questionnaires + Clinical Note Templates */}
          {tab==="patientpaperwork"&&canView("patientpaperwork")&&<PatientPaperworkHub/>}
          {/* Back-compat: deep links to the old standalone tabs route to the hub */}
          {(tab==="consents"||tab==="treatment-messages")&&canView("patientpaperwork")&&<PatientPaperworkHub/>}
          {tab==="memberships"&&canView("memberships")&&<MembershipsManager/>}
          {tab==="mission"&&canView("mission")&&<TodaysMission/>}
          {tab==="sophiacalls"&&canView("sophiacalls")&&<SophiaCalls/>}
          {tab==="leadengine"&&canView("leadengine")&&<LeadEngine/>}
          {tab==="pipeline"&&canView("pipeline")&&<LeadPipelineBoard/>}
          {tab==="botoxrecall"&&canView("botoxrecall")&&<BotoxRecall/>}
          {tab==="services"&&canView("services")&&<ServicesManager/>}
          {tab==="packages"&&canView("packages")&&<PackagesCatalog/>}
          {tab==="inventory"&&canView("inventory")&&<Inventory/>}
          {tab==="payroll"&&canView("payroll")&&<PayrollPage/>}
          {tab==="staffhours"&&isAdminLike&&<StaffHours/>}
          {tab==="bookinglink"&&canView("bookinglink")&&<BookingLink appointments={appointments} setAppointments={setAppointments} clients={clients}/>}
          {tab==="sales"&&canView("sales")&&<SalesReport/>}
          {tab==="analytics"&&canView("analytics")&&<Analytics clients={clients} appointments={appointments}/>}
          {tab==="reports"&&canView("reports")&&<BusinessReports/>}
          {tab==="clientinsights"&&canView("clientinsights")&&<ClientAnalytics/>}
          {tab==="winback"&&canView("winback")&&<WinBackEngine/>}
          {tab==="coldoutreach"&&canView("coldoutreach")&&<ColdOutreach/>}
          {tab==="treatments"&&canView("treatments")&&<TreatmentAnalytics/>}
          {tab==="users"&&canView("users")&&<UserMgmtNew/>}
          {tab==="messages"&&canView("messages")&&<MessagesInbox clients={clients} openClientProfile={openClientProfile} />}
          {tab==="ai"&&canView("ai")&&<AIAssistant clients={clients} appointments={appointments}/>}
          {tab==="bottraining"&&canView("bottraining")&&<BotTraining/>}
          {tab==="automations"&&canView("automations")&&<AppointmentAutomations/>}
        </div>
      </div>
    </div>
  );
}
